import React, { useState, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import {
    Task, ProtocolState, ProtocolConfig, WeeklyPlan, PhysicalUnit, Subcontractor, Contract,
    PaymentStatement, CompanyConfig, ProjectInfo, Responsible, StatementItem, UnitDetail,
    SubcontractType, Level, AnexoContrato, RevisionArea, RevisionParameter, RoadmapParamEvaluation,
    Profile, SubcontractorEvaluationConfig
} from '../src/types';
import { truncateProgress, findTaskById, getTaskGanttRowNumber } from '../src/utils/planningUtils';
import { cleanRut, formatRut, CHILE_REGIONS } from '../src/utils/chileData';
import { EvaluationConfigModal, DEFAULT_EVALUATION_RANGES, EvaluationRangeBadge, getEvaluationRangeForScore } from '../src/components/payments/EvaluationConfigModal';
import { SubcontractorEvaluationModal } from '../src/components/payments/SubcontractorEvaluationModal';
import { MobileTitleBar } from '../src/components/mobile/MobileTitleBar';
import { PullToRefresh } from '../src/components/mobile/PullToRefresh';
import { MobileAddContractModal } from '../src/components/mobile/payments/MobileAddContractModal';
import { MobileTaskSelectorSheet } from '../src/components/mobile/payments/MobileTaskSelectorSheet';
import { MobileSubcontractorPickerSheet } from '../src/components/mobile/payments/MobileSubcontractorPickerSheet';
import { MobileUnitSelectorSheet } from '../src/components/mobile/payments/MobileUnitSelectorSheet';
import { MobileCreateSubcontractorModal } from '../src/components/mobile/payments/MobileCreateSubcontractorModal';
import { MobileGenerateEPModal } from '../src/components/mobile/payments/MobileGenerateEPModal';
import { MobileContractDocumentsSheet } from '../src/components/mobile/payments/MobileContractDocumentsSheet';
import { MobileViewEPListSheet } from '../src/components/mobile/payments/MobileViewEPListSheet';
import { MobileContractAnnexesSheet } from '../src/components/mobile/payments/MobileContractAnnexesSheet';
import { MobileContractorListSheet } from '../src/components/mobile/payments/MobileContractorListSheet';
import { MobileApprovalConfigModal } from '../src/components/mobile/payments/MobileApprovalConfigModal';
import { MobileRoadmapConfigModal } from '../src/components/mobile/payments/MobileRoadmapConfigModal';
import { MobileEvaluationConfigModal } from '../src/components/mobile/payments/MobileEvaluationConfigModal';
import { MobileSubcontractorEvaluationModal } from '../src/components/mobile/payments/MobileSubcontractorEvaluationModal';
import { KlMenu, KlDialog, KlSheet } from '../src/components/mobile/modal';

interface PaymentsScreenProps {
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    matrixData: Record<string, string>;
    protocolConfigs: Record<string, ProtocolConfig>; // New: required for payment flags
    subcontractors: Subcontractor[];
    setSubcontractors: React.Dispatch<React.SetStateAction<Subcontractor[]>>;
    contracts: Contract[];
    setContracts: React.Dispatch<React.SetStateAction<Contract[]>>;
    paymentStatements: PaymentStatement[];
    setPaymentStatements: React.Dispatch<React.SetStateAction<PaymentStatement[]>>;
    savedProtocols: Record<string, ProtocolState>;
    subcontractTypes: SubcontractType[];
    setSubcontractTypes: React.Dispatch<React.SetStateAction<SubcontractType[]>>;
    projectInfo: { id?: string; name: string; code?: string };
    addActivity: (activity: any) => void;
    companyConfig: CompanyConfig;
    hasPermission: (module: string, action: 'view'|'edit') => boolean;
    isPlatformAdmin?: boolean;
    globalUsers?: any[];
    globalProfiles?: Profile[];
    responsibles?: Responsible[];
    weeklyPlans?: WeeklyPlan[];
    lookaheadPlans?: any[];
    globalSubcontractors?: Subcontractor[];
    setGlobalSubcontractors?: React.Dispatch<React.SetStateAction<Subcontractor[]>>;
    globalContracts?: Contract[];
    setGlobalContracts?: React.Dispatch<React.SetStateAction<Contract[]>>;
}

const SignatureModal = ({ isOpen, onClose, onSave }: { isOpen: boolean, onClose: () => void, onSave: (data: string) => void }) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    React.useEffect(() => {
        if (!isOpen) return;
        setTimeout(() => {
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.lineWidth = 3;
                    ctx.lineCap = 'round';
                    ctx.strokeStyle = '#1e3a8a';
                }
            }
        }, 50);
    }, [isOpen]);

    const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        if ('touches' in e) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top
            };
        }
        return {
            x: (e as React.MouseEvent).clientX - rect.left,
            y: (e as React.MouseEvent).clientY - rect.top
        };
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        const coords = getCoordinates(e);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && coords) {
            ctx.beginPath();
            ctx.moveTo(coords.x, coords.y);
            if (e.cancelable) e.preventDefault();
        }
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const coords = getCoordinates(e);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && coords) {
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
            if (e.cancelable) e.preventDefault();
        }
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) ctx.closePath();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full overflow-hidden shadow-2xl flex flex-col animate-fade-in-up">
                <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                    <h3 className="font-bold text-slate-800 dark:text-white uppercase text-sm">Firma Electrónica</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>
                <div className="p-4 flex-1 flex flex-col items-center bg-slate-50 dark:bg-slate-800/50">
                    <p className="text-xs text-slate-500 mb-3 text-center">Dibuje su firma en el recuadro inferior para aprobar.</p>
                    <div className="bg-white rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 overflow-hidden touch-none w-full max-w-[340px] aspect-[2/1]">
                        <canvas
                            ref={canvasRef}
                            width={340}
                            height={170}
                            className="w-full h-full cursor-crosshair bg-white"
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                        />
                    </div>
                    <button 
                        onClick={() => {
                            const canvas = canvasRef.current;
                            if (canvas) {
                                const ctx = canvas.getContext('2d');
                                if (ctx) ctx.clearRect(0,0, canvas.width, canvas.height);
                            }
                        }}
                        className="mt-4 px-3 py-1.5 bg-slate-200 dark:bg-slate-700 rounded-full text-[10px] font-bold text-slate-600 dark:text-slate-300 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[14px]">ink_eraser</span> Limpiar Firma
                    </button>
                </div>
                <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Cancelar</button>
                    <button 
                        onClick={() => {
                            if (canvasRef.current) {
                                onSave(canvasRef.current.toDataURL('image/png'));
                                onClose();
                            }
                        }} 
                        className="px-6 py-2 bg-primary hover:bg-blue-600 text-white text-xs font-bold rounded-lg shadow-md transition-colors"
                    >
                        Guardar Firma
                    </button>
                </div>
            </div>
        </div>
    );
};

const DEFAULT_REVISION_AREAS: RevisionArea[] = [
    {
        id: 'area-1',
        title: 'Prevención de Riesgos',
        maxDiscountPercent: 10,
        parameters: [
            { id: 'param-1-1', name: 'Matriz MIPER firmada y al día' },
            { id: 'param-1-2', name: 'Charla de inducción de seguridad completada' },
            { id: 'param-1-3', name: 'Registro de entrega de EPP en terreno' }
        ]
    },
    {
        id: 'area-2',
        title: 'Medio Ambiente',
        maxDiscountPercent: 10,
        parameters: [
            { id: 'param-2-1', name: 'Gestión y acopio de residuos de obra' },
            { id: 'param-2-2', name: 'Permisos y resoluciones ambientales al día' }
        ]
    },
    {
        id: 'area-3',
        title: 'Terreno y Operación',
        maxDiscountPercent: 10,
        parameters: [
            { id: 'param-3-1', name: 'Cumplimiento de avance físico según programa semanal' },
            { id: 'param-3-2', name: 'Libro de obra y anotaciones de terreno al día' }
        ]
    },
    {
        id: 'area-4',
        title: 'Calidad',
        maxDiscountPercent: 10,
        parameters: [
            { id: 'param-4-1', name: 'Protocolos de recepción aprobados por ITO' },
            { id: 'param-4-2', name: 'Ensayos de autocontrol y laboratorio conformes' }
        ]
    },
    {
        id: 'area-5',
        title: 'Documentación Administrativa',
        maxDiscountPercent: 10,
        parameters: [
            { id: 'param-5-1', name: 'Certificado F30-1 y pago de cotizaciones previsionales' },
            { id: 'param-5-2', name: 'Contratos de trabajo y anexos al día' },
            { id: 'param-5-3', name: 'Factura o documento de cobro autorizado' }
        ]
    }
];

const PaymentsScreen: React.FC<PaymentsScreenProps> = ({
    tasks,
    units,
    levels,
    matrixData,
    protocolConfigs,
    subcontractors,
    setSubcontractors,
    contracts,
    setContracts,
    paymentStatements,
    setPaymentStatements,
    savedProtocols,
    subcontractTypes,
    setSubcontractTypes,
    projectInfo,
    addActivity,
    companyConfig,
    hasPermission,
    isPlatformAdmin,
    globalUsers = [],
    globalProfiles = [],
    responsibles = [],
    weeklyPlans = [],
    lookaheadPlans = [],
    globalSubcontractors,
    setGlobalSubcontractors,
    globalContracts,
    setGlobalContracts
}) => {
    // --- State ---
    const checkIsMobile = () => {
        if (typeof window === 'undefined') return false;
        return Capacitor.isNativePlatform() || (typeof document !== 'undefined' && document.documentElement.classList.contains('is-native')) || window.innerWidth < 768;
    };
    const [isMobile, setIsMobile] = useState(checkIsMobile);
    React.useEffect(() => {
        const handleResize = () => setIsMobile(checkIsMobile());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const [menuOpen, setMenuOpen] = useState(false);
    const [evaluationMenuOpen, setEvaluationMenuOpen] = useState(false);
    const [evaluationConfigModalOpen, setEvaluationConfigModalOpen] = useState(false);
    const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
    const empresasBtnRef = React.useRef<HTMLButtonElement>(null);
    const evaluacionBtnRef = React.useRef<HTMLButtonElement>(null);

    // Responsables ordenados alfabéticamente A-Z
    const sortedResponsibles = useMemo(() => {
        return [...(responsibles || [])].sort((a, b) => 
            (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
        );
    }, [responsibles]);

    // Subcontractor Evaluation Configuration State (default template if none saved)
    const [evaluationConfig, setEvaluationConfig] = useState<SubcontractorEvaluationConfig>(() => {
        try {
            const saved = localStorage.getItem('klydapp_sc_evaluation_config');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (!parsed.ranges || parsed.ranges.length === 0) {
                    parsed.ranges = DEFAULT_EVALUATION_RANGES;
                }
                return parsed;
            }
        } catch (e) {
            console.error(e);
        }
        return {
            areas: [
                {
                    id: 'eval-area-1',
                    correlative: 1,
                    name: 'Prevención de Riesgos (SSOMA)',
                    weight: 25,
                    parameters: [
                        {
                            id: 'ep-1-1',
                            correlative: 1,
                            name: 'Cumplimiento de Charlas y EPP',
                            weight: 50,
                            indicatorDescription: 'Revisión periódica de inducciones, charlas de 5 min y uso correcto de EPP',
                            indicatorType: 'Manual'
                        },
                        {
                            id: 'ep-1-2',
                            correlative: 2,
                            name: 'Causas de No Cumplimiento (C.N.C)',
                            weight: 50,
                            indicatorDescription: 'C.N.C asociadas a seguridad y ambiente',
                            indicatorType: 'Automatico',
                            automaticCode: 'CNC'
                        }
                    ]
                },
                {
                    id: 'eval-area-2',
                    correlative: 2,
                    name: 'Calidad',
                    weight: 25,
                    parameters: [
                        {
                            id: 'ep-2-1',
                            correlative: 1,
                            name: 'Confiabilidad de Protocolos de Calidad',
                            weight: 100,
                            indicatorDescription: 'Protocolos aprobados vs rechazados en obra',
                            indicatorType: 'Automatico',
                            automaticCode: 'CONFIABILIDAD'
                        }
                    ]
                },
                {
                    id: 'eval-area-3',
                    correlative: 3,
                    name: 'Terreno y Avance Semanal',
                    weight: 30,
                    parameters: [
                        {
                            id: 'ep-3-1',
                            correlative: 1,
                            name: 'Porcentaje de Plan Cumplido (PPC)',
                            weight: 60,
                            indicatorDescription: 'Cumplimiento de tareas asignadas en planificación semanal',
                            indicatorType: 'Automatico',
                            automaticCode: 'PPC'
                        },
                        {
                            id: 'ep-3-2',
                            correlative: 2,
                            name: 'Liberación de Restricciones (PCR)',
                            weight: 40,
                            indicatorDescription: 'Porcentaje de restricciones liberadas a tiempo',
                            indicatorType: 'Automatico',
                            automaticCode: 'PCR'
                        }
                    ]
                },
                {
                    id: 'eval-area-4',
                    correlative: 4,
                    name: 'Documentación Administrativa',
                    weight: 20,
                    parameters: [
                        {
                            id: 'ep-4-1',
                            correlative: 1,
                            name: 'Certificados F30-1 y Cotizaciones',
                            weight: 50,
                            indicatorDescription: 'Entrega mensual oportuna de certificados laborales y previsionales',
                            indicatorType: 'Manual'
                        },
                        {
                            id: 'ep-4-2',
                            correlative: 2,
                            name: 'Facturación y Carpetas de Personal',
                            weight: 50,
                            indicatorDescription: 'Contratos y anexos registrados en el sistema',
                            indicatorType: 'Manual'
                        }
                    ]
                }
            ],
            ranges: DEFAULT_EVALUATION_RANGES
        };
    });

    const handleSaveEvaluationConfig = (newConfig: SubcontractorEvaluationConfig) => {
        setEvaluationConfig(newConfig);
        try {
            localStorage.setItem('klydapp_sc_evaluation_config', JSON.stringify(newConfig));
        } catch (e) {
            console.error('Error saving evaluation config', e);
        }
    };

    // Modals visibility
    const [createMasterSubModalOpen, setCreateMasterSubModalOpen] = useState(false);
    const [listMasterSubModalOpen, setListMasterSubModalOpen] = useState(false);
    const [editingSubcontractorId, setEditingSubcontractorId] = useState<string | null>(null);
    const [approvalConfigModalOpen, setApprovalConfigModalOpen] = useState(false);
    const [approvalFlows, setApprovalFlows] = useState<{
        subcontracts: { reviewerId: string }[];
        paymentStatements: { reviewerId: string; minAmount?: number }[];
    }>(() => {
        try {
            const saved = localStorage.getItem('klydapp_approval_flows_v2') || localStorage.getItem('klydapp_approval_flows');
            if (saved) return JSON.parse(saved);
        } catch (e) {
            console.error(e);
        }
        return {
            subcontracts: [],
            paymentStatements: []
        };
    });

    React.useEffect(() => {
        localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(approvalFlows));
    }, [approvalFlows]);

    const [signatureTarget, setSignatureTarget] = useState<{
        type: 'contract' | 'statement';
        id: string;
        reviewerId: string;
    } | null>(null);

    const handleOpenSignatureModalForContract = (reviewerId: string) => {
        setSignatureTarget({
            type: 'contract',
            id: currentForm.id || 'new',
            reviewerId
        });
    };

    const handleOpenSignatureModalForEP = (epId: string, reviewerId: string) => {
        setSignatureTarget({
            type: 'statement',
            id: epId,
            reviewerId
        });
    };

    const handleSaveSignature = (signatureData: string) => {
        if (!signatureTarget) return;
        const { type, reviewerId } = signatureTarget;

        if (type === 'contract') {
            const updatedSignatures = { ...currentForm.signatures, [reviewerId]: signatureData };
            setCurrentForm(prev => ({
                ...prev,
                signatures: updatedSignatures
            }));
            if (currentForm.id) {
                setContracts(prev => prev.map(c => 
                    c.id === currentForm.id 
                        ? { ...c, signatures: updatedSignatures } 
                        : c
                ));
            }
        } else if (type === 'statement') {
            setPaymentStatements(prev => prev.map(ps => {
                if (ps.id === signatureTarget.id) {
                    const updatedSigs = { ...ps.signatures, [reviewerId]: signatureData };
                    
                    // Determine if all required reviewers have signed
                    const epAmount = parseFloat(ps.amount.replace(/[^0-9.-]+/g, "")) || 0;
                    const requiredReviewers = approvalFlows.paymentStatements.filter(step => 
                        !step.minAmount || epAmount > step.minAmount
                    );
                    
                    const allSigned = requiredReviewers.every(step => updatedSigs[step.reviewerId]);
                    
                    return {
                        ...ps,
                        signatures: updatedSigs,
                        status: allSigned ? 'Autorizado' : ps.status
                    };
                }
                return ps;
            }));
            
            // Also update selectedEPForView state to reflect immediately
            setSelectedEPForView(prev => {
                if (prev && prev.id === signatureTarget.id) {
                    const updatedSigs = { ...prev.signatures, [reviewerId]: signatureData };
                    const epAmount = parseFloat(prev.amount.replace(/[^0-9.-]+/g, "")) || 0;
                    const requiredReviewers = approvalFlows.paymentStatements.filter(step => 
                        !step.minAmount || epAmount > step.minAmount
                    );
                    const allSigned = requiredReviewers.every(step => updatedSigs[step.reviewerId]);
                    return {
                        ...prev,
                        signatures: updatedSigs,
                        status: allSigned ? 'Autorizado' : prev.status
                    };
                }
                return prev;
            });
        }
        setSignatureTarget(null);
    };

    // Main Form States (Old Create Modal - kept for "Añadir")
    const [addStatementModalOpen, setAddStatementModalOpen] = useState(false);
    const [anexosListModalOpen, setAnexosListModalOpen] = useState(false);
    const [anexoFormModalOpen, setAnexoFormModalOpen] = useState(false);
    const [subcontractorPickerOpen, setSubcontractorPickerOpen] = useState(false);
    const [viewEPModalOpen, setViewEPModalOpen] = useState(false); // New State for "Ver EP"
    const [isAnexoMode, setIsAnexoMode] = useState(false);

    // --- HOJA DE RUTA MASTER CONFIGURATION & EVALUATION STATES ---
    const [roadmapConfigModalOpen, setRoadmapConfigModalOpen] = useState(false);
    const [revisionAreas, setRevisionAreas] = useState<RevisionArea[]>(() => {
        try {
            const saved = localStorage.getItem('klydapp_hoja_de_ruta_master');
            if (saved) return JSON.parse(saved);
        } catch (e) {
            console.error("Error loading klydapp_hoja_de_ruta_master:", e);
        }
        return DEFAULT_REVISION_AREAS;
    });

    const [expandedAreaIds, setExpandedAreaIds] = useState<Set<string>>(() => 
        new Set(DEFAULT_REVISION_AREAS.map(a => a.id))
    );

    const toggleAreaExpand = (areaId: string) => {
        setExpandedAreaIds(prev => {
            const next = new Set(prev);
            if (next.has(areaId)) next.delete(areaId);
            else next.add(areaId);
            return next;
        });
    };

    React.useEffect(() => {
        localStorage.setItem('klydapp_hoja_de_ruta_master', JSON.stringify(revisionAreas));
    }, [revisionAreas]);

    const [editingAreaModal, setEditingAreaModal] = useState<{
        id?: string;
        title: string;
        maxDiscountPercent: number;
    } | null>(null);

    const handleOpenCreateAreaModal = () => {
        setEditingAreaModal({
            title: '',
            maxDiscountPercent: 10
        });
    };

    const handleOpenEditAreaModal = (area: RevisionArea, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setEditingAreaModal({
            id: area.id,
            title: area.title,
            maxDiscountPercent: area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10
        });
    };

    const handleSaveAreaModal = () => {
        if (!editingAreaModal || !editingAreaModal.title.trim()) return;

        const discountNum = editingAreaModal.maxDiscountPercent >= 0 ? editingAreaModal.maxDiscountPercent : 10;

        if (editingAreaModal.id) {
            setRevisionAreas(prev => prev.map(a => a.id === editingAreaModal.id ? {
                ...a,
                title: editingAreaModal.title.trim(),
                maxDiscountPercent: discountNum
            } : a));
        } else {
            const newId = `area-${Date.now()}`;
            const newArea: RevisionArea = {
                id: newId,
                title: editingAreaModal.title.trim(),
                maxDiscountPercent: discountNum,
                parameters: []
            };
            setRevisionAreas(prev => [...prev, newArea]);
            setExpandedAreaIds(prev => new Set([...prev, newId]));
        }

        setEditingAreaModal(null);
    };

    const handleDeleteArea = (areaId: string) => {
        const area = revisionAreas.find(a => a.id === areaId);
        if (!area) return;
        if (confirm(`¿Eliminar el área "${area.title}" y sus sub-parámetros? Esta acción no se puede deshacer.`)) {
            setRevisionAreas(prev => prev.filter(a => a.id !== areaId));
        }
    };

    const handleAddParameter = (areaId: string) => {
        const name = prompt("Ingrese el nombre del nuevo Parámetro de Revisión:");
        if (!name || !name.trim()) return;
        const newParam: RevisionParameter = {
            id: `param-${Date.now()}`,
            name: name.trim()
        };
        setRevisionAreas(prev => prev.map(a => a.id === areaId ? { ...a, parameters: [...a.parameters, newParam] } : a));
    };

    const handleEditParameter = (areaId: string, paramId: string) => {
        const area = revisionAreas.find(a => a.id === areaId);
        if (!area) return;
        const param = area.parameters.find(p => p.id === paramId);
        if (!param) return;
        const newName = prompt("Nuevo nombre para el parámetro:", param.name);
        if (!newName || !newName.trim()) return;
        setRevisionAreas(prev => prev.map(a => a.id === areaId ? {
            ...a,
            parameters: a.parameters.map(p => p.id === paramId ? { ...p, name: newName.trim() } : p)
        } : a));
    };

    const handleDeleteParameter = (areaId: string, paramId: string) => {
        setRevisionAreas(prev => prev.map(a => a.id === areaId ? {
            ...a,
            parameters: a.parameters.filter(p => p.id !== paramId)
        } : a));
    };

    // Evaluation & EP Carátula States & Handlers
    const [selectedEPForRoadmap, setSelectedEPForRoadmap] = useState<PaymentStatement | null>(null);
    const [selectedEPForCaratula, setSelectedEPForCaratula] = useState<PaymentStatement | null>(null);
    const [caratulaRetentionPercent, setCaratulaRetentionPercent] = useState<number>(5);
    const [expandedPartidaIds, setExpandedPartidaIds] = useState<Set<string>>(new Set());

    const togglePartidaExpand = (partidaId: string) => {
        setExpandedPartidaIds(prev => {
            const next = new Set(prev);
            if (next.has(partidaId)) next.delete(partidaId);
            else next.add(partidaId);
            return next;
        });
    };

    const [roadmapEvalAnswers, setRoadmapEvalAnswers] = useState<Record<string, RoadmapParamEvaluation>>({});
    const [roadmapEvalAmount, setRoadmapEvalAmount] = useState<number>(0);
    const [exportDropdownOpen, setExportDropdownOpen] = useState<boolean>(false);
    const [commentModalTarget, setCommentModalTarget] = useState<{ paramId: string; paramName: string; comment: string } | null>(null);
    const [confirmApproveTarget, setConfirmApproveTarget] = useState<{ epId: string; reviewerId: string; reviewerName: string } | null>(null);
    const [confirmRejectTarget, setConfirmRejectTarget] = useState<{ epId: string; reviewerId: string; reviewerName: string; idx: number } | null>(null);
    const [rejectionReasonInput, setRejectionReasonInput] = useState<string>('');
    const [confirmReopenTarget, setConfirmReopenTarget] = useState<string | null>(null);

    // Dialog state para acciones de contratos en móvil (Punto 5)
    const [mobileContractDialog, setMobileContractDialog] = useState<{
        open: boolean;
        type: 'inactivar' | 'anular' | 'activar' | 'eliminar' | 'alerta';
        contractId?: string;
        title: string;
        message: string;
        danger?: boolean;
        confirmText?: string;
        cancelText?: string | null;
    } | null>(null);
    const [voidReasonText, setVoidReasonText] = useState('');

    const handleConfirmContractDialog = () => {
        if (!mobileContractDialog) return;
        const { type, contractId } = mobileContractDialog;
        if (type === 'inactivar' && contractId) {
            setContracts(prev => prev.map(p => p.id === contractId ? { ...p, status: 'Inactivo' } : p));
        } else if (type === 'anular' && contractId) {
            if (!voidReasonText.trim()) {
                setMobileContractDialog({
                    open: true,
                    type: 'alerta',
                    title: 'Motivo requerido',
                    message: 'Debe ingresar un motivo para anular el subcontrato.',
                    cancelText: null,
                    confirmText: 'Entendido'
                });
                return;
            }
            setContracts(prev => prev.map(p => p.id === contractId ? { ...p, status: 'Anulado', notes: (p.notes ? p.notes + '\n' : '') + `Anulado: ${voidReasonText.trim()}` } : p));
        } else if (type === 'activar' && contractId) {
            setContracts(prev => prev.map(p => p.id === contractId ? { ...p, status: 'Activo' } : p));
        } else if (type === 'eliminar' && contractId) {
            setContracts(prev => prev.map(p => p.id === contractId ? { ...p, isDeleted: true } : p));
            setSelectedContractId(null);
        }
        setMobileContractDialog(null);
    };

    const handleConfirmApproval = () => {
        if (!confirmApproveTarget) return;
        const { epId, reviewerId, reviewerName } = confirmApproveTarget;

        const signatureStamp = `Aprobado por ${reviewerName} - ${new Date().toLocaleDateString('es-CL')}`;

        setPaymentStatements(prev => prev.map(ep => {
            if (ep.id === epId) {
                const nextSignatures = { ...(ep.signatures || {}), [reviewerId]: signatureStamp };
                const signedCount = Object.keys(nextSignatures).length;
                const totalRequired = approvalFlows.paymentStatements.length;
                const newApprovalStatus = signedCount >= totalRequired ? 'Aprobado' : 'En proceso';

                const updatedEP: PaymentStatement = {
                    ...ep,
                    signatures: nextSignatures,
                    epApprovalStatus: newApprovalStatus as 'Pendiente' | 'En proceso' | 'Aprobado'
                };

                if (selectedEPForView?.id === epId) {
                    setSelectedEPForView(updatedEP);
                }
                return updatedEP;
            }
            return ep;
        }));

        setConfirmApproveTarget(null);
    };

    const handleConfirmRejection = () => {
        if (!confirmRejectTarget || !rejectionReasonInput.trim()) return;
        const { epId, idx, reviewerName } = confirmRejectTarget;
        const reason = rejectionReasonInput.trim();

        setPaymentStatements(prev => prev.map(ep => {
            if (ep.id === epId) {
                if (idx === 0) {
                    // First reviewer (Solicitante) rejects: Reset EP & HR to edit mode
                    const updatedEP: PaymentStatement = {
                        ...ep,
                        signatures: {},
                        roadmapStatus: 'Rechazado',
                        epApprovalStatus: 'En Espera',
                        rejectionReason: reason,
                        rejectedByReviewerName: reviewerName
                    };
                    if (selectedEPForView?.id === epId) {
                        setSelectedEPForView(updatedEP);
                    }
                    return updatedEP;
                } else {
                    // Subsequent reviewer rejects: rollback 1 step to previous required reviewer signature
                    const updatedSignatures = { ...(ep.signatures || {}) };
                    const prevStep = approvalFlows.paymentStatements[idx - 1];
                    if (prevStep) {
                        delete updatedSignatures[prevStep.reviewerId];
                    }
                    const remainingSignaturesCount = Object.keys(updatedSignatures).length;
                    const newApprovalStatus = remainingSignaturesCount > 0 ? 'En proceso' : 'Pendiente';

                    const updatedEP: PaymentStatement = {
                        ...ep,
                        signatures: updatedSignatures,
                        epApprovalStatus: newApprovalStatus as 'Pendiente' | 'En proceso' | 'Aprobado',
                        rejectionReason: reason,
                        rejectedByReviewerName: reviewerName
                    };
                    if (selectedEPForView?.id === epId) {
                        setSelectedEPForView(updatedEP);
                    }
                    return updatedEP;
                }
            }
            return ep;
        }));

        setConfirmRejectTarget(null);
        setRejectionReasonInput('');
    };

    const handleConfirmReopen = () => {
        if (!confirmReopenTarget) return;
        const epId = confirmReopenTarget;

        setPaymentStatements(prev => prev.map(ep => {
            if (ep.id === epId) {
                const updatedSignatures = { ...(ep.signatures || {}) };
                const lastStep = approvalFlows.paymentStatements[approvalFlows.paymentStatements.length - 1];
                if (lastStep) {
                    delete updatedSignatures[lastStep.reviewerId];
                }
                const updatedEP: PaymentStatement = {
                    ...ep,
                    signatures: updatedSignatures,
                    epApprovalStatus: 'En proceso'
                };
                if (selectedEPForView?.id === epId) {
                    setSelectedEPForView(updatedEP);
                }
                return updatedEP;
            }
            return ep;
        }));

        setConfirmReopenTarget(null);
    };

    const handleSendEpToApproval = (epId: string) => {
        setPaymentStatements(prev => prev.map(p => p.id === epId ? {
            ...p,
            epApprovalStatus: 'Pendiente'
        } : p));
        alert("Estado de Pago enviado a Aprobación exitosamente.");
    };

    const handleOpenEpForView = (ep: PaymentStatement) => {
        setCurrentDraftEp(JSON.parse(JSON.stringify(ep)));
        setGenerateEpOpen(true);
    };

    const handleOpenRoadmapEvaluation = (ep: PaymentStatement) => {
        setSelectedEPForRoadmap(ep);
        const rawEvals = ep.roadmapEvaluations || {};
        const normalized: Record<string, RoadmapParamEvaluation> = {};
        for (const [paramId, val] of Object.entries(rawEvals)) {
            if (typeof val === 'string') {
                normalized[paramId] = {
                    status: val,
                    observedPeriods: val === 'no_cumple' ? 1 : undefined,
                    factor: val === 'no_cumple' ? 1.0 : undefined
                };
            } else if (val && typeof val === 'object') {
                normalized[paramId] = { ...val };
            }
        }
        setRoadmapEvalAnswers(normalized);
        const initialAmount = ep.roadmapApprovedAmount !== undefined
            ? ep.roadmapApprovedAmount
            : (ep.preliminaryAmount !== undefined ? ep.preliminaryAmount : (parseInt((ep.amount || '').replace(/\D/g, ''), 10) || 0));
        setRoadmapEvalAmount(initialAmount);
    };

    const handleParamStatusChange = (paramId: string, newStatus: 'cumple' | 'no_cumple' | 'na' | '') => {
        setRoadmapEvalAnswers(prev => {
            const current = prev[paramId] || {};
            let updated: RoadmapParamEvaluation;
            if (newStatus === 'no_cumple') {
                const periods = current.observedPeriods || 1;
                const factor = periods === 2 ? 1.5 : periods === 3 ? 2.0 : 1.0;
                updated = { status: 'no_cumple', observedPeriods: periods, factor };
            } else if (newStatus === 'cumple' || newStatus === 'na') {
                updated = { status: newStatus, observedPeriods: undefined, factor: undefined };
            } else {
                updated = { status: '', observedPeriods: undefined, factor: undefined };
            }
            return { ...prev, [paramId]: updated };
        });
    };

    const handleParamPeriodsChange = (paramId: string, newPeriods: 1 | 2 | 3) => {
        setRoadmapEvalAnswers(prev => {
            const current = prev[paramId] || {};
            if (current.status !== 'no_cumple') return prev;
            const factor = newPeriods === 2 ? 1.5 : newPeriods === 3 ? 2.0 : 1.0;
            return {
                ...prev,
                [paramId]: {
                    ...current,
                    observedPeriods: newPeriods,
                    factor
                }
            };
        });
    };

    const getAreaMetrics = (area: RevisionArea) => {
        const maxDiscount = area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10;
        let observados = 0;
        let aplicables = 0;
        let sumaFactores = 0;

        area.parameters.forEach(p => {
            const ev = roadmapEvalAnswers[p.id];
            const status = ev?.status;
            if (status === 'cumple') {
                aplicables++;
            } else if (status === 'no_cumple') {
                aplicables++;
                observados++;
                const periods = ev?.observedPeriods || 1;
                const factor = periods === 2 ? 1.5 : periods === 3 ? 2.0 : 1.0;
                sumaFactores += factor;
            }
        });

        const rawDescuento = aplicables > 0 ? (maxDiscount * sumaFactores) / aplicables : 0;
        // El máximo descuento de área no puede superar el Dcto. Máx configurado
        const descuentoAreaPercent = Math.min(maxDiscount, rawDescuento);

        return {
            maxDiscount,
            observados,
            aplicables,
            sumaFactores,
            descuentoAreaPercent,
            isCapped: rawDescuento > maxDiscount
        };
    };

    const globalRoadmapMetrics = useMemo(() => {
        const preliminaryAmount = selectedEPForRoadmap?.preliminaryAmount !== undefined
            ? selectedEPForRoadmap.preliminaryAmount
            : (selectedEPForRoadmap ? (parseInt((selectedEPForRoadmap.amount || '').replace(/\D/g, ''), 10) || 0) : 0);

        let totalDiscountPercent = 0;
        revisionAreas.forEach(area => {
            const { descuentoAreaPercent } = getAreaMetrics(area);
            totalDiscountPercent += descuentoAreaPercent;
        });

        const discountAmount = Math.round(preliminaryAmount * (totalDiscountPercent / 100));
        const approvedAmount = Math.max(0, preliminaryAmount - discountAmount);

        return {
            preliminaryAmount,
            totalDiscountPercent,
            discountAmount,
            approvedAmount
        };
    }, [selectedEPForRoadmap, revisionAreas, roadmapEvalAnswers]);

    const computedOverallRoadmapStatus = useMemo(() => {
        const allParamIds: string[] = [];
        revisionAreas.forEach(a => a.parameters.forEach(p => allParamIds.push(p.id)));
        if (allParamIds.length === 0) return 'Pendiente';

        const evaluatedCount = allParamIds.filter(id => {
            const st = roadmapEvalAnswers[id]?.status;
            return st === 'cumple' || st === 'no_cumple' || st === 'na';
        }).length;

        if (evaluatedCount === 0) return 'Pendiente';
        if (evaluatedCount < allParamIds.length) return 'En Proceso';
        return 'Aprobado';
    }, [revisionAreas, roadmapEvalAnswers]);

    const handleSaveRoadmapEvaluation = () => {
        if (!selectedEPForRoadmap) return;
        setPaymentStatements(prev => prev.map(p => p.id === selectedEPForRoadmap.id ? {
            ...p,
            roadmapEvaluations: roadmapEvalAnswers,
            roadmapApprovedAmount: globalRoadmapMetrics.approvedAmount,
            roadmapStatus: computedOverallRoadmapStatus
        } : p));
        setSelectedEPForRoadmap(null);
    };

    const handleApproveArea = (areaId: string) => {
        if (!selectedEPForRoadmap) return;
        const nowStr = new Date().toLocaleString('es-CL');
        const approverName = 'Usuario Registrado';

        const nextAreaApprovals = {
            ...(selectedEPForRoadmap.roadmapAreaApprovals || {}),
            [areaId]: { approvedBy: approverName, approvedAt: nowStr }
        };

        const updatedEP: PaymentStatement = {
            ...selectedEPForRoadmap,
            roadmapAreaApprovals: nextAreaApprovals
        };

        setSelectedEPForRoadmap(updatedEP);
        setPaymentStatements(prev => prev.map(p => p.id === updatedEP.id ? updatedEP : p));
    };

    const handleSaveParamComment = (commentText: string) => {
        if (!commentModalTarget) return;
        const { paramId } = commentModalTarget;
        setRoadmapEvalAnswers(prev => {
            const current = prev[paramId] || {};
            return {
                ...prev,
                [paramId]: { ...current, comment: commentText.trim() }
            };
        });
        setCommentModalTarget(null);
    };

    const generateRoadmapPrintableHTML = (ep: PaymentStatement) => {
        const contract = contracts.find(c => c.id === ep.contractId);
        const sub = subcontractors.find(s => s.id === (contract?.subcontractorId || ep.subcontractorId));

        const rawEvals = ep.roadmapEvaluations || {};
        const normalizedEvals: Record<string, RoadmapParamEvaluation> = {};
        for (const [paramId, val] of Object.entries(rawEvals)) {
            if (typeof val === 'string') {
                normalizedEvals[paramId] = {
                    status: val,
                    observedPeriods: val === 'no_cumple' ? 1 : undefined,
                    factor: val === 'no_cumple' ? 1.0 : undefined
                };
            } else if (val && typeof val === 'object') {
                normalizedEvals[paramId] = { ...val };
            }
        }

        const preliminaryAmount = ep.preliminaryAmount !== undefined
            ? ep.preliminaryAmount
            : (parseInt((ep.amount || '').replace(/\D/g, ''), 10) || 0);

        let totalDiscountPercent = 0;

        const areasHtml = revisionAreas.map(area => {
            const maxDiscount = area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10;
            let observados = 0;
            let aplicables = 0;
            let sumaFactores = 0;

            area.parameters.forEach(p => {
                const ev = normalizedEvals[p.id];
                const status = ev?.status;
                if (status === 'cumple') {
                    aplicables++;
                } else if (status === 'no_cumple') {
                    aplicables++;
                    observados++;
                    const periods = ev?.observedPeriods || 1;
                    const factor = periods === 2 ? 1.5 : periods === 3 ? 2.0 : 1.0;
                    sumaFactores += factor;
                }
            });

            const rawDescuento = aplicables > 0 ? (maxDiscount * sumaFactores) / aplicables : 0;
            const descuentoAreaPercent = Math.min(maxDiscount, rawDescuento);
            totalDiscountPercent += descuentoAreaPercent;

            const approval = ep.roadmapAreaApprovals?.[area.id];
            const approvalStamp = approval
                ? `<div style="background-color: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; padding: 4px 10px; border-radius: 6px; font-weight: bold; font-size: 11px;">
                    ✓ Área Aprobada por ${approval.approvedBy} (${approval.approvedAt})
                   </div>`
                : `<div style="background-color: #fef3c7; color: #92400e; border: 1px solid #fcd34d; padding: 4px 10px; border-radius: 6px; font-weight: bold; font-size: 11px;">
                    Pendiente de Aprobación
                   </div>`;

            let paramsHtml = '';
            area.parameters.forEach(p => {
                const ev = normalizedEvals[p.id] || {};
                const st = ev.status || '-';
                const stLabel = st === 'cumple' ? 'CUMPLE' : st === 'no_cumple' ? 'NO CUMPLE' : st === 'na' ? 'NO APLICA' : 'SIN EVALUAR';
                const stColor = st === 'cumple' ? '#059669' : st === 'no_cumple' ? '#dc2626' : '#6b7280';
                const periods = ev.observedPeriods || 1;
                const factor = st === 'no_cumple' ? (periods === 2 ? '1.5' : periods === 3 ? '2.0' : '1.0') : '-';
                const commentText = ev.comment ? `<div style="margin-top: 4px; font-size: 11px; font-style: italic; color: #374151; background: #f3f4f6; padding: 4px 8px; border-left: 3px solid #3b82f6; border-radius: 2px;"><strong>Obs:</strong> ${ev.comment}</div>` : '';

                paramsHtml += `
                    <tr style="border-bottom: 1px solid #e5e7eb;">
                        <td style="padding: 8px 12px; font-weight: 500; color: #1f2937;">
                            ${p.name}
                            ${commentText}
                        </td>
                        <td style="padding: 8px 12px; text-align: center; font-weight: bold; color: ${stColor};">
                            ${stLabel}
                        </td>
                        <td style="padding: 8px 12px; text-align: center;">
                            ${st === 'no_cumple' ? periods : '-'}
                        </td>
                        <td style="padding: 8px 12px; text-align: center; font-family: monospace; font-weight: bold;">
                            ${factor}
                        </td>
                    </tr>
                `;
            });

            return `
                <div style="margin-bottom: 20px; border: 1px solid #cbd5e1; border-radius: 8px; overflow: hidden; background: #ffffff;">
                    <div style="background-color: #f1f5f9; padding: 10px 16px; font-weight: bold; font-size: 13px; color: #0f172a; border-bottom: 1px solid #cbd5e1; display: flex; align-items: center; justify-content: space-between;">
                        <span>${area.title} (Dcto Máx: ${maxDiscount}%)</span>
                        ${approvalStamp}
                    </div>
                    <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
                        <thead>
                            <tr style="background-color: #f8fafc; border-bottom: 1px solid #e2e8f0; color: #475569; font-size: 11px; text-transform: uppercase;">
                                <th style="padding: 8px 12px; text-align: left;">Parámetro</th>
                                <th style="padding: 8px 12px; text-align: center; width: 120px;">Estado</th>
                                <th style="padding: 8px 12px; text-align: center; width: 120px;">Periodos Obs.</th>
                                <th style="padding: 8px 12px; text-align: center; width: 100px;">Factor</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${paramsHtml}
                        </tbody>
                    </table>
                </div>
            `;
        }).join('');

        const discountAmount = Math.round(preliminaryAmount * (totalDiscountPercent / 100));
        const approvedAmount = Math.max(0, preliminaryAmount - discountAmount);

        return `
            <div style="font-family: system-ui, -apple-system, sans-serif; color: #0f172a; padding: 10px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 2px solid #0284c7; padding-bottom: 12px;">
                    <div>
                        <h2 style="margin: 0; font-size: 18px; font-weight: 900; color: #0f172a; text-transform: uppercase;">
                            EVALUACIÓN DE HOJA DE RUTA
                        </h2>
                        <p style="margin: 4px 0 0 0; font-size: 12px; color: #64748b;">
                            Obra: <strong>${projectInfo?.name || 'Obra Principal'}</strong> | Contratista: <strong>${sub?.razonSocial || 'N/A'}</strong> (${sub?.rut || ''})
                        </p>
                    </div>
                    <div style="text-align: right; background-color: #0284c7; color: white; padding: 6px 14px; border-radius: 6px; font-weight: bold; font-size: 12px;">
                        ESTADO DE PAGO ${ep.period || ''}
                    </div>
                </div>

                <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; background-color: #f8fafc; padding: 16px; border-radius: 8px; border: 1px solid #e2e8f0;">
                    <div style="background: white; padding: 10px 14px; border-radius: 6px; border: 1px solid #cbd5e1;">
                        <div style="font-size: 10px; text-transform: uppercase; font-weight: bold; color: #64748b;">Monto EP Preliminar</div>
                        <div style="font-size: 15px; font-weight: bold; font-family: monospace; color: #0f172a;">$${preliminaryAmount.toLocaleString('es-CL')}</div>
                    </div>
                    <div style="background: #fffbeb; padding: 10px 14px; border-radius: 6px; border: 1px solid #fde68a;">
                        <div style="font-size: 10px; text-transform: uppercase; font-weight: bold; color: #b45309;">% Descuento HR</div>
                        <div style="font-size: 15px; font-weight: bold; font-family: monospace; color: #92400e;">${totalDiscountPercent.toFixed(1).replace('.', ',')}%</div>
                    </div>
                    <div style="background: #fef2f2; padding: 10px 14px; border-radius: 6px; border: 1px solid #fecaca;">
                        <div style="font-size: 10px; text-transform: uppercase; font-weight: bold; color: #b91c1c;">Descuento Calculado</div>
                        <div style="font-size: 15px; font-weight: bold; font-family: monospace; color: #dc2626;">-$${discountAmount.toLocaleString('es-CL')}</div>
                    </div>
                    <div style="background: #ecfdf5; padding: 10px 14px; border-radius: 6px; border: 1px solid #a7f3d0;">
                        <div style="font-size: 10px; text-transform: uppercase; font-weight: bold; color: #047857;">Monto Aprobado</div>
                        <div style="font-size: 15px; font-weight: bold; font-family: monospace; color: #059669;">$${approvedAmount.toLocaleString('es-CL')}</div>
                    </div>
                </div>

                ${areasHtml}
            </div>
        `;
    };

    const handlePrintRoadmapOnly = () => {
        if (!selectedEPForRoadmap) return;
        const html = generateRoadmapPrintableHTML(selectedEPForRoadmap);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow?.document;
        if (!iframeDoc) return;

        iframeDoc.open();
        iframeDoc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Hoja de Ruta - ${selectedEPForRoadmap.period}</title>
                <script src="https://cdn.tailwindcss.com"></script>
                <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />
                <style>
                    @page { margin: 0.8cm; }
                    body { 
                        background: white !important; 
                        color: black !important;
                        -webkit-print-color-adjust: exact !important; 
                        print-color-adjust: exact !important; 
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .material-symbols-outlined {
                        font-family: 'Material Symbols Outlined' !important;
                        font-weight: normal;
                        font-style: normal;
                        font-size: 16px;
                        line-height: 1;
                        letter-spacing: normal;
                        text-transform: none;
                        display: inline-block;
                        white-space: nowrap;
                        word-wrap: normal;
                        direction: ltr;
                        -webkit-font-feature-settings: 'liga';
                        -webkit-font-smoothing: antialiased;
                    }
                    .print-hidden { display: none !important; }
                    .keep-together {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    tr { page-break-inside: avoid !important; break-inside: avoid !important; }
                </style>
            </head>
            <body>
                <div style="padding: 10px;">
                    ${html}
                </div>
            </body>
            </html>
        `);
        iframeDoc.close();

        const triggerPrint = () => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        };

        if (iframeDoc.fonts) {
            iframeDoc.fonts.ready.then(() => setTimeout(triggerPrint, 300));
        } else {
            setTimeout(triggerPrint, 1000);
        }
    };

    const handlePrintEPUndRoadmap = () => {
        if (!selectedEPForView) return;
        const contentElement = document.getElementById('ep-caratula-content');
        if (!contentElement) return;

        const roadmapHtml = generateRoadmapPrintableHTML(selectedEPForView);

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow?.document;
        if (!iframeDoc) return;

        iframeDoc.open();
        iframeDoc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Estado de Pago + Hoja de Ruta - ${selectedEPForView.period}</title>
                <script src="https://cdn.tailwindcss.com"></script>
                <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />
                <style>
                    @page { margin: 0.8cm; }
                    body { 
                        background: white !important; 
                        color: black !important;
                        -webkit-print-color-adjust: exact !important; 
                        print-color-adjust: exact !important; 
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .material-symbols-outlined {
                        font-family: 'Material Symbols Outlined' !important;
                        font-weight: normal;
                        font-style: normal;
                        font-size: 16px;
                        line-height: 1;
                        letter-spacing: normal;
                        text-transform: none;
                        display: inline-block;
                        white-space: nowrap;
                        word-wrap: normal;
                        direction: ltr;
                        -webkit-font-feature-settings: 'liga';
                        -webkit-font-smoothing: antialiased;
                    }
                    .print-hidden { display: none !important; }
                    .keep-together {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    tr { page-break-inside: avoid !important; break-inside: avoid !important; }
                    input { border: none !important; padding: 0 !important; margin: 0 !important; width: auto !important; }
                </style>
            </head>
            <body>
                <div style="padding: 10px;">
                    ${contentElement.innerHTML}
                </div>
                <div style="page-break-before: always;"></div>
                <div style="padding: 10px;">
                    ${roadmapHtml}
                </div>
            </body>
            </html>
        `);
        iframeDoc.close();

        const triggerPrint = () => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        };

        if (iframeDoc.fonts) {
            iframeDoc.fonts.ready.then(() => setTimeout(triggerPrint, 300));
        } else {
            setTimeout(triggerPrint, 1000);
        }
    };

    const [currentAnexoForm, setCurrentAnexoForm] = useState<Partial<AnexoContrato>>({
        id: '',
        numero: '',
        fecha: new Date().toISOString().split('T')[0],
        description: '',
        items: [],
        totalAmount: 0,
        status: 'Borrador'
    });
    const [expandedAnexoItemIds, setExpandedAnexoItemIds] = useState<Set<string>>(new Set());

    // NEW STATES for "Generar EP" Workflow
    const [generateEpOpen, setGenerateEpOpen] = useState(false);
    const [detailEpOpen, setDetailEpOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);

    // NEW: EP Detail Modal (Cover View)
    const [selectedEPForView, setSelectedEPForView] = useState<PaymentStatement | null>(null);
    const [epDetailModalOpen, setEpDetailModalOpen] = useState(false);
    const [expandedItemsInView, setExpandedItemsInView] = useState<Set<string>>(new Set());
    const [retentionPercentageInView, setRetentionPercentageInView] = useState<number>(0);

    const [currentDraftEp, setCurrentDraftEp] = useState<Partial<PaymentStatement>>({});
    const [activeDraftTask, setActiveDraftTask] = useState<{ id: string, name: string, code: string } | null>(null);
    const [tempSelectedDetailUnits, setTempSelectedDetailUnits] = useState<string[]>([]); // Units selected for payment in detail view

    // Item Selection States
    const [taskSelectorOpen, setTaskSelectorOpen] = useState(false); // Step 1: Pick Task
    const [unitSelectorOpen, setUnitSelectorOpen] = useState(false); // Step 2: Pick Units
    const [selectedTaskForAdd, setSelectedTaskForAdd] = useState<{ id: string, code: string, name: string } | null>(null);
    const [tempSelectedUnits, setTempSelectedUnits] = useState<string[]>([]); // Array of unitIds

    // Documents Modal State
    const [documentsModalOpen, setDocumentsModalOpen] = useState(false);

    // NEW: Subcontract Type Management
    const [subcontractTypeModalOpen, setSubcontractTypeModalOpen] = useState(false);
    const [typeSearchQuery, setTypeSearchQuery] = useState('');
    const [newTypeName, setNewTypeName] = useState('');

    const chileData = CHILE_REGIONS;

    // Initial selected statement to avoid null on first render if list is not empty
    const [selectedContractId, setSelectedContractId] = useState<string | null>(contracts.filter(c => !c.isDeleted).length > 0 ? contracts.filter(c => !c.isDeleted)[0].id : null);

    // State for the "New/Edit Statement" Form (Now essentially "Edit Contract")
    const [currentForm, setCurrentForm] = useState({
        id: '', // Empty for new, populated for edit
        subcontractorId: '',
        rut: '',
        razonSocial: '',
        startDate: '',
        endDate: '',
        retention: 0,
        items: [] as StatementItem[],
        attachedDocuments: [] as string[],
        linkedUserIds: [] as string[],
        signatures: {} as Record<string, string>
    });

    // State for New Master Subcontractor
    const [newSubcontractor, setNewSubcontractor] = useState<Omit<Subcontractor, 'id'>>({
        rut: '',
        razonSocial: '',
        giro: '',
        representante: '',
        telefono: '',
        correo: '',
        subcontractTypeId: '',
        fono: '',
        ciudad: '',
        comuna: '',
        status: 'Activo',
        linkedUserIds: []
    });

    // --- Actions ---

    // 1. Create/Edit Master Subcontractor
    const handleSaveMasterSubcontractor = () => {
        if (!newSubcontractor.rut || !newSubcontractor.razonSocial) {
            alert("Por favor ingrese al menos el RUT y la Razón Social.");
            return;
        }

        const formattedSub = {
            ...newSubcontractor,
            rut: formatRut(newSubcontractor.rut)
        };

        if (editingSubcontractorId) {
            setSubcontractors(prev => prev.map(sub => 
                sub.id === editingSubcontractorId 
                    ? { ...sub, ...formattedSub } 
                    : sub
            ));
            if (setGlobalSubcontractors) {
                setGlobalSubcontractors(prev => {
                    const cleanedRut = cleanRut(formattedSub.rut);
                    const existsInGlobal = prev.some(s => s.id === editingSubcontractorId || cleanRut(s.rut) === cleanedRut);
                    if (existsInGlobal) {
                        return prev.map(s => (s.id === editingSubcontractorId || cleanRut(s.rut) === cleanedRut) ? { ...s, ...formattedSub } : s);
                    }
                    return [...prev, { id: editingSubcontractorId, ...formattedSub }];
                });
            }
        } else {
            const newEntry: Subcontractor = {
                id: Math.random().toString(36).substr(2, 9),
                ...formattedSub
            };
            setSubcontractors(prev => [...prev, newEntry]);
            if (setGlobalSubcontractors) {
                setGlobalSubcontractors(prev => {
                    const cleanedRut = cleanRut(newEntry.rut);
                    const existsInGlobal = prev.some(s => cleanRut(s.rut) === cleanedRut);
                    if (existsInGlobal) {
                        return prev.map(s => cleanRut(s.rut) === cleanedRut ? { ...s, ...formattedSub } : s);
                    }
                    return [...prev, newEntry];
                });
            }
        }
        setCreateMasterSubModalOpen(false);
        setEditingSubcontractorId(null);
        setNewSubcontractor({
            rut: '',
            razonSocial: '',
            giro: '',
            representante: '',
            telefono: '',
            correo: '',
            subcontractTypeId: '',
            fono: '',
            ciudad: '',
            comuna: '',
            status: 'Activo',
            linkedUserIds: []
        });
        setListMasterSubModalOpen(true);
    };

    const handleEditMasterSubcontractor = (sub: Subcontractor) => {
        setEditingSubcontractorId(sub.id);
        setNewSubcontractor({
            rut: sub.rut,
            razonSocial: sub.razonSocial,
            giro: sub.giro || '',
            representante: sub.representante || '',
            telefono: sub.telefono || '',
            correo: sub.correo || '',
            subcontractTypeId: sub.subcontractTypeId || '',
            fono: sub.fono || '',
            ciudad: sub.ciudad || '',
            comuna: sub.comuna || '',
            status: sub.status || 'Activo',
            linkedUserIds: sub.linkedUserIds || []
        });
        setListMasterSubModalOpen(false);
        setCreateMasterSubModalOpen(true);
    };

    const handleCreateSubcontractType = () => {
        if (!newTypeName.trim()) return;
        const newType: SubcontractType = {
            id: 'st-' + Math.random().toString(36).substr(2, 5),
            name: newTypeName.trim()
        };
        setSubcontractTypes([...subcontractTypes, newType]);
        setNewTypeName('');
    };

    // 2. Open "Add Contract" Modal (CREATE MODE)
    const handleOpenAddStatement = () => {
        setCurrentForm({
            id: '',
            subcontractorId: '',
            rut: '',
            razonSocial: '',
            startDate: '',
            endDate: '',
            retention: 0,
            items: [],
            attachedDocuments: [],
            linkedUserIds: [],
            signatures: {}
        });
        setAddStatementModalOpen(true);
    };

    // 2.5 Open "Edit Contract" Modal (EDIT MODE)
    const handleEditStatement = (contractId: string | null = selectedContractId) => {
        const stmt = contracts.find(p => p.id === contractId);
        if (!stmt) return;

        const sub = getSubcontractorById(stmt.subcontractorId);

        setCurrentForm({
            id: stmt.id,
            subcontractorId: stmt.subcontractorId,
            rut: sub?.rut || '',
            razonSocial: sub?.razonSocial || '',
            startDate: stmt.startDate || '',
            endDate: stmt.endDate || '',
            retention: stmt.retention || 0,
            items: stmt.items ? JSON.parse(JSON.stringify(stmt.items)) : [], // Deep copy
            attachedDocuments: (stmt as any).attachedDocuments ? [...(stmt as any).attachedDocuments] : ((stmt as any).signedContractUrl ? [(stmt as any).signedContractUrl] : []),
            linkedUserIds: sub?.linkedUserIds || [],
            signatures: stmt.signatures || {}
        });
        setAddStatementModalOpen(true);
    };

    // 3. Pick Subcontractor logic
    const handlePickSubcontractor = (sub: Subcontractor) => {
        setCurrentForm(prev => ({
            ...prev,
            subcontractorId: sub.id,
            rut: sub.rut,
            razonSocial: sub.razonSocial,
            linkedUserIds: sub.linkedUserIds || []
        }));
        setSubcontractorPickerOpen(false);
    };

    // 4. Selection Logic Flow
    const handleOpenTaskSelector = (forAnexo: boolean = false) => {
        setIsAnexoMode(forAnexo === true);
        setTaskSelectorOpen(true);
    };

    const handleTaskSelect = (task: Task, indexCode: string) => {
        setSelectedTaskForAdd({ id: task.id, code: indexCode, name: task.name });
        setTaskSelectorOpen(false);
        // Load existing selection if editing
        const existingItem = isAnexoMode
            ? currentAnexoForm.items?.find((i: StatementItem) => i.id === task.id)
            : currentForm.items.find((i: StatementItem) => i.id === task.id);
        
        if (existingItem) {
            setTempSelectedUnits(existingItem.details.map((d: UnitDetail) => d.unitId));
        } else {
            setTempSelectedUnits([]);
        }
        setUnitSelectorOpen(true);
    };

    // NEW LOGIC FOR GENERATE EP
    const handleGenerateEPClick = () => {
        // Find the selected contract to use as base
        const sourceContract = contracts.find(p => p.id === selectedContractId);

        if (!sourceContract) {
            alert("Seleccione un subcontrato de la lista para usar como base.");
            return;
        }

        if (!sourceContract.items || sourceContract.items.length === 0) {
            alert("El subcontrato seleccionado no tiene partidas asignadas. Agregue partidas mediante 'Editar' antes de generar un EP.");
            return;
        }

        // Consolidate base items + active anexos
        const consolidatedItemsMap = new Map<string, StatementItem>();
        
        // 1. Add base contract items
        sourceContract.items.forEach(item => {
            consolidatedItemsMap.set(item.id, JSON.parse(JSON.stringify(item)));
        });
        
        // 2. Add anexo items
        if (sourceContract.anexos) {
            sourceContract.anexos.filter(a => a.status === 'Activo').forEach(anexo => {
                anexo.items.forEach(aItem => {
                    if (aItem.totalValue < 0) return; // Skip eliminations, they were already removed from base contract items during authorization
                    
                    if (consolidatedItemsMap.has(aItem.id)) {
                        const existingItem = consolidatedItemsMap.get(aItem.id)!;
                        existingItem.totalValue += aItem.totalValue;
                        // Merge details
                        aItem.details.forEach(aDetail => {
                            const existingDetail = existingItem.details.find(d => d.unitId === aDetail.unitId);
                            if (existingDetail) {
                                existingDetail.value += aDetail.value;
                                existingDetail.quantity = ((parseFloat(existingDetail.quantity || '0') || 0) + (parseFloat(aDetail.quantity || '0') || 0)).toString();
                            } else {
                                existingItem.details.push(JSON.parse(JSON.stringify(aDetail)));
                            }
                        });
                    } else {
                        consolidatedItemsMap.set(aItem.id, JSON.parse(JSON.stringify(aItem)));
                    }
                });
            });
        }

        const draftItems: StatementItem[] = Array.from(consolidatedItemsMap.values()).map(item => ({
            ...item,
            code: resolveItemGanttCode(item.id, item.code),
            paidAmount: 0,
            selectedUnitIds: [], // Start with no units selected for payment in this new EP
            details: item.details.map(d => ({ ...d })) // Deep copy details (the scope)
        }));

        // Initialize Draft
        setCurrentDraftEp({
            id: `ep-${Date.now()}`,
            contractId: sourceContract.id,
            subcontractorId: sourceContract.subcontractorId,
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0],
            status: 'Borrador',
            items: draftItems
        });

        setGenerateEpOpen(true);
    };

    const handleOpenEpDetail = (task: StatementItem) => {
        setActiveDraftTask({ id: task.id, name: task.description, code: resolveItemGanttCode(task.id, task.code) });
        // Use the saved selection state in the draft, or empty if none
        setTempSelectedDetailUnits(task.selectedUnitIds || []);
        setDetailEpOpen(true);
    };

    const toggleUnitSelection = (unitId: string) => {
        setTempSelectedUnits(prev =>
            prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]
        );
    };

    const handleConfirmUnits = () => {
        if (!selectedTaskForAdd) return;

        const details: UnitDetail[] = [];
        let totalVal = 0;

        // Collect details for selected units
        const validUnits = getValidUnitsForTask(selectedTaskForAdd.id);
        
        tempSelectedUnits.forEach(uId => {
            const unitInfo = validUnits.find(v => v.id === uId);
            if (!unitInfo) return;

            details.push({
                unitId: uId,
                unitName: unitInfo.name,
                value: unitInfo.value,
                quantity: unitInfo.quantity,
                unitPrice: unitInfo.unitPrice,
            });
            totalVal += unitInfo.value;
        });

        const newItem: StatementItem = {
            id: selectedTaskForAdd.id,
            code: selectedTaskForAdd.code,
            description: isAnexoMode
                ? (selectedTaskForAdd.name.endsWith('(ANEXO)') ? selectedTaskForAdd.name : `${selectedTaskForAdd.name} (ANEXO)`)
                : selectedTaskForAdd.name,
            totalValue: totalVal, // Total contract value for selected units
            paidAmount: 0,
            details: details,
            expanded: false
        };

        // Add to the regular Add/Edit Contract or Anexo Form
        if (isAnexoMode) {
            setCurrentAnexoForm(prev => {
                const exists = prev.items?.find(i => i.id === newItem.id);
                if (exists) {
                    return { ...prev, items: prev.items?.map(i => i.id === newItem.id ? newItem : i) };
                }
                return { ...prev, items: [...(prev.items || []), newItem] };
            });
        } else {
            setCurrentForm(prev => {
                const exists = prev.items.find(i => i.id === newItem.id);
                if (exists) {
                    return { ...prev, items: prev.items.map(i => i.id === newItem.id ? newItem : i) };
                }
                return { ...prev, items: [...prev.items, newItem] };
            });
        }

        setUnitSelectorOpen(false);
        setTaskSelectorOpen(false);
        setSelectedTaskForAdd(null);
        setTempSelectedUnits([]);
    };

    // Helper: Compute pending balance and protocol status for a physical unit across all EPs of the contract
    const getUnitPendingBalanceAndStatus = (
        contractId: string,
        taskId: string,
        detail: UnitDetail,
        currentDraftEpId: string
    ) => {
        const totalQty = parseFloat(detail.quantity || '0') || 0;
        const unitPrice = parseFloat(detail.unitPrice || '0') || 0;
        const unitTotalValue = detail.value || (totalQty * unitPrice);

        const allOtherEPs = paymentStatements.filter(ep => 
            ep.contractId === contractId && 
            ep.id !== currentDraftEpId
        );

        // 1. Check if unit is in an IN-PROCESS EP (Borrador, Enviado, Pendiente, En proceso, En Espera)
        const inProcessEP = allOtherEPs.find(ep => {
            const isApproved = ep.epApprovalStatus === 'Aprobado' || ep.status === 'Aprobado';
            const isAnulado = ep.status === 'Anulado';
            if (isApproved || isAnulado) return false;
            return ep.items?.some(i => i.id === taskId && i.selectedUnitIds?.includes(detail.unitId));
        });

        if (inProcessEP) {
            return {
                status: 'IN_PROCESS',
                label: 'En Proceso',
                pendingBalance: 0,
                isPayable: false
            };
        }

        // 2. Check PREVIOUS APPROVED EPs
        const previousApprovedEPs = allOtherEPs.filter(ep => 
            ep.epApprovalStatus === 'Aprobado' || ep.status === 'Aprobado'
        );

        let totalNetPaid = 0;
        let wasPaidInPreviousApprovedEP = false;

        previousApprovedEPs.forEach(pEp => {
            const pItem = pEp.items?.find(i => i.id === taskId);
            if (pItem?.selectedUnitIds?.includes(detail.unitId)) {
                wasPaidInPreviousApprovedEP = true;
                const pDetail = pItem.details?.find(d => d.unitId === detail.unitId);
                const q = parseFloat(pDetail?.quantity || '0') || 0;
                const pGross = pDetail?.value || (q * unitPrice);

                let pEpDiscountPercent = 0;
                if (pEp.roadmapEvaluations) {
                    revisionAreas.forEach(area => {
                        const maxDcto = area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10;
                        let aplicablesCount = 0;
                        let sumaFactores = 0;
                        area.parameters.forEach(p => {
                            const evalAns = pEp.roadmapEvaluations?.[p.id];
                            const st = typeof evalAns === 'string' ? evalAns : evalAns?.status;
                            const pd = typeof evalAns === 'object' ? evalAns?.observedPeriods || 1 : 1;
                            if (st === 'cumple') aplicablesCount++;
                            else if (st === 'no_cumple') {
                                aplicablesCount++;
                                sumaFactores += (pd === 3 ? 2.0 : pd === 2 ? 1.5 : 1.0);
                            }
                        });
                        if (aplicablesCount > 0 && sumaFactores > 0) {
                            pEpDiscountPercent += Math.min((maxDcto * sumaFactores) / aplicablesCount, maxDcto);
                        }
                    });
                }
                const pDcto = Math.round(pGross * (pEpDiscountPercent / 100));
                totalNetPaid += Math.max(0, pGross - pDcto);
            }
        });

        const pendingBalance = Math.max(0, unitTotalValue - totalNetPaid);

        if (wasPaidInPreviousApprovedEP && pendingBalance <= 0) {
            return {
                status: 'FULLY_PAID',
                label: 'Pagado',
                pendingBalance: 0,
                isPayable: false
            };
        } else if (wasPaidInPreviousApprovedEP && pendingBalance > 0) {
            return {
                status: 'PENDING_BALANCE',
                label: 'Saldo Pendiente',
                pendingBalance: pendingBalance,
                isPayable: true
            };
        } else {
            const payable = isProtocolPayable(taskId, detail.unitId);
            return {
                status: payable ? 'APPROVED' : 'NOT_APPROVED',
                label: payable ? 'SI' : 'NO',
                pendingBalance: payable ? unitTotalValue : 0,
                isPayable: payable
            };
        }
    };

    // Logic for Detail EP Modal (Saving selected payable units)
    const handleSaveDetailEp = () => {
        if (!activeDraftTask || !currentDraftEp.items) return;

        // Find the item in draft
        const itemIndex = currentDraftEp.items.findIndex(i => i.id === activeDraftTask.id);
        if (itemIndex === -1) return;

        const item = currentDraftEp.items[itemIndex];

        let newPaidAmount = 0;

        // Sum up payable amounts (pending balance for previously discounted units, or full value for new units)
        item.details.forEach(d => {
            if (tempSelectedDetailUnits.includes(d.unitId)) {
                const info = getUnitPendingBalanceAndStatus(
                    currentDraftEp.contractId || '',
                    activeDraftTask.id,
                    d,
                    currentDraftEp.id
                );
                newPaidAmount += info.pendingBalance;
            }
        });

        // Update Draft with new amount AND save the selection state
        const updatedItem = {
            ...item,
            paidAmount: newPaidAmount,
            selectedUnitIds: tempSelectedDetailUnits
        };

        const newItems = [...currentDraftEp.items];
        newItems[itemIndex] = updatedItem;

        setCurrentDraftEp({ ...currentDraftEp, items: newItems });
        setDetailEpOpen(false);
    };

    const toggleDetailUnitSelection = (unitId: string, amount: number, isPendingBalance: boolean = false) => {
        if (!activeDraftTask) return;
        if (!isPendingBalance && !isProtocolPayable(activeDraftTask.id, unitId)) {
            alert("No se puede seleccionar esta unidad: El protocolo de calidad aún no ha sido aprobado por el revisor correspondiente para pago.");
            return;
        }
        if (amount <= 0) return;
        setTempSelectedDetailUnits(prev =>
            prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]
        );
    };

    const handleSaveDraftEp = () => {
        if (!currentDraftEp.id) return;
        const totalAmount = currentDraftEp.items?.reduce((acc, i) => acc + i.paidAmount, 0) || 0;
        const updatedEp: PaymentStatement = {
            id: currentDraftEp.id,
            contractId: currentDraftEp.contractId || '',
            subcontractorId: currentDraftEp.subcontractorId || '',
            period: currentDraftEp.period || 'Nuevo Periodo',
            progress: currentDraftEp.progress || 0,
            amount: `${totalAmount.toLocaleString()} CLP`,
            status: currentDraftEp.status || 'Borrador',
            startDate: currentDraftEp.startDate,
            endDate: currentDraftEp.endDate,
            items: currentDraftEp.items,
            preliminaryAmount: currentDraftEp.preliminaryAmount !== undefined ? currentDraftEp.preliminaryAmount : totalAmount,
            roadmapApprovedAmount: currentDraftEp.roadmapApprovedAmount,
            roadmapEvaluations: currentDraftEp.roadmapEvaluations,
            roadmapStatus: currentDraftEp.roadmapStatus,
            epApprovalStatus: currentDraftEp.epApprovalStatus
        };

        setPaymentStatements(prev => {
            const exists = prev.some(p => p.id === updatedEp.id);
            if (exists) {
                return prev.map(p => p.id === updatedEp.id ? updatedEp : p);
            }
            return [...prev, updatedEp];
        });

        setGenerateEpOpen(false);
    };

    // Authorization
    const handleAuthorizeClick = () => {
        setSignatureModalOpen(true);
    };

    const handleConfirmSignature = () => {
        // Save the Draft EP as a real "Autorizado" EP
        const totalAmount = currentDraftEp.items?.reduce((acc, i) => acc + i.paidAmount, 0) || 0;

        const newEp: PaymentStatement = {
            id: currentDraftEp.id || 'err',
            contractId: currentDraftEp.contractId || '',
            subcontractorId: currentDraftEp.subcontractorId || '',
            period: currentDraftEp.period || 'Nuevo Periodo',
            progress: 50,
            amount: `${totalAmount.toLocaleString()} CLP`,
            status: 'Autorizado',
            startDate: currentDraftEp.startDate,
            endDate: currentDraftEp.endDate,
            items: currentDraftEp.items,
            preliminaryAmount: currentDraftEp.preliminaryAmount !== undefined ? currentDraftEp.preliminaryAmount : totalAmount,
            roadmapApprovedAmount: currentDraftEp.roadmapApprovedAmount,
            roadmapEvaluations: currentDraftEp.roadmapEvaluations,
            roadmapStatus: currentDraftEp.roadmapStatus,
            epApprovalStatus: 'Aprobado'
        };

        addActivity({
            type: 'subcontracts',
            title: 'Estado de Pago Autorizado',
            description: `Se ha autorizado un EP por ${newEp.amount} para el subcontratista ${getSubcontractorById(newEp.subcontractorId)?.razonSocial}`,
            user: 'Usuario'
        });

        setPaymentStatements(prev => {
            const exists = prev.some(p => p.id === newEp.id);
            if (exists) {
                return prev.map(p => p.id === newEp.id ? newEp : p);
            }
            return [...prev, newEp];
        });

        setSignatureModalOpen(false);
        setGenerateEpOpen(false);
    };

    // Toggle expansion of rows in main table
    const toggleItemExpansion = (itemId: string, forAnexo = false) => {
        if (forAnexo) {
            setCurrentAnexoForm(prev => ({
                ...prev,
                items: prev.items?.map(item => item.id === itemId ? { ...item, expanded: !item.expanded } : item)
            }));
        } else {
            setCurrentForm(prev => ({
                ...prev,
                items: prev.items.map(item => item.id === itemId ? { ...item, expanded: !item.expanded } : item)
            }));
        }
    };

    // 5.5 Anexos Flow
    const handleCreateAnexo = () => {
        setCurrentAnexoForm({
            id: '',
            numero: `Anexo N°${(selectedContract?.anexos?.length || 0) + 1}`,
            fecha: new Date().toISOString().split('T')[0],
            description: '',
            items: [],
            totalAmount: 0,
            status: 'Borrador'
        });
        setExpandedAnexoItemIds(new Set());
        setAnexoFormModalOpen(true);
    };

    const toggleAnexoItemExpansion = (itemId: string) => {
        setExpandedAnexoItemIds(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return next;
        });
    };

    const handleToggleEliminateUnitInAnexo = (baseItem: StatementItem, unitId: string) => {
        setCurrentAnexoForm(prev => {
            const items = [...(prev.items || [])];
            const existingIdx = items.findIndex(i => i.id === baseItem.id);
            
            let itemToUpdate: StatementItem;
            let eliminatedUnitIds: string[] = [];
            
            if (existingIdx >= 0) {
                itemToUpdate = { ...items[existingIdx] };
                eliminatedUnitIds = (itemToUpdate as any).eliminatedUnitIds ? [...(itemToUpdate as any).eliminatedUnitIds] : [];
            } else {
                itemToUpdate = {
                    ...baseItem,
                    totalValue: 0,
                    details: [],
                    expanded: true
                };
            }

            if (eliminatedUnitIds.includes(unitId)) {
                eliminatedUnitIds = eliminatedUnitIds.filter(id => id !== unitId);
            } else {
                eliminatedUnitIds = [...eliminatedUnitIds, unitId];
            }

            (itemToUpdate as any).eliminatedUnitIds = eliminatedUnitIds;

            const eliminatedUnitsDetails = baseItem.details.filter(d => eliminatedUnitIds.includes(d.unitId));
            const negValue = -eliminatedUnitsDetails.reduce((sum, d) => sum + d.value, 0);
            itemToUpdate.totalValue = negValue;
            itemToUpdate.details = eliminatedUnitsDetails.map(d => ({
                ...d,
                value: -d.value,
                quantity: `-${d.quantity}`
            }));

            if (eliminatedUnitIds.length === 0) {
                return {
                    ...prev,
                    items: items.filter(i => i.id !== baseItem.id)
                };
            }

            if (existingIdx >= 0) {
                items[existingIdx] = itemToUpdate;
            } else {
                items.push(itemToUpdate);
            }

            return { ...prev, items };
        });
    };

    const handleToggleEliminatePartidaInAnexo = (baseItem: StatementItem) => {
        setCurrentAnexoForm(prev => {
            const items = [...(prev.items || [])];
            const existing = items.find(i => i.id === baseItem.id);
            const allUnitIds = baseItem.details.map(d => d.unitId);
            const currentEliminated = (existing as any)?.eliminatedUnitIds || [];
            const isAllEliminated = currentEliminated.length === allUnitIds.length && allUnitIds.length > 0;

            if (isAllEliminated || existing?.totalValue === -baseItem.totalValue) {
                return {
                    ...prev,
                    items: items.filter(i => i.id !== baseItem.id)
                };
            } else {
                const negItem: StatementItem = {
                    ...baseItem,
                    totalValue: -baseItem.totalValue,
                    details: baseItem.details.map(d => ({ ...d, value: -d.value, quantity: `-${d.quantity}` })),
                    expanded: false
                };
                (negItem as any).eliminatedUnitIds = allUnitIds;
                return {
                    ...prev,
                    items: [...items.filter(i => i.id !== baseItem.id), negItem]
                };
            }
        });
    };

    const handleEditAnexo = (anexo: AnexoContrato) => {
        setCurrentAnexoForm(JSON.parse(JSON.stringify(anexo)));
        setAnexoFormModalOpen(true);
    };

    const handleSaveAnexo = () => {
        if (!selectedContractId) return;
        
        const totalAmount = currentAnexoForm.items?.reduce((acc, i) => acc + i.totalValue, 0) || 0;
        
        setContracts(prev => prev.map(p => {
            if (p.id === selectedContractId) {
                const existingAnexos = p.anexos || [];
                let newAnexos;
                
                if (currentAnexoForm.id) {
                    newAnexos = existingAnexos.map(a => a.id === currentAnexoForm.id ? { ...currentAnexoForm, totalAmount } as AnexoContrato : a);
                } else {
                    const newAnexo: AnexoContrato = {
                        ...(currentAnexoForm as AnexoContrato),
                        id: `anx-${Math.floor(Math.random() * 10000)}`,
                        totalAmount
                    };
                    newAnexos = [...existingAnexos, newAnexo];
                }
                
                return { ...p, anexos: newAnexos };
            }
            return p;
        }));
        
        setAnexoFormModalOpen(false);
    };

    const handleAuthorizeAnexo = () => {
        if (!selectedContractId || !currentAnexoForm.id) return;
        
        const totalAmount = currentAnexoForm.items?.reduce((acc, i) => acc + i.totalValue, 0) || 0;
        
        setContracts(prev => prev.map(p => {
            if (p.id === selectedContractId) {
                const existingAnexos = p.anexos || [];
                const newAnexos = existingAnexos.map(a => a.id === currentAnexoForm.id ? { ...currentAnexoForm, totalAmount, status: 'Activo' } as AnexoContrato : a);
                
                let updatedBaseItems: StatementItem[] = JSON.parse(JSON.stringify(p.items));
                const anexoItemsMap = new Map((currentAnexoForm.items || []).map(i => [i.id, i]));

                // 1. Process eliminations / reductions on existing base items
                updatedBaseItems = updatedBaseItems.map(baseItem => {
                    const adjItem = anexoItemsMap.get(baseItem.id);
                    if (!adjItem) return baseItem;

                    // If totalValue < 0 and no details specified, or totalValue equals -baseItem.totalValue, entire item is eliminated
                    if (adjItem.totalValue < 0 && (!adjItem.details || adjItem.details.length === 0 || Math.abs(adjItem.totalValue) === baseItem.totalValue)) {
                        return null;
                    }

                    const eliminatedUnitIds = (adjItem as any).eliminatedUnitIds as string[] | undefined || [];
                    const negativeUnitIds = adjItem.details?.filter(d => d.value < 0 || (d.quantity && d.quantity.startsWith('-'))).map(d => d.unitId) || [];
                    const allEliminatedIds = Array.from(new Set([...eliminatedUnitIds, ...negativeUnitIds]));

                    if (allEliminatedIds.length > 0) {
                        const remainingDetails = baseItem.details.filter(d => !allEliminatedIds.includes(d.unitId));
                        if (remainingDetails.length === 0) {
                            return null; // All units eliminated
                        }
                        const newTotalValue = remainingDetails.reduce((sum, d) => sum + d.value, 0);
                        return {
                            ...baseItem,
                            details: remainingDetails,
                            totalValue: newTotalValue
                        };
                    }

                    return baseItem;
                }).filter((item): item is StatementItem => item !== null);

                // 2. Process NEW items added in this Anexo (items with positive value not present in base contract)
                const addedItems = (currentAnexoForm.items || []).filter(i => 
                    !p.items.some(bi => bi.id === i.id) && i.totalValue > 0
                );

                addedItems.forEach(newItem => {
                    const itemToAdd: StatementItem = {
                        ...newItem,
                        description: newItem.description.endsWith('(ANEXO)') ? newItem.description : `${newItem.description} (ANEXO)`
                    };
                    updatedBaseItems.push(itemToAdd);
                });

                // Update base contract total amount
                const updatedBaseTotalAmount = updatedBaseItems.reduce((acc, i) => acc + i.totalValue, 0);
                
                return { ...p, items: updatedBaseItems, anexos: newAnexos, totalAmount: updatedBaseTotalAmount };
            }
            return p;
        }));
        
        setAnexoFormModalOpen(false);
        addActivity({
            type: 'subcontracts',
            title: 'Anexo Autorizado',
            description: `Se ha autorizado el ${currentAnexoForm.numero}. Los cambios han sido aplicados al contrato base.`,
            user: 'Usuario'
        });
    };

    // 5. Save the whole CONTRACT (Standard Form)
    const handleSaveContract = () => {
        if (!currentForm.subcontractorId) {
            alert("Debe seleccionar un Subcontratista (Razón Social)");
            return;
        }

        setSubcontractors(prev => prev.map(s => {
            if (s.id === currentForm.subcontractorId) {
                return {
                    ...s,
                    linkedUserIds: currentForm.linkedUserIds
                };
            }
            return s;
        }));

        const totalAmount = currentForm.items.reduce((acc, i) => acc + i.totalValue, 0);

        const isContractValid = Boolean(currentForm.subcontractorId && currentForm.items.length > 0 && currentForm.rut && currentForm.razonSocial && currentForm.startDate && currentForm.endDate && currentForm.retention !== undefined && ((currentForm as any).signedContractUrl || (currentForm.attachedDocuments && currentForm.attachedDocuments.length > 0)));
        const finalStatus = isContractValid ? 'Activo' : 'En Proceso';

        const allSigned = approvalFlows.subcontracts.every(step => currentForm.signatures?.[step.reviewerId]);
        const finalStatusWithSignatures = (isContractValid && allSigned) ? 'Activo' : 'En Proceso';

        // Check if updating or creating
        if (currentForm.id) {
            // Update
            setContracts(prev => prev.map(p => {
                if (p.id === currentForm.id) {
                    return {
                        ...p,
                        subcontractorId: currentForm.subcontractorId,
                        startDate: currentForm.startDate,
                        endDate: currentForm.endDate,
                        retention: currentForm.retention,
                        items: currentForm.items,
                        totalAmount: totalAmount,
                        status: p.status === 'Inactivo' ? 'Inactivo' : finalStatusWithSignatures,
                        signatures: currentForm.signatures
                    };
                }
                return p;
            }));

            if (setGlobalContracts) {
                setGlobalContracts(prev => prev.map(p => {
                    if (p.id === currentForm.id) {
                        return {
                            ...p,
                            projectId: (p as any).projectId || projectInfo.id || projectInfo.name,
                            subcontractorId: currentForm.subcontractorId,
                            startDate: currentForm.startDate,
                            endDate: currentForm.endDate,
                            retention: currentForm.retention,
                            items: currentForm.items,
                            totalAmount: totalAmount,
                            status: p.status === 'Inactivo' ? 'Inactivo' : finalStatusWithSignatures,
                            signatures: currentForm.signatures
                        };
                    }
                    return p;
                }));
            }
        } else {
            // Create New
            const newContract: Contract = {
                id: `ctr-${Math.floor(Math.random() * 10000)}`,
                subcontractorId: currentForm.subcontractorId,
                startDate: currentForm.startDate,
                endDate: currentForm.endDate,
                retention: currentForm.retention,
                items: currentForm.items,
                totalAmount: totalAmount,
                status: finalStatusWithSignatures,
                signatures: currentForm.signatures || {}
            };
            (newContract as any).projectId = projectInfo.id || projectInfo.name;
            setContracts([...contracts, newContract]);
            setSelectedContractId(newContract.id);

            if (setGlobalContracts) {
                setGlobalContracts(prev => {
                    const exists = prev.some(c => c.id === newContract.id);
                    return exists ? prev.map(c => c.id === newContract.id ? newContract : c) : [...prev, newContract];
                });
            }
        }

        // Ensure contractor is also in globalSubcontractors
        const activeSub = subcontractors.find(s => s.id === currentForm.subcontractorId);
        if (activeSub && setGlobalSubcontractors) {
            setGlobalSubcontractors(prev => {
                const cleanedRut = cleanRut(activeSub.rut);
                const exists = prev.some(s => s.id === activeSub.id || cleanRut(s.rut) === cleanedRut);
                if (exists) {
                    return prev.map(s => (s.id === activeSub.id || cleanRut(s.rut) === cleanedRut) ? { ...s, ...activeSub } : s);
                }
                return [...prev, activeSub];
            });
        }

        const subName = getSubcontractorById(currentForm.subcontractorId)?.razonSocial || 'Desconocido';
        addActivity({
            type: 'subcontracts',
            title: currentForm.id ? 'Subcontrato Actualizado' : 'Nuevo Subcontrato Creado',
            description: `Subcontrato para ${subName} - Monto Total: $${totalAmount.toLocaleString()}`,
            user: 'Usuario'
        });

        setAddStatementModalOpen(false);
    };

    // 6. Authorize the Contract (Changes Status)
    const handleAuthorizeContract = () => {
        if (!currentForm.id) return;

        const totalAmount = currentForm.items.reduce((acc, i) => acc + i.totalValue, 0);

        setContracts(prev => prev.map(p => {
            if (p.id === currentForm.id) {
                return {
                    ...p,
                    subcontractorId: currentForm.subcontractorId,
                    startDate: currentForm.startDate,
                    endDate: currentForm.endDate,
                    retention: currentForm.retention,
                    items: currentForm.items,
                    totalAmount: totalAmount,
                    status: 'Activo'
                };
            }
            return p;
        }));
        setAddStatementModalOpen(false);
    };

    // 7. Handle View EP Detail from List
    // We are viewing the detail of an existing EP here (Read Only view usually)
    const handleOpenEPDetailFromList = (ep: PaymentStatement) => {
        setSelectedEPForView(ep);
        setEpDetailModalOpen(true);
        // Initialize retention from contract if available
        const contract = contracts.find(c => c.id === ep.contractId);
        if (contract) {
            setRetentionPercentageInView(contract.retention || 0);
        }
    };

    // Helpers
    const getSubcontractorById = (id: string) => subcontractors.find(s => s.id === id);
    const selectedContract = contracts.find(ep => ep.id === selectedContractId);

    // Check if the currently editing form belongs to an Authorized contract
    const isEditingAuthorized = currentForm.id
        ? contracts.find(p => p.id === currentForm.id)?.status === 'Activo'
        : false;

    // Check if a unit (or EDT anexo) is already assigned to ANY contract
    const isUnitAlreadyContracted = (unitId: string, taskId: string, excludeContractId?: string) => {
        return contracts.filter(c => !c.isDeleted).some(c => {
            if (excludeContractId && c.id === excludeContractId) return false;
            // Check items in base contract
            return c.items.some(item => {
                if (item.id !== taskId) return false;
                return item.details.some(d => d.unitId === unitId);
            }) || (c.anexos || []).some(anexo => {
                // Check items in active anexos
                if (anexo.status === 'Anulado') return false;
                return anexo.items.some(item => {
                    if (item.id !== taskId) return false;
                    return item.details.some(d => d.unitId === unitId);
                });
            });
        });
    };

    // Get valid units for the currently selected task (those that have a value in matrix)
    const getValidUnitsForTask = (taskId: string) => {
        const validResults: any[] = [];
        units.forEach(u => {
            // 1. Check Base Value
            const rawVal = matrixData[`payment_${taskId}_${u.id}`];
            const val = rawVal ? parseInt(rawVal.replace(/\D/g, ''), 10) : 0;

            if (val > 0) {
                const qty = matrixData[`payment_qty_${taskId}_${u.id}`] || '';
                const unitPrice = matrixData[`payment_unit_${taskId}_${u.id}`] || '';

                validResults.push({
                    ...u,
                    value: val,
                    quantity: qty,
                    unitPrice: unitPrice
                });
            }

            // 2. Check for Anexos in EDT
            const anexosKey = `payment_anexos_${taskId}_${u.id}`;
            const anexosJson = matrixData[anexosKey];
            if (anexosJson) {
                try {
                    const anexos = JSON.parse(anexosJson);
                    anexos.forEach((a: any) => {
                        const anexoTotal = (parseFloat(a.quantity) || 0) * (parseFloat(a.unitPrice) || 0);
                        if (anexoTotal > 0) {
                            validResults.push({
                                ...u,
                                id: `${u.id}_${a.id}`, // Unique composite ID for selection
                                parentUnitId: u.id,
                                name: `${u.name} (${a.name})`,
                                value: anexoTotal,
                                quantity: a.quantity,
                                unitPrice: a.unitPrice,
                                isEdtAnexo: true
                            });
                        }
                    });
                } catch (e) {}
            }
        });

        // Filter by Exclusivity (unless we are EDITING the current contract/anexo and it's already there)
        // For simplicity in this mock, we filter out everything already contracted.
        return validResults.filter(r => !isUnitAlreadyContracted(r.id, taskId));
    };

    // Check if a protocol allows payment for a task/unit based on reviewer flags
    const isProtocolPayable = (taskId: string, unitId: string) => {
        return Object.values(savedProtocols).some((p: ProtocolState) => {
            if (p.taskId === taskId && p.unitId === unitId) {
                const config = protocolConfigs[taskId];
                const group = p.groupId
                    ? config?.reviewerGroups?.find(g => g.id === p.groupId)
                    : config?.reviewerGroups?.find(g => g.unitIds.includes(p.unitId));

                if (group) {
                    return group.reviewers.some((rev, idx) =>
                        rev.generatesPayment && p.signatures[idx]
                    );
                }
            }
            return false;
        });
    };

    const handleCancelPaymentStatement = (epId: string) => {
        const ep = paymentStatements.find(p => p.id === epId);
        if (!ep) return;

        if (!confirm(`¿Está seguro que desea anular el Estado de Pago #${paymentStatements.filter(p => p.contractId === ep.contractId).findIndex(p => p.id === epId) + 1}? Esta acción no se puede deshacer.`)) {
            return;
        }

        setPaymentStatements(prev => prev.map(p => p.id === epId ? { ...p, status: 'Anulado' } : p));

        const subName = getSubcontractorById(ep.subcontractorId)?.razonSocial || 'Desconocido';
        addActivity({
            type: 'subcontracts',
            title: 'Estado de Pago Anulado',
            description: `Se ha anulado el EP por ${ep.amount} para el subcontratista ${subName}`,
            user: 'Usuario'
        });
    };

    // Recursive task renderer for selector
    const renderTaskSelectorItems = (list: Task[], level = 0) => {
        let items: React.ReactNode[] = [];
        list.forEach((t) => {
            const ganttNum = getTaskGanttRowNumber(t, tasks);
            const currentCode = ganttNum !== null && ganttNum !== undefined ? `#${ganttNum}` : '-';
            const padding = level * 20 + 16;

            const isFolder = t.type === 'folder' || (t.children && t.children.length > 0);

            if (isFolder) {
                items.push(
                    <div key={t.id} className="py-2 px-4 bg-slate-50 font-bold text-slate-700 text-sm border-b border-slate-100 whitespace-nowrap min-w-max" style={{ paddingLeft: padding }}>
                        <span className="font-mono text-slate-400 mr-2">{currentCode}</span>{t.name}
                    </div>
                );
                if (t.children) items = [...items, ...renderTaskSelectorItems(t.children, level + 1)];
            } else {
                const hasAssignedUnits = units.some(u => {
                    const rawVal = matrixData[`payment_${t.id}_${u.id}`];
                    return rawVal ? parseInt(rawVal.replace(/\D/g, ''), 10) > 0 : false;
                });

                items.push(
                    <div
                        key={t.id}
                        onClick={() => handleTaskSelect(t, currentCode)}
                        className="py-2 px-4 hover:bg-blue-50 cursor-pointer text-sm text-slate-600 border-b border-slate-100 flex justify-between items-center group whitespace-nowrap min-w-max gap-4"
                        style={{ paddingLeft: padding }}
                    >
                        <div className="flex items-center gap-2">
                            {hasAssignedUnits ? (
                                <span className="material-symbols-outlined text-[16px] text-green-600 font-bold shrink-0" title="Tiene presupuesto asignado">attach_money</span>
                            ) : (
                                <span className="w-[16px] shrink-0" />
                            )}
                            <span><span className="font-mono text-slate-400 mr-2">{currentCode}</span>{t.name}</span>
                        </div>
                        <span className="material-symbols-outlined text-[18px] text-slate-300 group-hover:text-primary shrink-0">arrow_forward</span>
                    </div>
                );
            }
        });
        return items;
    };

    const resolveItemGanttCode = (taskId: string, fallbackCode?: string): string => {
        const found = findTaskById(taskId, tasks);
        if (found) {
            const ganttNum = getTaskGanttRowNumber(found, tasks);
            if (ganttNum !== null && ganttNum !== undefined) {
                return `#${ganttNum}`;
            }
        }
        if (fallbackCode && String(fallbackCode).trim() !== '' && String(fallbackCode).trim() !== '-') {
            const clean = String(fallbackCode).trim();
            return clean.startsWith('#') ? clean : `#${clean}`;
        }
        return '-';
    };

    // Helper to find task by ID in deep structure
    const findTaskById = (id: string, list: Task[]): Task | null => {
        for (const t of list) {
            if (t.id === id) return t;
            if (t.children) {
                const found = findTaskById(id, t.children);
                if (found) return found;
            }
        }
        return null;
    };

    const renderSubcontractTypeModal = () => {
        if (!subcontractTypeModalOpen) return null;

        const filteredTypes = subcontractTypes.filter(t =>
            t.name.toLowerCase().includes(typeSearchQuery.toLowerCase())
        );

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 rounded-t-xl">
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-blue-600">category</span>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Tipos de Subcontrato</h3>
                        </div>
                        <button onClick={() => setSubcontractTypeModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    <div className="p-6 flex flex-col gap-6 overflow-hidden">
                        {/* New Type Creation */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-sm">add_circle</span>
                                <input
                                    type="text"
                                    placeholder="Nombre del nuevo tipo..."
                                    value={newTypeName}
                                    onChange={(e) => setNewTypeName(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateSubcontractType()}
                                />
                            </div>
                            <button
                                onClick={handleCreateSubcontractType}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-md hover:bg-blue-700 transition-colors"
                            >
                                + Crear
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-sm">search</span>
                            <input
                                type="text"
                                placeholder="Buscar tipos..."
                                value={typeSearchQuery}
                                onChange={(e) => setTypeSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 transition-all"
                            />
                        </div>

                        {/* List */}
                        <div className="flex-1 overflow-y-auto min-h-[300px] border border-slate-100 dark:border-slate-800 rounded-lg">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2 text-xs font-bold text-slate-500 uppercase">Nombre del Tipo</th>
                                        <th className="px-4 py-2 text-xs font-bold text-slate-500 uppercase text-right w-20">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTypes.length === 0 ? (
                                        <tr>
                                            <td colSpan={2} className="px-4 py-8 text-center text-slate-500 italic text-sm">
                                                {typeSearchQuery ? 'No se encontraron resultados' : 'No hay tipos creados'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredTypes.map(type => (
                                            <tr key={type.id} className="border-t border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                <td className="px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300">{type.name}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <button
                                                        onClick={() => setSubcontractTypes(subcontractTypes.filter(t => t.id !== type.id))}
                                                        className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                                    >
                                                        <span className="material-symbols-outlined text-lg">delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                        <button
                            onClick={() => setSubcontractTypeModalOpen(false)}
                            className="px-6 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderEPDetailModal = () => {
        if (!selectedEPForView) return null;
        const selectedContractForDetail = contracts.find(c => c.id === selectedEPForView.contractId);
        if (!selectedContractForDetail) return null;

        const sub = getSubcontractorById(selectedEPForView.subcontractorId);

        const allContractEPs = paymentStatements
            .filter(ep => ep.contractId === selectedContractForDetail.id && ep.status !== 'Anulado');

        const currentEpIndex = allContractEPs.findIndex(ep => ep.id === selectedEPForView.id);
        const previousEPs = currentEpIndex > 0 ? allContractEPs.slice(0, currentEpIndex) : [];

        // 1. Calculate Hoja de Ruta Discount Percentage for selectedEPForView
        let epRoadmapDiscountPercent = 0;
        if (selectedEPForView.roadmapEvaluations) {
            revisionAreas.forEach(area => {
                const maxDcto = area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10;
                let aplicablesCount = 0;
                let sumaFactores = 0;
                area.parameters.forEach(p => {
                    const evalAns = selectedEPForView.roadmapEvaluations?.[p.id];
                    const status = typeof evalAns === 'string' ? evalAns : evalAns?.status;
                    const period = typeof evalAns === 'object' ? evalAns?.observedPeriods || 1 : 1;
                    const isCumple = status === 'cumple';
                    const isNoCumple = status === 'no_cumple';

                    if (isCumple) {
                        aplicablesCount++;
                    } else if (isNoCumple) {
                        aplicablesCount++;
                        const factor = period === 3 ? 2.0 : period === 2 ? 1.5 : 1.0;
                        sumaFactores += factor;
                    }
                });
                if (aplicablesCount > 0 && sumaFactores > 0) {
                    const areaDiscount = (maxDcto * sumaFactores) / aplicablesCount;
                    epRoadmapDiscountPercent += Math.min(areaDiscount, maxDcto);
                }
            });
        }

        // Totals for Summary Table
        let totalGrossCurrentEP = 0;
        let totalHrDiscountEP = 0;
        let totalNetCurrentEP = 0;

        // 2. Group contract items and details
        const groupedRows = selectedContractForDetail.items.map(contractItem => {
            const taskId = contractItem.id;
            const task = findTaskById(taskId, tasks);

            let itemTotalContractualQty = 0;
            let itemTotalUnitPrice = 0;
            let itemTotalValue = 0;

            let itemPrevQty = 0;
            let itemPrevTotal = 0;

            let itemCurrentQty = 0;
            let itemCurrentGrossTotal = 0;
            let itemCurrentHrDiscountTotal = 0;
            let itemCurrentNetTotal = 0;

            let itemAccQty = 0;
            let itemAccTotal = 0;

            const currentItem = selectedEPForView.items?.find(i => i.id === taskId);
            const selectedUnitIds = currentItem?.selectedUnitIds || [];

            const details = contractItem.details.map(detail => {
                const unitId = detail.unitId;
                const totalQty = parseFloat(detail.quantity || '0') || 0;
                const unitPrice = parseFloat(detail.unitPrice || '0') || 0;
                const totalValue = totalQty * unitPrice;

                // Previous EPs paid for this unit (net after previous discounts)
                let prevQty = 0;
                let prevTotal = 0;
                previousEPs.forEach(pEp => {
                    const pItem = pEp.items?.find(i => i.id === taskId);
                    if (pItem?.selectedUnitIds?.includes(unitId)) {
                        const pDetail = pItem?.details.find(d => d.unitId === unitId);
                        const q = parseFloat(pDetail?.quantity || '0') || 0;
                        prevQty += q;

                        // Calculate previous HR discount for this pEp if any
                        let pEpDiscountPercent = 0;
                        if (pEp.roadmapEvaluations) {
                            revisionAreas.forEach(area => {
                                const maxDcto = area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10;
                                let aplicablesCount = 0;
                                let sumaFactores = 0;
                                area.parameters.forEach(p => {
                                    const evalAns = pEp.roadmapEvaluations?.[p.id];
                                    const st = typeof evalAns === 'string' ? evalAns : evalAns?.status;
                                    const pd = typeof evalAns === 'object' ? evalAns?.observedPeriods || 1 : 1;
                                    if (st === 'cumple') aplicablesCount++;
                                    else if (st === 'no_cumple') {
                                        aplicablesCount++;
                                        sumaFactores += (pd === 3 ? 2.0 : pd === 2 ? 1.5 : 1.0);
                                    }
                                });
                                if (aplicablesCount > 0 && sumaFactores > 0) {
                                    pEpDiscountPercent += Math.min((maxDcto * sumaFactores) / aplicablesCount, maxDcto);
                                }
                            });
                        }
                        const pGross = q * unitPrice;
                        const pDcto = Math.round(pGross * (pEpDiscountPercent / 100));
                        prevTotal += Math.max(0, pGross - pDcto);
                    }
                });

                // Current EP for this unit
                let currentQty = 0;
                let currentGrossTotal = 0;
                let currentHrDiscountTotal = 0;
                let currentNetTotal = 0;

                if (selectedUnitIds.includes(unitId)) {
                    const currentDetail = currentItem?.details.find(d => d.unitId === unitId);
                    currentQty = parseFloat(currentDetail?.quantity || '0') || 0;
                    currentGrossTotal = currentQty * unitPrice;
                    currentHrDiscountTotal = Math.round(currentGrossTotal * (epRoadmapDiscountPercent / 100));
                    currentNetTotal = Math.max(0, currentGrossTotal - currentHrDiscountTotal);
                }

                const accQty = prevQty + currentQty;
                const accTotal = prevTotal + currentNetTotal;

                itemTotalContractualQty += totalQty;
                itemTotalUnitPrice += unitPrice;
                itemTotalValue += totalValue;

                itemPrevQty += prevQty;
                itemPrevTotal += prevTotal;

                itemCurrentQty += currentQty;
                itemCurrentGrossTotal += currentGrossTotal;
                itemCurrentHrDiscountTotal += currentHrDiscountTotal;
                itemCurrentNetTotal += currentNetTotal;

                itemAccQty += accQty;
                itemAccTotal += accTotal;

                const prevPercent = totalValue > 0 ? (prevTotal / totalValue) * 100 : 0;
                const currentPercent = totalValue > 0 ? (currentNetTotal / totalValue) * 100 : 0;
                const accPercent = totalValue > 0 ? (accTotal / totalValue) * 100 : 0;

                return {
                    unitId,
                    unitName: detail.unitName,
                    totalQty,
                    unitPrice,
                    totalValue,
                    acc: { qty: accQty, total: accTotal, percent: accPercent },
                    prev: { qty: prevQty, total: prevTotal, percent: prevPercent },
                    hrDiscount: { percent: currentQty > 0 ? epRoadmapDiscountPercent : 0, amount: currentHrDiscountTotal },
                    current: { qty: currentQty, grossTotal: currentGrossTotal, netTotal: currentNetTotal, percent: currentPercent }
                };
            });

            totalGrossCurrentEP += itemCurrentGrossTotal;
            totalHrDiscountEP += itemCurrentHrDiscountTotal;
            totalNetCurrentEP += itemCurrentNetTotal;

            const itemAccPercent = itemTotalValue > 0 ? (itemAccTotal / itemTotalValue) * 100 : 0;
            const itemPrevPercent = itemTotalValue > 0 ? (itemPrevTotal / itemTotalValue) * 100 : 0;
            const itemCurrentPercent = itemTotalValue > 0 ? (itemCurrentNetTotal / itemTotalValue) * 100 : 0;

            return {
                id: taskId,
                code: resolveItemGanttCode(taskId, contractItem.code),
                name: contractItem.description,
                unit: task?.unit || '-',
                totalContractualQty: itemTotalContractualQty,
                totalUnitPrice: itemTotalUnitPrice,
                totalValue: itemTotalValue,
                acc: { qty: itemAccQty, total: itemAccTotal, percent: itemAccPercent },
                prev: { qty: itemPrevQty, total: itemPrevTotal, percent: itemPrevPercent },
                hrDiscount: { percent: itemCurrentQty > 0 ? epRoadmapDiscountPercent : 0, amount: itemCurrentHrDiscountTotal },
                current: { qty: itemCurrentQty, grossTotal: itemCurrentGrossTotal, netTotal: itemCurrentNetTotal, percent: itemCurrentPercent },
                details
            };
        });

        // 3. Financial Calculations for Summary Box
        const retentionAmount = Math.round(totalNetCurrentEP * (retentionPercentageInView / 100));
        const netAmountToPay = Math.max(0, totalNetCurrentEP - retentionAmount);
        const ivaAmount = Math.round(netAmountToPay * 0.19);
        const totalToPay = netAmountToPay + ivaAmount;

        const toggleItem = (id: string) => {
            const next = new Set(expandedItemsInView);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            setExpandedItemsInView(next);
        };

        const handleKillModal = () => {
            setEpDetailModalOpen(false);
            setExpandedItemsInView(new Set());
        };

        const handlePrint = () => {
            const contentElement = document.getElementById('ep-caratula-content');
            if (!contentElement) return;

            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            document.body.appendChild(iframe);

            const iframeDoc = iframe.contentWindow?.document;
            if (!iframeDoc) return;

            iframeDoc.open();
            iframeDoc.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Caratula EP</title>
                    <script src="https://cdn.tailwindcss.com"></script>
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />
                    <style>
                        @page { margin: 0.8cm; }
                        body { 
                            background: white !important; 
                            color: black !important;
                            -webkit-print-color-adjust: exact !important; 
                            print-color-adjust: exact !important; 
                            font-family: system-ui, -apple-system, sans-serif;
                        }
                        .material-symbols-outlined {
                            font-family: 'Material Symbols Outlined' !important;
                            font-weight: normal;
                            font-style: normal;
                            font-size: 16px;
                            line-height: 1;
                            letter-spacing: normal;
                            text-transform: none;
                            display: inline-block;
                            white-space: nowrap;
                            word-wrap: normal;
                            direction: ltr;
                            -webkit-font-feature-settings: 'liga';
                            -webkit-font-smoothing: antialiased;
                        }
                        .print-hidden { display: none !important; }
                        .keep-together {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                        tr { page-break-inside: avoid !important; break-inside: avoid !important; }
                        input { border: none !important; padding: 0 !important; margin: 0 !important; width: auto !important; }
                    </style>
                </head>
                <body>
                    <div style="padding: 10px;">
                        ${contentElement.innerHTML}
                    </div>
                </body>
                </html>
            `);
            iframeDoc.close();

            const triggerPrint = () => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                }, 1000);
            };

            if (iframeDoc.fonts) {
                iframeDoc.fonts.ready.then(() => setTimeout(triggerPrint, 300));
            } else {
                setTimeout(triggerPrint, 1000);
            }
        };

        const isAdminUser = isPlatformAdmin || hasPermission('sc_ep_autorizar', 'edit') || hasPermission('sc_config', 'edit');
        const isEpClosed = selectedEPForView.epApprovalStatus === 'Aprobado' || (
            approvalFlows.paymentStatements.length > 0 &&
            approvalFlows.paymentStatements.every(st => Boolean(selectedEPForView.signatures?.[st.reviewerId]))
        );

        const cleanTaskName = (name: string) => {
            if (!name) return '';
            return name.replace(/^\[?\d+(\.\d+)*\]?\s*/, '').trim();
        };

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div id="ep-caratula-container" className="bg-white dark:bg-slate-900 shadow-2xl w-full max-w-7xl border border-slate-300 flex flex-col h-[95vh] overflow-hidden">
                    {/* Header bar */}
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 md:p-3 border-b border-slate-300 flex flex-wrap md:flex-nowrap justify-between items-center shrink-0 gap-2">
                        <div className="flex gap-2 md:gap-4 items-center w-full md:w-auto justify-between md:justify-start">
                            <div className="relative inline-block text-left">
                                <button
                                    onClick={() => setExportDropdownOpen(prev => !prev)}
                                    className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 text-xs md:text-sm cursor-pointer shadow-sm"
                                >
                                    <span className="material-symbols-outlined text-[16px] md:text-[20px]">picture_as_pdf</span>
                                    Exportar PDF
                                    <span className="material-symbols-outlined text-[16px]">arrow_drop_down</span>
                                </button>
                                {exportDropdownOpen && (
                                    <div className="absolute left-0 mt-1 w-52 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-[120] py-1 overflow-hidden animate-in fade-in duration-150">
                                        <button
                                            onClick={() => {
                                                setExportDropdownOpen(false);
                                                handlePrint();
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2 cursor-pointer transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[16px] text-blue-600">description</span>
                                            1. Descargar EP
                                        </button>
                                        <button
                                            onClick={() => {
                                                setExportDropdownOpen(false);
                                                handlePrintEPUndRoadmap();
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700 cursor-pointer transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[16px] text-emerald-600">assignment_turned_in</span>
                                            2. Descargar EP + HR
                                        </button>
                                    </div>
                                )}
                            </div>
                            {isAdminUser && isEpClosed && (
                                <button
                                    onClick={() => setConfirmReopenTarget(selectedEPForView.id)}
                                    className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white rounded font-bold text-xs md:text-sm transition-all cursor-pointer shadow-xs"
                                    title="Re-abrir este Estado de Pago cerrado para revisión"
                                >
                                    <span className="material-symbols-outlined text-[16px] md:text-[20px]">lock_open</span>
                                    Re-abrir EP
                                </button>
                            )}
                            <div className="text-[10px] md:text-xs text-slate-500 italic hidden sm:block">Los items desplegados aparecerán así en el PDF.</div>
                            <button onClick={handleKillModal} className="text-slate-500 hover:text-slate-800 md:hidden ml-auto">
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>
                        <button onClick={handleKillModal} className="text-slate-500 hover:text-slate-800 hidden md:block">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    <div id="ep-caratula-content" className="flex-1 overflow-auto p-3 md:p-6 bg-white text-slate-900">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
                            <div className="flex items-center gap-3 md:gap-4">
                                <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt="Constructora LYD S.A." className="h-9 md:h-12 max-w-[120px] md:max-w-xs object-contain shrink-0" />
                                <div>
                                    <div className="text-sm md:text-lg font-black text-slate-900 uppercase leading-tight">{companyConfig?.name || 'CONSTRUCTORA LYD S.A'}</div>
                                    <div className="text-[10px] text-slate-600 font-bold">RUT: {companyConfig?.rut || '96.528.140-1'}</div>
                                    <div className="text-[9px] md:text-[10px] text-slate-600 uppercase hidden sm:block">{companyConfig?.direccion || 'PALACIO RIESCO 4387, HUECHURABA, REGIÓN METROPOLITANA'}</div>
                                </div>
                            </div>
                            <div className="bg-blue-700 text-white p-1 px-3 md:p-1.5 md:px-4 font-bold text-[10px] md:text-xs uppercase tracking-wider rounded-sm shrink-0 w-full md:w-auto text-center">
                                CARATULA ESTADO DE PAGO
                            </div>
                        </div>

                        {/* Info Section */}
                        <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 mb-4 text-[11px] text-slate-800">
                            <div className="flex gap-2"><span className="font-bold w-28">N° ESTADO DE PAGO:</span> <span>{currentEpIndex + 1}</span></div>
                            <div className="flex gap-2"><span className="font-bold w-28">R.U.T:</span> <span>{sub?.rut}</span></div>
                            <div className="flex gap-2"><span className="font-bold w-28">RAZÓN SOCIAL:</span> <span>{sub?.razonSocial}</span></div>
                            <div className="flex gap-2"><span className="font-bold w-28">FECHA DE EMISIÓN:</span> <span>{selectedEPForView.endDate || 'N/A'}</span></div>
                        </div>

                        {/* Main Table with Hoja de Ruta Columns */}
                        <table className="w-full border-collapse border border-slate-400 text-[9px] mb-4">
                            <thead>
                                <tr className="bg-slate-100 font-bold">
                                    <th className="border border-slate-400 px-1 py-1 text-left min-w-[160px]" rowSpan={2}>Partidas / Detalle Unidades</th>
                                    <th className="border border-slate-400 px-1 py-1 w-14 text-center" rowSpan={2}>Nivel</th>
                                    <th className="border border-slate-400 px-1 py-1 w-10 text-center" rowSpan={2}>Unidad</th>
                                    <th className="border border-slate-400 px-1 py-1 w-14 text-center" rowSpan={2}>Cant. Contractual</th>
                                    <th className="border border-slate-400 px-1 py-1 w-16 text-right" rowSpan={2}>Val. Unitario</th>
                                    <th className="border border-slate-400 px-1 py-1 w-18 text-right" rowSpan={2}>Valor Total</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center" colSpan={3}>Avance Acumulado</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center" colSpan={3}>Avance Anterior</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center bg-rose-50 text-rose-800" colSpan={2}>Hoja de Ruta</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center bg-yellow-50" colSpan={3}>Avance Actual EP</th>
                                </tr>
                                <tr className="bg-slate-100 font-bold text-[8px]">
                                    <th className="border border-slate-400 px-1 py-0.5 text-center w-10">Cant.</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-center w-8">%</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-right w-16">Total $</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-center w-10">Cant.</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-center w-8">%</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-right w-16">Total $</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-center w-10 bg-rose-50 text-rose-800">% Dcto.</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-right w-16 bg-rose-50 text-rose-800">Monto Dcto.</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-center w-10 bg-yellow-50">Cant.</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-center w-8 bg-yellow-50">%</th>
                                    <th className="border border-slate-400 px-1 py-0.5 text-right w-16 bg-yellow-50">Total $</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedRows.map((row) => (
                                    <React.Fragment key={row.id}>
                                        {/* Parent Partida Row */}
                                        <tr className="bg-slate-50 cursor-pointer hover:bg-slate-100" onClick={() => toggleItem(row.id)}>
                                            <td className="border border-slate-400 px-1 py-1 font-bold">
                                                <div className="flex items-center gap-1">
                                                    <span className="material-symbols-outlined text-[13px] print-hidden">{expandedItemsInView.has(row.id) ? 'expand_less' : 'expand_more'}</span>
                                                    {cleanTaskName(row.name)}
                                                </div>
                                            </td>
                                            <td className="border border-slate-400 px-1 py-1 text-center">-</td>
                                            <td className="border border-slate-400 px-1 py-1 text-center">{row.unit}</td>
                                            <td className="border border-slate-400 px-1 py-1 text-center font-bold">{row.totalContractualQty.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1 text-right font-bold">{row.totalUnitPrice.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1 text-right font-bold font-mono">{row.totalValue.toLocaleString()}</td>

                                            {/* Avance Acumulado */}
                                            <td className="border border-slate-400 px-1 py-1 text-center font-bold">{row.acc.qty.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1 text-center">{truncateProgress(row.acc.percent)}%</td>
                                            <td className="border border-slate-400 px-1 py-1 text-right font-bold font-mono">{row.acc.total.toLocaleString()}</td>

                                            {/* Avance Anterior */}
                                            <td className="border border-slate-400 px-1 py-1 text-center font-bold">{row.prev.qty.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1 text-center">{truncateProgress(row.prev.percent)}%</td>
                                            <td className="border border-slate-400 px-1 py-1 text-right font-bold font-mono">{row.prev.total.toLocaleString()}</td>

                                            {/* Hoja de Ruta (Dcto) */}
                                            <td className="border border-slate-400 px-1 py-1 text-center font-bold bg-rose-50/70 text-rose-700">{row.hrDiscount.percent > 0 ? row.hrDiscount.percent.toFixed(2) + '%' : '0.00%'}</td>
                                            <td className="border border-slate-400 px-1 py-1 text-right font-bold font-mono bg-rose-50/70 text-rose-700">{row.hrDiscount.amount > 0 ? `-$${row.hrDiscount.amount.toLocaleString()}` : '$0'}</td>

                                            {/* Avance Actual EP (Neto) */}
                                            <td className="border border-slate-400 px-1 py-1 text-center font-bold bg-yellow-50">{row.current.qty.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1 text-center bg-yellow-50">{truncateProgress(row.current.percent)}%</td>
                                            <td className="border border-slate-400 px-1 py-1 text-right font-bold font-mono bg-yellow-50">{row.current.netTotal.toLocaleString()}</td>
                                        </tr>

                                        {/* Child Unit Rows */}
                                        {expandedItemsInView.has(row.id) && row.details.map((d, dIdx) => (
                                            <tr key={`${row.id}-${dIdx}`} className="text-slate-600 bg-white hover:bg-slate-50">
                                                <td className="border border-slate-400 px-1 py-0.5 pl-3 italic">{d.unitName}</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-center">{levels.find(l => l.id === units.find(u => u.id === d.unitId)?.levelId)?.name || "-"}</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-center italic opacity-70"> - </td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-center">{d.totalQty.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-right font-mono">{d.unitPrice.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-right font-mono italic opacity-70">{d.totalValue.toLocaleString()}</td>

                                                {/* Avance Acumulado */}
                                                <td className="border border-slate-400 px-1 py-0.5 text-center">{d.acc.qty.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-center">{truncateProgress(d.acc.percent)}%</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-right font-mono">{d.acc.total.toLocaleString()}</td>

                                                {/* Avance Anterior */}
                                                <td className="border border-slate-400 px-1 py-0.5 text-center">{d.prev.qty.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-center">{truncateProgress(d.prev.percent)}%</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-right font-mono">{d.prev.total.toLocaleString()}</td>

                                                {/* Hoja de Ruta */}
                                                <td className="border border-slate-400 px-1 py-0.5 text-center bg-rose-50/30 text-rose-700">{d.hrDiscount.percent > 0 ? d.hrDiscount.percent.toFixed(2) + '%' : '0.00%'}</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-right font-mono bg-rose-50/30 text-rose-700">{d.hrDiscount.amount > 0 ? `-$${d.hrDiscount.amount.toLocaleString()}` : '$0'}</td>

                                                {/* Avance Actual EP (Neto) */}
                                                <td className="border border-slate-400 px-1 py-0.5 text-center font-semibold bg-yellow-50/30">{d.current.qty.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-center bg-yellow-50/30 font-semibold">{truncateProgress(d.current.percent)}%</td>
                                                <td className="border border-slate-400 px-1 py-0.5 text-right font-bold font-mono bg-yellow-50/30">{d.current.netTotal.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>

                        {/* Payment Summary Table */}
                        <div className="flex justify-end pr-2 keep-together">
                            <table className="w-72 border-collapse border border-black text-[10px] text-slate-800">
                                <thead>
                                    <tr>
                                        <th colSpan={2} className="border border-black bg-slate-100 py-0.5 font-bold text-center uppercase text-[10px]">RESUMEN DE PAGO</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="border border-black px-2 py-1 font-bold bg-slate-50 uppercase">ESTADO DE PAGO (BRUTO)</td>
                                        <td className="border border-black px-2 py-1 text-right font-mono font-bold">${totalGrossCurrentEP.toLocaleString()}</td>
                                    </tr>
                                    {totalHrDiscountEP > 0 && (
                                        <tr>
                                            <td className="border border-black px-2 py-1 font-bold bg-rose-50 text-rose-700 uppercase">
                                                (-) DESCUENTO HOJA DE RUTA ({epRoadmapDiscountPercent.toFixed(2)}%)
                                            </td>
                                            <td className="border border-black px-2 py-1 text-right font-mono font-bold text-rose-700">-${totalHrDiscountEP.toLocaleString()}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td className="border border-black px-2 py-1 font-bold bg-slate-50 uppercase">SUBTOTAL LÍQUIDO APRECIADO</td>
                                        <td className="border border-black px-2 py-1 text-right font-mono font-bold">${totalNetCurrentEP.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black px-2 py-1 font-bold flex items-center justify-between uppercase">
                                            <span>% RETENCIÓN</span>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="number"
                                                    value={retentionPercentageInView}
                                                    onChange={(e) => setRetentionPercentageInView(parseFloat(e.target.value) || 0)}
                                                    className="w-10 border border-slate-300 px-1 text-right print-hidden"
                                                />
                                                <span className="hidden print:inline">{truncateProgress(retentionPercentageInView)}%</span>
                                            </div>
                                        </td>
                                        <td className="border border-black px-2 py-1 text-right font-mono">{truncateProgress(retentionPercentageInView)}%</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black px-2 py-1 font-bold bg-slate-50 uppercase">RETENCIÓN CONTRACTUAL</td>
                                        <td className="border border-black px-2 py-1 text-right font-mono text-red-600">-${retentionAmount.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black px-2 py-1 font-bold bg-slate-50 uppercase">ESTADO DE PAGO NETO</td>
                                        <td className="border border-black px-2 py-1 text-right font-mono font-bold">${netAmountToPay.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black px-2 py-1 font-bold bg-slate-50 uppercase">IVA (19%)</td>
                                        <td className="border border-black px-2 py-1 text-right font-mono">${ivaAmount.toLocaleString()}</td>
                                    </tr>
                                    <tr className="bg-slate-200">
                                        <td className="border border-black px-2 py-1 font-black uppercase text-xs">VALOR A PAGAR</td>
                                        <td className="border border-black px-2 py-1 text-right font-mono font-black text-xs">${totalToPay.toLocaleString()}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Flujo de Firmas y Aprobaciones del Estado de Pago */}
                        <div className="p-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl mt-4 keep-together">
                            {/* Banner de aviso si el EP fue rechazado previamente */}
                            {selectedEPForView.rejectionReason && (
                                <div className="mb-4 p-3 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-xl flex items-start gap-2.5 shadow-xs">
                                    <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-900/50 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                                        <span className="material-symbols-outlined text-[18px]">warning</span>
                                    </div>
                                    <div className="flex-1 text-left">
                                        <h5 className="text-[11px] font-extrabold text-rose-800 dark:text-rose-300 uppercase tracking-wider">
                                            Estado de Pago Rechazado por {selectedEPForView.rejectedByReviewerName || 'Aprobador'}
                                        </h5>
                                        <p className="text-xs text-rose-700 dark:text-rose-200 mt-1 font-medium bg-white/70 dark:bg-slate-900/70 p-2 rounded-lg border border-rose-200/80 dark:border-rose-800/60">
                                            "{selectedEPForView.rejectionReason}"
                                        </p>
                                    </div>
                                </div>
                            )}

                            <h4 className="text-[12px] font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[18px] text-blue-600">verified</span>
                                Aprobaciones y Firmas
                            </h4>

                            {(() => {
                                const isHrApproved = selectedEPForView.roadmapStatus === 'Aprobado';
                                const isSubmittedToApproval = selectedEPForView.epApprovalStatus === 'Pendiente' || selectedEPForView.epApprovalStatus === 'En proceso' || selectedEPForView.epApprovalStatus === 'Aprobado';
                                const canApproveOrReject = isHrApproved && isSubmittedToApproval;

                                return (
                                    <>
                                        <div className="flex flex-wrap md:flex-nowrap items-center justify-start md:justify-center gap-3 md:gap-6 overflow-x-auto py-2">
                                            {approvalFlows.paymentStatements.map((step, idx) => {
                                                const reviewer = responsibles.find(r => r.id === step.reviewerId);
                                                if (!reviewer) return null;

                                                const epAmount = parseFloat(selectedEPForView.amount.replace(/[^0-9.-]+/g, "")) || 0;
                                                const isRequired = !step.minAmount || epAmount > step.minAmount;
                                                
                                                const signature = selectedEPForView.signatures?.[reviewer.id];
                                                
                                                const isTheirTurn = isRequired && approvalFlows.paymentStatements.slice(0, idx).every(prevStep => {
                                                    const prevRequired = !prevStep.minAmount || epAmount > prevStep.minAmount;
                                                    return !prevRequired || Boolean(selectedEPForView.signatures?.[prevStep.reviewerId]);
                                                });

                                                return (
                                                    <React.Fragment key={idx}>
                                                        {idx > 0 && (
                                                            <div className="flex items-center justify-center shrink-0 text-slate-400 dark:text-slate-600">
                                                                <span className="material-symbols-outlined text-[20px]">arrow_circle_right</span>
                                                            </div>
                                                        )}
                                                        <div className={`flex flex-col items-center text-center p-2 min-w-[130px] rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xs relative ${!isRequired ? 'opacity-40' : ''}`}>
                                                            <div className="text-[10px] font-black uppercase tracking-wider text-blue-700 dark:text-blue-400 mb-0.5">
                                                                {idx === 0 ? 'Solicitante' : 'Aprobador'}
                                                            </div>
                                                            <div className="text-xs font-extrabold text-slate-900 dark:text-slate-100">
                                                                {reviewer.name}
                                                            </div>
                                                            <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-none">
                                                                {reviewer.role}
                                                            </div>
                                                            {step.minAmount && step.minAmount > 0 && (
                                                                <div className="text-[8px] text-slate-400 mt-0.5 font-mono">
                                                                    &gt; ${step.minAmount.toLocaleString()}
                                                                </div>
                                                            )}
                                                            
                                                            <div className="mt-2 min-h-[36px] flex flex-col items-center justify-center w-full">
                                                                {!isRequired ? (
                                                                    <span className="text-[10px] text-slate-400 italic">No Req.</span>
                                                                ) : signature ? (
                                                                    <div className="flex flex-col items-center gap-0.5 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-md border border-blue-200 dark:border-blue-800/60 w-full">
                                                                        {signature.startsWith('data:image') || signature.startsWith('http') ? (
                                                                            <img src={signature} alt="Firma" className="max-h-[26px] object-contain mix-blend-multiply dark:brightness-90" />
                                                                        ) : (
                                                                            <div className="flex items-center gap-1 text-[9px] font-extrabold text-blue-700 dark:text-blue-300">
                                                                                <span className="material-symbols-outlined text-[14px]">verified</span>
                                                                                <span>{signature}</span>
                                                                            </div>
                                                                        )}
                                                                        <span className="text-[8px] text-blue-700 dark:text-blue-300 font-black">Aprobó</span>
                                                                    </div>
                                                                ) : (isTheirTurn && canApproveOrReject) ? (
                                                                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                                                                        <button
                                                                            onClick={() => setConfirmApproveTarget({ epId: selectedEPForView.id, reviewerId: reviewer.id, reviewerName: reviewer.name })}
                                                                            className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-full text-[10px] font-extrabold shadow-xs transition-all flex items-center gap-0.5 cursor-pointer"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[13px]">check_circle</span>
                                                                            Aprobar
                                                                        </button>
                                                                        <button
                                                                            onClick={() => setConfirmRejectTarget({ epId: selectedEPForView.id, reviewerId: reviewer.id, reviewerName: reviewer.name, idx })}
                                                                            className="px-2 py-1 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white rounded-full text-[10px] font-extrabold shadow-xs transition-all flex items-center gap-0.5 cursor-pointer"
                                                                            title="Rechazar y devolver flujo"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[13px]">cancel</span>
                                                                            Rechazar
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold italic">Aun no aprueba</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                            {approvalFlows.paymentStatements.length === 0 && (
                                                <div className="col-span-full py-1 text-center text-xs text-slate-400 italic">
                                                    No se han configurado firmas.
                                                </div>
                                            )}
                                        </div>

                                        {/* Mensaje Informativo si NO se puede aprobar por Hoja de Ruta */}
                                        {!canApproveOrReject && (
                                            <div className="mt-3 p-2.5 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700/60 rounded-xl flex items-center justify-center gap-2 text-center text-xs font-bold text-amber-800 dark:text-amber-300">
                                                <span className="material-symbols-outlined text-[18px] text-amber-600">info</span>
                                                <span>
                                                    {!isHrApproved 
                                                        ? "No se puede aprobar porque aun no se ha aprobado la Hoja de Ruta." 
                                                        : "No se puede aprobar porque el Estado de Pago aún no ha sido enviado a Aprobación."}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // --- Mobile State & Helpers for Subcontratos (screens/05-sc-contratistas.html) ---
    const [mobileTab, setMobileTab] = useState<'empresas' | 'evaluacion'>('empresas');
    const [mobileSubSheetOpen, setMobileSubSheetOpen] = useState(false);
    const [mobileEvalSheetOpen, setMobileEvalSheetOpen] = useState(false);
    const [mobileStatusFilter, setMobileStatusFilter] = useState<'Todos' | 'Activo' | 'Inactivo'>('Todos');
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [mobileSearchQuery, setMobileSearchQuery] = useState('');
    const [mobileActionContract, setMobileActionContract] = useState<Contract | null>(null);

    const formatCLP = (amount: number): string => {
        return '$' + Math.round(amount || 0).toLocaleString('es-CL');
    };

    const formatContractDates = (startDate?: string, endDate?: string): string => {
        if (!startDate && !endDate) return 'Sin fecha';
        const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        const fmt = (dStr?: string) => {
            if (!dStr) return '';
            const parts = dStr.split('-');
            if (parts.length === 3) {
                const day = parseInt(parts[2], 10);
                const mIdx = parseInt(parts[1], 10) - 1;
                const year = parts[0];
                return `${day} ${months[mIdx] || ''} ${year}`.trim();
            }
            return dStr;
        };
        if (startDate && endDate) {
            return `${fmt(startDate)} → ${fmt(endDate)}`;
        }
        return fmt(startDate) || fmt(endDate) || '';
    };

    const filteredMobileContracts = useMemo(() => {
        return contracts.filter(c => {
            if (c.isDeleted) return false;
            if (mobileStatusFilter !== 'Todos') {
                if (mobileStatusFilter === 'Activo' && c.status !== 'Activo') return false;
                if (mobileStatusFilter === 'Inactivo' && c.status !== 'Inactivo') return false;
            }
            if (mobileSearchQuery.trim()) {
                const q = mobileSearchQuery.toLowerCase().trim();
                const sub = getSubcontractorById(c.subcontractorId);
                const name = (sub?.razonSocial || '').toLowerCase();
                const id = (c.id || '').toLowerCase();
                if (!name.includes(q) && !id.includes(q)) return false;
            }
            return true;
        });
    }, [contracts, mobileStatusFilter, mobileSearchQuery, subcontractors]);

    return (
        <div 
            className="flex flex-col h-full min-h-0 bg-[#F1F5F9] md:bg-background-light dark:md:bg-background-dark overflow-hidden"
        >
            {/* VISTA MÓVIL (block md:hidden) */}
            <PullToRefresh className="block md:hidden flex-1 pb-32">
                <MobileTitleBar
                    title="Empresas Contratistas"
                    projectName={projectInfo.name}
                    projectCode={projectInfo.code}
                />

                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Segmented control: Empresas Contratistas / Evaluación SC */}
                    <div style={{ display: 'flex', background: '#E2E8F0', borderRadius: '10px', padding: '3px', height: '36px', boxSizing: 'border-box' }}>
                        <button
                            ref={empresasBtnRef}
                            type="button"
                            onClick={() => {
                                setMobileTab('empresas');
                                setMobileSubSheetOpen(prev => !prev);
                                setMobileEvalSheetOpen(false);
                            }}
                            style={{
                                flex: 1,
                                border: 0,
                                borderRadius: '8px',
                                background: (mobileSubSheetOpen || (mobileTab === 'empresas' && !mobileEvalSheetOpen)) ? '#fff' : 'none',
                                fontSize: '13px',
                                fontWeight: (mobileSubSheetOpen || (mobileTab === 'empresas' && !mobileEvalSheetOpen)) ? 700 : 600,
                                color: (mobileSubSheetOpen || (mobileTab === 'empresas' && !mobileEvalSheetOpen)) ? '#1D4ED8' : '#475569',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '2px',
                                cursor: 'pointer',
                                transition: 'all 120ms ease'
                            }}
                        >
                            <span>Empresas Contratistas</span>
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>
                        <button
                            ref={evaluacionBtnRef}
                            type="button"
                            onClick={() => {
                                setMobileTab('evaluacion');
                                setMobileEvalSheetOpen(prev => !prev);
                                setMobileSubSheetOpen(false);
                            }}
                            style={{
                                flex: 1,
                                border: 0,
                                borderRadius: '8px',
                                background: (mobileEvalSheetOpen || (mobileTab === 'evaluacion' && !mobileSubSheetOpen)) ? '#fff' : 'none',
                                fontSize: '13px',
                                fontWeight: (mobileEvalSheetOpen || (mobileTab === 'evaluacion' && !mobileSubSheetOpen)) ? 700 : 600,
                                color: (mobileEvalSheetOpen || (mobileTab === 'evaluacion' && !mobileSubSheetOpen)) ? '#1D4ED8' : '#475569',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '2px',
                                cursor: 'pointer',
                                transition: 'all 120ms ease'
                            }}
                        >
                            <span>Evaluación SC</span>
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>
                    </div>

                    {/* Botón Añadir Contratista (azul sólido, 40px de alto, alineado a la izquierda) */}
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <button
                            type="button"
                            onClick={handleOpenAddStatement}
                            style={{
                                height: '40px',
                                padding: '0 14px',
                                borderRadius: '10px',
                                background: 'var(--kl-primary, #2563EB)',
                                color: '#ffffff',
                                border: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: '0 1px 3px rgba(37,99,235,0.2)'
                            }}
                        >
                            <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            <span>Añadir Contratista</span>
                        </button>
                    </div>

                    {/* Filter chips & Search */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {(['Todos', 'Activo', 'Inactivo'] as const).map(chip => {
                            const isChipActive = mobileStatusFilter === chip;
                            return (
                                <button
                                    key={chip}
                                    type="button"
                                    onClick={() => setMobileStatusFilter(chip)}
                                    style={{
                                        height: '30px',
                                        padding: '0 12px',
                                        borderRadius: '15px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        border: `1px solid ${isChipActive ? '#2563EB' : '#CBD5E1'}`,
                                        background: isChipActive ? '#2563EB' : '#fff',
                                        color: isChipActive ? '#fff' : '#475569',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {chip}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => setMobileSearchOpen(prev => !prev)}
                            className="ib"
                            aria-label="Buscar"
                            style={{
                                marginLeft: 'auto',
                                width: '36px',
                                height: '30px',
                                border: '1px solid #CBD5E1',
                                borderRadius: '8px',
                                background: mobileSearchOpen ? '#EFF6FF' : '#fff',
                                color: mobileSearchOpen ? '#2563EB' : '#475569',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                            }}
                        >
                            <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                                <circle cx="11" cy="11" r="7"/>
                                <path d="M21 21l-4.3-4.3"/>
                            </svg>
                        </button>
                    </div>

                    {/* Search Input when open */}
                    {mobileSearchOpen && (
                        <div style={{ height: '38px', border: '1px solid #BFDBFE', borderRadius: '10px', background: '#fff', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px' }}>
                            <span className="material-symbols-outlined text-[18px] text-slate-400">search</span>
                            <input
                                type="text"
                                placeholder="Buscar por contratista o ID..."
                                value={mobileSearchQuery}
                                onChange={(e) => setMobileSearchQuery(e.target.value)}
                                style={{ border: 0, background: 'transparent', outline: 'none', width: '100%', fontSize: '13px', color: '#0F172A' }}
                                autoFocus
                            />
                            {mobileSearchQuery && (
                                <button type="button" onClick={() => setMobileSearchQuery('')} style={{ border: 0, background: 'none', color: '#94A3B8', padding: 0 }}>
                                    <span className="material-symbols-outlined text-[16px]">close</span>
                                </button>
                            )}
                        </div>
                    )}

                    {/* Cards List */}
                    {filteredMobileContracts.length === 0 ? (
                        <div style={{ background: '#fff', borderRadius: '14px', padding: '32px 16px', textAlign: 'center', color: '#64748B', fontSize: '13px', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                            No hay empresas contratistas disponibles con los filtros actuales.
                        </div>
                    ) : (
                        filteredMobileContracts.map(contract => {
                            const subName = getSubcontractorById(contract.subcontractorId)?.razonSocial || 'Desconocido';
                            const isSelected = selectedContractId === contract.id;
                            const isVoided = contract.status === 'Anulado';

                            const activeAnexosTotal = (contract.anexos || [])
                                .filter(a => a.status === 'Activo')
                                .reduce((sum, a) => sum + (a.totalAmount || 0), 0);
                            const contractAmount = (contract.totalAmount || 0) + activeAnexosTotal;
                            const relatedEPs = paymentStatements.filter(ep => ep.contractId === contract.id && ep.status !== 'Anulado');
                            const totalPaid = relatedEPs.reduce((sum, ep) => sum + (parseInt(ep.amount.replace(/\D/g, ''), 10) || 0), 0);
                            const paidPct = contractAmount > 0 ? Math.min(100, Math.round((totalPaid / contractAmount) * 100)) : 0;

                            const statusBg = contract.status === 'Activo' ? '#DCFCE7' : contract.status === 'Inactivo' ? '#FEF3C7' : contract.status === 'Anulado' ? '#FEE2E2' : '#EFF6FF';
                            const statusColor = contract.status === 'Activo' ? '#166534' : contract.status === 'Inactivo' ? '#B45309' : contract.status === 'Anulado' ? '#B91C1C' : '#1D4ED8';

                            return (
                                <div
                                    key={contract.id}
                                    onClick={() => setSelectedContractId(contract.id)}
                                    style={{
                                        border: isSelected ? '2px solid #2563EB' : '1px solid #E2E8F0',
                                        background: isSelected ? '#F5F9FF' : '#fff',
                                        borderRadius: '14px',
                                        padding: '12px',
                                        boxSizing: 'border-box',
                                        boxShadow: '0 1px 3px rgba(0,0,0,.08)',
                                        opacity: isVoided ? 0.6 : 1,
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                        <b style={{ flex: 1, fontSize: '15px', lineHeight: 1.25, color: '#0F172A', textDecoration: isVoided ? 'line-through' : 'none' }}>
                                            {subName}
                                        </b>
                                        <span style={{ background: statusBg, color: statusColor, fontSize: '10px', fontWeight: 700, padding: '3px 7px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                                            {contract.status.toUpperCase()}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedContractId(contract.id);
                                                setMobileActionContract(contract);
                                            }}
                                            className="ib"
                                            aria-label="Más acciones"
                                            data-sc-menu-btn="true"
                                            style={{ width: '28px', height: '28px', margin: '-4px -4px 0 0', border: 0, background: 'none', color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                        >
                                            <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill: 'currentColor' }}>
                                                <circle cx="12" cy="5" r="1.5"/>
                                                <circle cx="12" cy="12" r="1.5"/>
                                                <circle cx="12" cy="19" r="1.5"/>
                                            </svg>
                                        </button>
                                    </div>

                                    <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
                                        ctr-{contract.id} · {formatContractDates(contract.startDate, contract.endDate)}
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginTop: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '10px', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>
                                                Contrato
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: 700, marginTop: '2px', color: '#0F172A' }}>
                                                {formatCLP(contractAmount)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '10px', color: '#1D4ED8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>
                                                Pagado
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: 700, marginTop: '2px', color: '#1D4ED8' }}>
                                                {formatCLP(totalPaid)}
                                            </div>
                                        </div>
                                        <span style={{ background: '#F1F5F9', color: '#475569', fontSize: '11px', fontWeight: 600, padding: '4px 8px', borderRadius: '8px', whiteSpace: 'nowrap' }}>
                                            {relatedEPs.length} EPs
                                        </span>
                                    </div>

                                    <div style={{ height: '4px', borderRadius: '2px', background: '#E2E8F0', overflow: 'hidden', marginTop: '8px' }}>
                                        <div style={{ width: `${paidPct}%`, height: '100%', background: '#2563EB', borderRadius: '2px' }} />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Espaciador inferior para no tapar el final de la lista */}
                <div className="kl-list-end" />
            </PullToRefresh>

            {/* VISTA ESCRITORIO (hidden md:flex) */}
            <div className="hidden md:flex flex-col flex-1 h-full overflow-hidden relative">
                {/* Header / Toolbar */}
                <div className="bg-[#1a73e8] dark:bg-blue-900 text-white h-14 px-4 md:px-6 flex items-center justify-between shrink-0 shadow-md z-30">
                <div className="flex items-center gap-6">

                    {/* Menu Item */}
                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                            className={`flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded transition-colors ${menuOpen ? 'bg-white/20' : 'hover:bg-white/10'}`}
                        >
                            <span className="material-symbols-outlined text-[18px]">handshake</span>
                            Empresas Contratistas
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>

                        {menuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}></div>
                                <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in duration-100">
                                    {hasPermission('sc_config', 'edit') && (
                                        <button
                                            onClick={() => { 
                                                setMenuOpen(false); 
                                                setEditingSubcontractorId(null);
                                                setNewSubcontractor({
                                                    rut: '',
                                                    razonSocial: '',
                                                    giro: '',
                                                    representante: '',
                                                    telefono: '',
                                                    correo: '',
                                                    subcontractTypeId: '',
                                                    fono: '',
                                                    ciudad: '',
                                                    comuna: '',
                                                    status: 'Activo',
                                                    linkedUserIds: []
                                                });
                                                setCreateMasterSubModalOpen(true); 
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                        >
                                            <span className="material-symbols-outlined text-[18px] text-slate-400">add_business</span>
                                            Crear Empresa Contratista
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { setMenuOpen(false); setListMasterSubModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-slate-400">list_alt</span>
                                        Lista de Empresas Contratistas
                                    </button>
                                    <button
                                        onClick={() => { setMenuOpen(false); setApprovalConfigModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-slate-400">fact_check</span>
                                        Configuración Aprobaciones
                                    </button>
                                    <button
                                        onClick={() => { setMenuOpen(false); setRoadmapConfigModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-slate-400">route</span>
                                        Hoja de Ruta
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Evaluación SC Dropdown Menu */}
                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setEvaluationMenuOpen(!evaluationMenuOpen); }}
                            className={`flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded transition-colors ${evaluationMenuOpen ? 'bg-white/20' : 'hover:bg-white/10'}`}
                        >
                            <span className="material-symbols-outlined text-[18px]">rate_review</span>
                            Evaluación SC
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>

                        {evaluationMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setEvaluationMenuOpen(false)}></div>
                                <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in duration-100">
                                    <button
                                        onClick={() => { 
                                            setEvaluationMenuOpen(false); 
                                            setEvaluationConfigModalOpen(true);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 font-medium cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-blue-600 dark:text-blue-400">tune</span>
                                        Conf. Evaluación
                                    </button>
                                    <button
                                        onClick={() => { 
                                            setEvaluationMenuOpen(false); 
                                            setEvaluationModalOpen(true);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 font-medium cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-emerald-600 dark:text-emerald-400">rate_review</span>
                                        Evaluación SC
                                    </button>
                                    <button
                                        onClick={() => { 
                                            setEvaluationMenuOpen(false); 
                                            setEvaluationModalOpen(true);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 font-medium cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-purple-600 dark:text-purple-400">history</span>
                                        Historial Evaluaciones
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                
                {/* Right side: Project Name (Desktop) OR Widgets (Mobile) */}
                <div className="flex items-center gap-2">
                    <div className="text-xs text-blue-100 hidden md:block truncate mr-2">Proyecto Activo: {projectInfo.name}</div>
                    
                    {/* Mobile triggers in the blue header bar */}
                    <div className="flex md:hidden items-center gap-1.5 shrink-0">
                        <button 
                            onClick={() => window.dispatchEvent(new CustomEvent('open-bug-report'))}
                            className="w-10 h-10 bg-rose-600 border border-white/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                            title="Reportar Falla"
                        >
                            <span className="material-symbols-outlined text-[20px]">bug_report</span>
                        </button>
                        <button 
                            onClick={() => window.dispatchEvent(new CustomEvent('open-ai-assistant'))}
                            className="w-10 h-10 bg-white/20 border border-white/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95 backdrop-blur-sm"
                            title="Asistente IA"
                        >
                            <svg viewBox="0 0 100 100" className="w-[20px] h-[20px]" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M25 50 C25 25, 75 25, 75 50" stroke="currentColor" strokeWidth="6" strokeLinecap="round" fill="none" />
                                <rect x="18" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                <rect x="74" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                <circle cx="40" cy="50" r="4.5" fill="currentColor" />
                                <circle cx="60" cy="50" r="4.5" fill="currentColor" />
                                <path d="M42 63 C45 67, 55 67, 58 63" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
                                <path d="M78 56 C78 72, 68 82, 52 82" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" fill="none" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div 
                className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-6 p-4 md:p-6 lg:p-8 w-full max-w-[1600px] mx-auto pb-[calc(5rem+env(safe-area-inset-bottom))]"
                style={{ 
                    WebkitOverflowScrolling: 'touch'
                }}
            >

                {/* Header Section */}
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h2 className="text-lg md:text-3xl font-bold text-slate-900 dark:text-white">Empresas Contratistas</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Gestión de contratos y estados de pago de subcontratos.</p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                        <button
                            onClick={handleOpenAddStatement}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white font-bold text-sm hover:bg-blue-600 shadow-sm transition-colors"
                        >
                            <span className="material-symbols-outlined text-[20px]">add</span>
                            Añadir Contratista
                        </button>
                        <button
                            onClick={() => handleEditStatement()}
                            disabled={!selectedContractId || selectedContract?.status === 'Anulado'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">edit</span>
                            Editar
                        </button>

                        <div className="h-6 w-px bg-slate-300 dark:bg-slate-600 mx-1"></div>

                        <button
                            onClick={() => {
                                if (confirm("¿Inactivar este Subcontrato?")) {
                                    setContracts(prev => prev.map(p => p.id === selectedContractId ? { ...p, status: 'Inactivo' } : p));
                                }
                            }}
                            disabled={!selectedContractId || selectedContract?.status === 'Inactivo' || selectedContract?.status === 'Anulado'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-amber-600 font-medium text-sm hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">block</span>
                            Inactivar
                        </button>
                        
                        <button
                            onClick={() => {
                                const reason = prompt("Ingrese el motivo de la anulación:");
                                if (reason !== null) {
                                    if (reason.trim() === '') {
                                        alert("Debe ingresar un motivo para anular el subcontrato.");
                                        return;
                                    }
                                    setContracts(prev => prev.map(p => p.id === selectedContractId ? { ...p, status: 'Anulado', notes: (p.notes ? p.notes + '\n' : '') + `Anulado: ${reason}` } : p));
                                    setSelectedContractId(null);
                                }
                            }}
                            disabled={!selectedContractId || selectedContract?.status === 'Anulado'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-red-600 font-medium text-sm hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">cancel</span>
                            Anular
                        </button>

                        <div className="h-6 w-px bg-slate-300 dark:bg-slate-600 mx-1"></div>
                        
                        <button
                            onClick={() => {
                                const contractToActivate = contracts.find(c => c.id === selectedContractId);
                                const isValid = contractToActivate && contractToActivate.subcontractorId && contractToActivate.items && contractToActivate.items.length > 0;
                                // Nota: Para una validación estricta podríamos buscar el rut en la lista de subcontractors, pero asumimos que si se pudo crear tiene id e items.
                                if (!isValid) {
                                    alert("El contrato no tiene todos los campos completos para ser activado.");
                                    return;
                                }
                                if (confirm("¿Activar este Subcontrato?")) {
                                    setContracts(prev => prev.map(p => p.id === selectedContractId ? { ...p, status: 'Activo' } : p));
                                }
                            }}
                            disabled={!selectedContractId || selectedContract?.status === 'Activo'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-green-600 font-medium text-sm hover:bg-green-50 dark:hover:bg-green-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">check_circle</span>
                            Activar
                        </button>

                        {isPlatformAdmin && (
                            <button
                                onClick={() => {
                                    if (confirm("¿Eliminar este Subcontrato de forma permanente?")) {
                                        setContracts(prev => prev.map(p => p.id === selectedContractId ? { ...p, isDeleted: true } : p));
                                        setSelectedContractId(null);
                                    }
                                }}
                                disabled={!selectedContractId}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-red-600 font-medium text-sm hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-[20px]">delete</span>
                                Eliminar
                            </button>
                        )}

                        {(isPlatformAdmin || hasPermission('sc_generar_ep', 'edit') || hasPermission('movil_sc_generar_ep', 'edit')) && (
                            <button
                                onClick={handleGenerateEPClick}
                                disabled={!selectedContractId || selectedContract?.status === 'Anulado'}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-primary font-medium text-sm hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-[20px]">description</span>
                                Generar EP
                            </button>
                        )}

                        <button
                            onClick={() => setAnexosListModalOpen(true)}
                            disabled={!selectedContractId || selectedContract?.status === 'Anulado'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">attachment</span>
                            Anexo
                        </button>

                        <button
                            onClick={() => setViewEPModalOpen(true)}
                            disabled={!selectedContractId || (!isPlatformAdmin && !hasPermission('sc_ver_ep', 'view') && !hasPermission('movil_sc_ver_ep', 'view'))}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-medium text-sm transition-colors ${!selectedContractId || (!isPlatformAdmin && !hasPermission('sc_ver_ep', 'view') && !hasPermission('movil_sc_ver_ep', 'view')) ? 'text-slate-400 opacity-50 cursor-not-allowed' : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10'}`}
                        >
                            <span className="material-symbols-outlined text-[20px]">history</span>
                            Ver EP
                        </button>
                    </div>
                </div>

                {/* Table List of Contracts */}
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-visible md:overflow-hidden">
                    <table className="w-full text-left border-collapse hidden md:table">
                        <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                            <tr>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Empresa Contratista</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Vigencia</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">EP</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase text-right">Monto Contrato</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase text-right">Total Pagado</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase text-center">Estado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {contracts.filter(c => !c.isDeleted).length === 0 ? (
                                <tr><td colSpan={6} className="p-8 text-center text-slate-500 dark:text-slate-400 italic font-medium">Crear Subcontratos y Cargar los Subcontratos.</td></tr>
                            ) : (
                                contracts.filter(c => !c.isDeleted).map(contract => {
                                    const subName = getSubcontractorById(contract.subcontractorId)?.razonSocial || 'Desconocido';
                                    const isSelected = selectedContractId === contract.id;
                                    const isVoided = contract.status === 'Anulado';

                                    // Calculate Amounts
                                    const activeAnexosTotal = (contract.anexos || [])
                                        .filter(a => a.status === 'Activo')
                                        .reduce((sum, a) => sum + (a.totalAmount || 0), 0);
                                    const contractAmount = (contract.totalAmount || 0) + activeAnexosTotal;
                                    // Calculate paid amount from EPs related to this contract (excluding Drafts and Voided)
                                    const relatedEPs = paymentStatements.filter(ep => ep.contractId === contract.id && ep.status !== 'Borrador' && ep.status !== 'Anulado');
                                    const totalPaid = relatedEPs.reduce((sum, ep) => {
                                        // Simple parsing of "100.000 CLP" -> 100000
                                        const numericAmount = parseInt(ep.amount.replace(/\D/g, ''), 10) || 0;
                                        return sum + numericAmount;
                                    }, 0);

                                    return (
                                        <tr
                                            key={contract.id}
                                            onClick={() => setSelectedContractId(contract.id)}
                                            className={`
                                                cursor-pointer transition-colors
                                                ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}
                                                ${isVoided ? 'opacity-60 grayscale' : ''}
                                            `}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className={`font-bold text-sm ${isSelected ? 'text-primary' : isVoided ? 'text-slate-500 line-through' : 'text-slate-800 dark:text-slate-200'}`}>{subName}</span>
                                                    <span className="text-xs text-slate-400">ID: {contract.id}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                                                {contract.startDate && contract.endDate ? (
                                                    <span className="font-mono text-xs">{contract.startDate} <span className="text-slate-400 mx-1">➜</span> {contract.endDate}</span>
                                                ) : (
                                                    <span className="text-slate-400 italic">Sin fecha</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">{relatedEPs.length}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right font-mono font-medium text-slate-700 dark:text-slate-300 text-sm">
                                                ${contractAmount.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right font-mono font-bold text-slate-900 dark:text-white text-sm">
                                                ${totalPaid.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`
                                                    px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide
                                                    ${contract.status === 'Borrador' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                                                        contract.status === 'Anulado' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                                        contract.status === 'Activo' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                                            contract.status === 'Inactivo' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                                                'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}
                                                `}>
                                                    {contract.status}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    {/* VISTA MÓVIL: Tarjetas */}
                    <div className="md:hidden flex flex-col p-4 gap-3 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 pb-28">
                        {contracts.filter(c => !c.isDeleted).length === 0 ? (
                            <div className="p-8 text-center text-slate-500 dark:text-slate-400 italic font-medium">Crear Subcontratos y Cargar los Subcontratos.</div>
                        ) : (
                            contracts.filter(c => !c.isDeleted).map(contract => {
                                const subName = getSubcontractorById(contract.subcontractorId)?.razonSocial || 'Desconocido';
                                const isSelected = selectedContractId === contract.id;
                                const isVoided = contract.status === 'Anulado';
                                
                                const activeAnexosTotal = (contract.anexos || [])
                                    .filter(a => a.status === 'Activo')
                                    .reduce((sum, a) => sum + (a.totalAmount || 0), 0);
                                const contractAmount = (contract.totalAmount || 0) + activeAnexosTotal;
                                const relatedEPs = paymentStatements.filter(ep => ep.contractId === contract.id);
                                const totalPaid = relatedEPs.reduce((sum, ep) => sum + (parseInt(ep.amount.replace(/\D/g, ''), 10) || 0), 0);

                                return (
                                    <div
                                        key={contract.id}
                                        onClick={() => setSelectedContractId(contract.id)}
                                        className={`rounded-xl border ${isSelected ? 'border-primary ring-2 ring-primary/20 shadow-md' : 'border-slate-200'} p-4 flex flex-col gap-3 transition-all ${isVoided ? 'opacity-60 bg-slate-100' : 'bg-white'}`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex flex-col">
                                                <h4 className={`font-bold text-base ${isVoided ? 'line-through text-slate-500' : 'text-slate-800'}`}>{subName}</h4>
                                                <span className="text-xs text-slate-400">ID: {contract.id}</span>
                                            </div>
                                            <span className={`
                                                px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider
                                                ${contract.status === 'Borrador' ? 'bg-blue-100 text-blue-700' :
                                                    contract.status === 'Anulado' ? 'bg-red-100 text-red-700' :
                                                        contract.status === 'Activo' ? 'bg-green-100 text-green-700' :
                                                            contract.status === 'Inactivo' ? 'bg-orange-100 text-orange-700' :
                                                                'bg-slate-100 text-slate-700'}
                                            `}>
                                                {contract.status}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-sm mt-1">
                                            <div className="flex flex-col bg-slate-50 p-2 rounded">
                                                <span className="text-[10px] text-slate-500 font-bold uppercase">Monto Contrato</span>
                                                <span className="font-mono font-medium">${contractAmount.toLocaleString()}</span>
                                            </div>
                                            <div className="flex flex-col bg-slate-50 p-2 rounded">
                                                <span className="text-[10px] text-slate-500 font-bold uppercase">Total Pagado</span>
                                                <span className="font-mono font-bold text-primary">${totalPaid.toLocaleString()}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                                            <div className="text-xs text-slate-500">
                                                {contract.startDate && contract.endDate ? (
                                                    <span className="font-mono">{contract.startDate} ➜ {contract.endDate}</span>
                                                ) : 'Sin fecha'}
                                            </div>
                                            <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded text-xs font-bold text-slate-600">
                                                <span className="material-symbols-outlined text-[14px]">receipt_long</span>
                                                {relatedEPs.length} EPs
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
            </div>

            {/* Menú de Acciones Móvil (Hoja Inferior / Bottom Sheet con los 9 Botones) */}
            {mobileActionContract && (() => {
                const subName = getSubcontractorById(mobileActionContract.subcontractorId)?.razonSocial || 'Empresa Contratista';
                const isVoided = mobileActionContract.status === 'Anulado';
                const isInactive = mobileActionContract.status === 'Inactivo';
                const isActive = mobileActionContract.status === 'Activo';

                return (
                    <div 
                        className="fixed inset-0 z-[120] flex flex-col justify-end bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
                        onClick={() => setMobileActionContract(null)}
                    >
                        <div 
                            className="bg-white dark:bg-slate-900 rounded-t-2xl shadow-2xl p-4 max-h-[85vh] overflow-y-auto flex flex-col gap-2.5 animate-in slide-in-from-bottom duration-200"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                borderTop: '1px solid #E2E8F0',
                                paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))'
                            }}
                        >
                            {/* Drag handle */}
                            <div style={{ width: '36px', height: '4px', background: '#CBD5E1', borderRadius: '2px', margin: '0 auto 4px' }} />

                            {/* Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '8px', borderBottom: '1px solid #F1F5F9' }}>
                                <div style={{ minWidth: 0, flex: 1, paddingRight: '8px' }}>
                                    <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#0F172A', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {subName}
                                    </h3>
                                    <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
                                        ID: {mobileActionContract.id} · Estado: {mobileActionContract.status}
                                    </div>
                                </div>
                                <button 
                                    type="button" 
                                    onClick={() => setMobileActionContract(null)}
                                    style={{ border: 0, background: '#F1F5F9', color: '#475569', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                                    aria-label="Cerrar menú"
                                >
                                    <span className="material-symbols-outlined text-[18px]">close</span>
                                </button>
                            </div>

                            {/* Los 9 Botones de Acción */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {/* 1. Añadir Contratista */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMobileActionContract(null);
                                        handleOpenAddStatement();
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: '#F0F9FF', color: '#0369A1', fontSize: '13px', fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}
                                >
                                    <span className="material-symbols-outlined text-[20px] text-sky-600">add_business</span>
                                    <span>Añadir Contratista</span>
                                </button>

                                {/* 2. Editar */}
                                <button
                                    type="button"
                                    disabled={isVoided}
                                    onClick={() => {
                                        setMobileActionContract(null);
                                        handleEditStatement();
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: 'none', color: isVoided ? '#94A3B8' : '#0F172A', fontSize: '13px', fontWeight: 600, cursor: isVoided ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: isVoided ? 0.5 : 1 }}
                                >
                                    <span className="material-symbols-outlined text-[20px] text-slate-500">edit</span>
                                    <span>Editar</span>
                                </button>

                                {/* 3. Inactivar */}
                                <button
                                    type="button"
                                    disabled={isInactive || isVoided}
                                    onClick={() => {
                                        setMobileContractDialog({
                                            open: true,
                                            type: 'inactivar',
                                            contractId: mobileActionContract.id,
                                            title: 'Inactivar Subcontrato',
                                            message: '¿Seguro que deseas inactivar este Subcontrato?',
                                            confirmText: 'Inactivar',
                                            cancelText: 'Cancelar'
                                        });
                                        setMobileActionContract(null);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: 'none', color: (isInactive || isVoided) ? '#94A3B8' : '#D97706', fontSize: '13px', fontWeight: 600, cursor: (isInactive || isVoided) ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: (isInactive || isVoided) ? 0.5 : 1 }}
                                >
                                    <span className="material-symbols-outlined text-[20px]">block</span>
                                    <span>Inactivar</span>
                                </button>

                                {/* 4. Anular */}
                                <button
                                    type="button"
                                    disabled={isVoided}
                                    onClick={() => {
                                        setVoidReasonText('');
                                        setMobileContractDialog({
                                            open: true,
                                            type: 'anular',
                                            contractId: mobileActionContract.id,
                                            title: 'Anular Subcontrato',
                                            message: 'Ingresa el motivo de la anulación para registrarlo en el historial:',
                                            danger: true,
                                            confirmText: 'Anular',
                                            cancelText: 'Cancelar'
                                        });
                                        setMobileActionContract(null);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: 'none', color: isVoided ? '#94A3B8' : '#DC2626', fontSize: '13px', fontWeight: 600, cursor: isVoided ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: isVoided ? 0.5 : 1 }}
                                >
                                    <span className="material-symbols-outlined text-[20px]">cancel</span>
                                    <span>Anular</span>
                                </button>

                                {/* 5. Activar */}
                                <button
                                    type="button"
                                    disabled={isActive}
                                    onClick={() => {
                                        const isValid = mobileActionContract.subcontractorId && mobileActionContract.items && mobileActionContract.items.length > 0;
                                        if (!isValid) {
                                            setMobileContractDialog({
                                                open: true,
                                                type: 'alerta',
                                                title: 'Subcontrato Incompleto',
                                                message: 'El contrato no tiene todos los campos completos para ser activado.',
                                                cancelText: null,
                                                confirmText: 'Entendido'
                                            });
                                            setMobileActionContract(null);
                                            return;
                                        }
                                        setMobileContractDialog({
                                            open: true,
                                            type: 'activar',
                                            contractId: mobileActionContract.id,
                                            title: 'Activar Subcontrato',
                                            message: '¿Deseas activar este Subcontrato para su uso en obra?',
                                            confirmText: 'Activar',
                                            cancelText: 'Cancelar'
                                        });
                                        setMobileActionContract(null);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: 'none', color: isActive ? '#94A3B8' : '#16A34A', fontSize: '13px', fontWeight: 600, cursor: isActive ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: isActive ? 0.5 : 1 }}
                                >
                                    <span className="material-symbols-outlined text-[20px]">check_circle</span>
                                    <span>Activar</span>
                                </button>

                                {/* 6. Eliminar (Admin) */}
                                {isPlatformAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMobileContractDialog({
                                                open: true,
                                                type: 'eliminar',
                                                contractId: mobileActionContract.id,
                                                title: 'Eliminar Subcontrato',
                                                message: '¿Eliminar este Subcontrato de forma permanente? Esta acción no se puede deshacer.',
                                                danger: true,
                                                confirmText: 'Eliminar',
                                                cancelText: 'Cancelar'
                                            });
                                            setMobileActionContract(null);
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: 'none', color: '#DC2626', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
                                    >
                                        <span className="material-symbols-outlined text-[20px]">delete</span>
                                        <span>Eliminar</span>
                                    </button>
                                )}

                                {/* 7. Generar EP */}
                                {(isPlatformAdmin || hasPermission('sc_generar_ep', 'edit') || hasPermission('movil_sc_generar_ep', 'edit')) && (
                                    <button
                                        type="button"
                                        disabled={isVoided}
                                        onClick={() => {
                                            setMobileActionContract(null);
                                            handleGenerateEPClick();
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: 'none', color: isVoided ? '#94A3B8' : '#2563EB', fontSize: '13px', fontWeight: 600, cursor: isVoided ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: isVoided ? 0.5 : 1 }}
                                    >
                                        <span className="material-symbols-outlined text-[20px]">description</span>
                                        <span>Generar EP</span>
                                    </button>
                                )}

                                {/* 8. Anexo */}
                                <button
                                    type="button"
                                    disabled={isVoided}
                                    onClick={() => {
                                        setMobileActionContract(null);
                                        setAnexosListModalOpen(true);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: 'none', color: isVoided ? '#94A3B8' : '#475569', fontSize: '13px', fontWeight: 600, cursor: isVoided ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: isVoided ? 0.5 : 1 }}
                                >
                                    <span className="material-symbols-outlined text-[20px]">attachment</span>
                                    <span>Anexo</span>
                                </button>

                                {/* 9. Ver EP */}
                                <button
                                    type="button"
                                    disabled={!isPlatformAdmin && !hasPermission('sc_ver_ep', 'view') && !hasPermission('movil_sc_ver_ep', 'view')}
                                    onClick={() => {
                                        setMobileActionContract(null);
                                        setViewEPModalOpen(true);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 0, background: 'none', color: '#1D4ED8', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
                                >
                                    <span className="material-symbols-outlined text-[20px]">history</span>
                                    <span>Ver EP</span>
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Menú Superior: Empresas Contratistas (KlMenu anclado al botón) */}
            <KlMenu
                open={mobileSubSheetOpen}
                onClose={() => setMobileSubSheetOpen(false)}
                anchorRef={empresasBtnRef}
                items={[
                    ...(hasPermission('sc_config', 'edit') ? [{
                        id: 'crear-empresa',
                        label: 'Crear Empresa Contratista',
                        iconName: 'add_business',
                        iconColor: 'text-primary',
                        onClick: () => {
                            setEditingSubcontractorId(null);
                            setNewSubcontractor({
                                rut: '',
                                razonSocial: '',
                                giro: '',
                                representante: '',
                                telefono: '',
                                correo: '',
                                subcontractTypeId: '',
                                fono: '',
                                ciudad: '',
                                comuna: '',
                                status: 'Activo',
                                linkedUserIds: []
                            });
                            setCreateMasterSubModalOpen(true);
                        }
                    }] : []),
                    {
                        id: 'lista-empresas',
                        label: 'Lista de Empresas Contratistas',
                        iconName: 'list_alt',
                        iconColor: 'text-slate-600 dark:text-slate-300',
                        onClick: () => setListMasterSubModalOpen(true)
                    },
                    {
                        id: 'config-aprobaciones',
                        label: 'Configuración Aprobaciones',
                        iconName: 'fact_check',
                        iconColor: 'text-slate-600 dark:text-slate-300',
                        onClick: () => setApprovalConfigModalOpen(true)
                    },
                    {
                        id: 'hoja-ruta',
                        label: 'Hoja de Ruta',
                        iconName: 'route',
                        iconColor: 'text-slate-600 dark:text-slate-300',
                        onClick: () => setRoadmapConfigModalOpen(true)
                    }
                ]}
            />

            {/* Menú Superior: Evaluación SC (KlMenu anclado al botón) */}
            <KlMenu
                open={mobileEvalSheetOpen}
                onClose={() => setMobileEvalSheetOpen(false)}
                anchorRef={evaluacionBtnRef}
                items={[
                    {
                        id: 'conf-evaluacion',
                        label: 'Conf. Evaluación',
                        iconName: 'tune',
                        iconColor: 'text-blue-600 dark:text-blue-400',
                        onClick: () => setEvaluationConfigModalOpen(true)
                    },
                    {
                        id: 'evaluacion-sc',
                        label: 'Evaluación SC',
                        iconName: 'rate_review',
                        iconColor: 'text-emerald-600 dark:text-emerald-400',
                        onClick: () => setEvaluationModalOpen(true)
                    },
                    {
                        id: 'historial-evaluaciones',
                        label: 'Historial Evaluaciones',
                        iconName: 'history',
                        iconColor: 'text-purple-600 dark:text-purple-400',
                        onClick: () => setEvaluationModalOpen(true)
                    }
                ]}
            />

            {/* --- MODALS --- */}

            {/* --- CONFIGURACIÓN HOJA DE RUTA (CHECKLIST GLOBAL) --- */}
            {roadmapConfigModalOpen && (
                isMobile ? (
                    <MobileRoadmapConfigModal
                        open={roadmapConfigModalOpen}
                        onClose={() => setRoadmapConfigModalOpen(false)}
                        revisionAreas={revisionAreas}
                        setRevisionAreas={setRevisionAreas}
                    />
                ) : (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-5 text-white flex items-center justify-between shadow-md shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                    <span className="material-symbols-outlined text-white text-[24px]">route</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold uppercase tracking-wider text-white">Configuración Hoja de Ruta</h3>
                                    <p className="text-xs text-blue-100 font-normal">Checklist global de áreas y parámetros de evaluación para Estados de Pago</p>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleOpenCreateAreaModal}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-white text-blue-700 hover:bg-blue-50 font-bold text-xs rounded-xl shadow-md transition-all active:scale-95"
                                >
                                    <span className="material-symbols-outlined text-[18px]">add</span>
                                    + Agregar Área de Evaluación
                                </button>
                                <button
                                    onClick={() => setRoadmapConfigModalOpen(false)}
                                    className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>
                        </div>

                        {/* Body: Lista vertical de Áreas con Acordeón desplegable (con scroll completo) */}
                        <div className="p-6 pb-20 overflow-y-auto flex-1 min-h-0 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-4">
                            {revisionAreas.length === 0 ? (
                                <div className="text-center py-16 bg-white dark:bg-slate-800/40 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 p-8">
                                    <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600 mb-3">playlist_add_check</span>
                                    <h4 className="text-base font-bold text-slate-700 dark:text-slate-300">No hay áreas de evaluación configuradas</h4>
                                    <p className="text-xs text-slate-500 max-w-md mx-auto mt-1 mb-4">Crea áreas como Prevención, Calidad o Medio Ambiente y asigna parámetros de control.</p>
                                    <button
                                        onClick={handleOpenCreateAreaModal}
                                        className="px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl shadow-sm hover:bg-blue-600 transition-colors inline-flex items-center gap-1.5"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">add</span>
                                        Agregar Área de Evaluación
                                    </button>
                                </div>
                            ) : (
                                revisionAreas.map((area, areaIdx) => {
                                    const isExpanded = expandedAreaIds.has(area.id);
                                    return (
                                        <div
                                            key={area.id}
                                            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all flex flex-col shrink-0 overflow-hidden"
                                        >
                                            {/* Header Área (Clickable para Expandir/Contraer) */}
                                            <div
                                                onClick={() => toggleAreaExpand(area.id)}
                                                className="px-5 py-3.5 bg-slate-100/80 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3 cursor-pointer select-none hover:bg-slate-200/70 dark:hover:bg-slate-700 transition-colors"
                                            >
                                                <div className="flex items-center gap-2.5 flex-wrap">
                                                    <span className="material-symbols-outlined text-slate-500 text-[20px] transition-transform duration-200 shrink-0">
                                                        {isExpanded ? 'expand_less' : 'expand_more'}
                                                    </span>
                                                    <span className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                                                        {areaIdx + 1}
                                                    </span>
                                                    <h4 className="font-bold text-slate-800 dark:text-white text-sm">
                                                        {area.title}
                                                    </h4>
                                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 shrink-0">
                                                        {area.parameters.length} parámetros
                                                    </span>

                                                    {/* Descuento Máximo (Informativo Destacado) */}
                                                    <div
                                                        className="inline-flex items-center gap-1.5 px-3 py-0.5 bg-amber-500/10 dark:bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40 rounded-full font-semibold text-[11px] shadow-sm shrink-0 select-none"
                                                        title="Descuento Máximo configurado para esta área"
                                                    >
                                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                                                        <span className="text-[10px] uppercase tracking-wide font-bold text-amber-800 dark:text-amber-300">Dcto. máx.:</span>
                                                        <span className="font-mono font-extrabold text-xs bg-amber-500 text-white px-2 py-0.5 rounded-md shadow-xs">
                                                            {area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10}%
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => handleAddParameter(area.id)}
                                                        className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-xs font-bold rounded-lg border border-blue-200 dark:border-blue-800 transition-colors"
                                                        title="Agregar parámetro de revisión"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">add</span>
                                                        <span className="hidden sm:inline">Parámetro</span>
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleOpenEditAreaModal(area, e)}
                                                        className="flex items-center gap-1 px-2.5 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 text-xs font-bold rounded-lg border border-slate-200 dark:border-slate-600 transition-colors"
                                                        title="Editar nombre y Descuento Máximo del área"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">edit</span>
                                                        <span className="hidden sm:inline">Editar</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteArea(area.id)}
                                                        className="flex items-center gap-1 px-2.5 py-1 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-xs font-bold rounded-lg border border-rose-200 dark:border-rose-900/30 transition-colors"
                                                        title="Eliminar área"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">delete</span>
                                                        <span className="hidden sm:inline">Eliminar</span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Body Parámetros (Desplegable) */}
                                            {isExpanded && (
                                                <div className="p-4 flex flex-col gap-2 animate-in fade-in duration-150">
                                                    {area.parameters.length === 0 ? (
                                                        <div className="p-4 text-center text-xs text-slate-400 italic bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                                                            No hay parámetros en esta área. Presiona el botón <span className="font-bold text-blue-600">+ Parámetro</span> para agregar uno.
                                                        </div>
                                                    ) : (
                                                        area.parameters.map((param, pIdx) => (
                                                            <div
                                                                key={param.id}
                                                                className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900/40 hover:bg-slate-100/70 dark:hover:bg-slate-900/70 rounded-xl border border-slate-100 dark:border-slate-800 transition-colors group"
                                                            >
                                                                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                                                    <span className="material-symbols-outlined text-slate-400 text-[18px] shrink-0">check_circle_outline</span>
                                                                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">
                                                                        <span className="font-mono text-slate-400 text-[11px] mr-1">{areaIdx + 1}.{pIdx + 1}</span>
                                                                        {param.name}
                                                                    </span>
                                                                </div>

                                                                <div className="flex items-center gap-1 shrink-0 opacity-90 group-hover:opacity-100">
                                                                    <button
                                                                        onClick={() => handleEditParameter(area.id, param.id)}
                                                                        className="p-1 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors"
                                                                        title="Editar parámetro"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px]">edit</span>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteParameter(area.id, param.id)}
                                                                        className="p-1 text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors"
                                                                        title="Eliminar parámetro"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center shrink-0">
                            <span className="text-xs text-slate-500">
                                Total: <strong className="text-slate-800 dark:text-slate-200">{revisionAreas.length}</strong> áreas de evaluación, <strong className="text-slate-800 dark:text-slate-200">{revisionAreas.reduce((acc, a) => acc + a.parameters.length, 0)}</strong> parámetros.
                            </span>
                            <button
                                onClick={() => setRoadmapConfigModalOpen(false)}
                                className="px-6 py-2 bg-primary hover:bg-blue-600 text-white font-bold text-xs rounded-xl shadow-md transition-colors"
                            >
                                Listo / Guardar
                            </button>
                        </div>
                    </div>
                </div>
                )
            )}

            {/* --- MODAL PARA EDITAR / CREAR ÁREA DE EVALUACIÓN (NOMBRE Y DCTO. MÁX.) --- */}
            {editingAreaModal && (
                <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="bg-slate-900 p-4 text-white flex justify-between items-center border-b border-slate-800">
                            <h4 className="font-bold text-sm uppercase tracking-wider text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-400 text-[20px]">edit_note</span>
                                {editingAreaModal.id ? 'Editar Área de Evaluación' : 'Nueva Área de Evaluación'}
                            </h4>
                            <button
                                onClick={() => setEditingAreaModal(null)}
                                className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors"
                            >
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        {/* Form Body */}
                        <div className="p-6 flex flex-col gap-4 bg-slate-50 dark:bg-slate-900/50">
                            <div>
                                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase mb-1.5">
                                    Nombre / Título del Área
                                </label>
                                <input
                                    type="text"
                                    value={editingAreaModal.title}
                                    onChange={(e) => setEditingAreaModal(prev => prev ? { ...prev, title: e.target.value } : null)}
                                    placeholder="Ej: Prevención de Riesgos"
                                    className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-sm"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase mb-1.5">
                                    Descuento Máximo (%)
                                </label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        value={editingAreaModal.maxDiscountPercent}
                                        onChange={(e) => setEditingAreaModal(prev => prev ? { ...prev, maxDiscountPercent: Number(e.target.value) } : null)}
                                        placeholder="10"
                                        className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-mono font-bold text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-sm pr-8"
                                    />
                                    <span className="absolute right-3.5 top-2.5 text-xs font-bold text-slate-400 pointer-events-none">
                                        %
                                    </span>
                                </div>
                                <p className="text-[11px] text-slate-400 mt-1">
                                    Porcentaje máximo permitido de penalización/descuento aplicable a esta área.
                                </p>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2.5">
                            <button
                                onClick={() => setEditingAreaModal(null)}
                                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 text-slate-700 dark:text-slate-200 font-bold text-xs rounded-xl transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveAreaModal}
                                disabled={!editingAreaModal.title.trim()}
                                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5"
                            >
                                <span className="material-symbols-outlined text-[16px]">check</span>
                                Guardar Cambios
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- TAREA 2: REDISEÑO Y MEJORA DE LA INTERFAZ "VER EP" (TABLA DE ESTADOS DE PAGO) --- */}
            {viewEPModalOpen && selectedContract && (
                isMobile ? (
                    <MobileViewEPListSheet
                        open={viewEPModalOpen}
                        onClose={() => setViewEPModalOpen(false)}
                        contract={selectedContract}
                        subcontractor={getSubcontractorById(selectedContract.subcontractorId)}
                        paymentStatements={paymentStatements}
                        onSelectEP={(ep) => setSelectedEPForCaratula(ep)}
                    />
                ) : (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[96vw] xl:max-w-[1400px] max-h-[90vh] border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-5 text-white flex justify-between items-center shadow-md">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                    <span className="material-symbols-outlined text-white text-[24px]">history</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                                        ESTADOS DE PAGO — {getSubcontractorById(selectedContract.subcontractorId)?.razonSocial || 'CONTRATISTA'}
                                    </h3>
                                    <p className="text-xs text-blue-100">
                                        Historial y evaluación de Hojas de Ruta para el Contrato ID: {selectedContract.id}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setViewEPModalOpen(false)}
                                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                            >
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>

                        {/* Content: Tabla estructurada */}
                        <div className="p-6 overflow-y-auto bg-slate-50 dark:bg-slate-900/50 flex-1">
                            {(() => {
                                const relatedEPs = paymentStatements.filter(ep => ep.contractId === selectedContract.id);

                                if (relatedEPs.length === 0) {
                                    return (
                                        <div className="text-center py-16 bg-white dark:bg-slate-800/40 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700">
                                            <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600 mb-2">folder_off</span>
                                            <h4 className="text-base font-bold text-slate-700 dark:text-slate-300">No se han generado Estados de Pago</h4>
                                            <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">Este subcontrato aún no posee registros de cobro o EPs preliminares.</p>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-slate-100/90 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                                        <th className="px-3.5 py-3.5 whitespace-nowrap"># Estado de Pago</th>
                                                        <th className="px-3.5 py-3.5 whitespace-nowrap">Fecha EP</th>
                                                        <th className="px-3.5 py-3.5 text-right whitespace-nowrap">Monto Preliminar</th>
                                                        <th className="px-3.5 py-3.5 text-center whitespace-nowrap">Hoja de Ruta</th>
                                                        <th className="px-3.5 py-3.5 text-center whitespace-nowrap">Estado HR</th>
                                                        <th className="px-3.5 py-3.5 text-center whitespace-nowrap bg-rose-50/60 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300">% Dcto</th>
                                                        <th className="px-3.5 py-3.5 text-right whitespace-nowrap">Monto Aprobado HR</th>
                                                        <th className="px-3.5 py-3.5 text-center whitespace-nowrap">Acción</th>
                                                        <th className="px-3.5 py-3.5 text-center whitespace-nowrap">Estado EP</th>
                                                        <th className="px-3.5 py-3.5 text-center whitespace-nowrap">Ver EP</th>
                                                        <th className="px-3.5 py-3.5 text-center whitespace-nowrap">Editar</th>
                                                        <th className="px-3.5 py-3.5 text-center whitespace-nowrap">Anular</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60 text-xs">
                                                    {relatedEPs.map((ep, index) => {
                                                        const isVoided = ep.status === 'Anulado';
                                                        const epCode = `Estado de Pago #${index + 1}`;
                                                        const epDate = ep.endDate || ep.startDate || ep.period || 'Sin fecha';
                                                        
                                                        const rawAmount = ep.preliminaryAmount !== undefined
                                                            ? ep.preliminaryAmount
                                                            : (parseInt((ep.amount || '').replace(/\D/g, ''), 10) || 0);
                                                        const prelimFormatted = `$${rawAmount.toLocaleString('es-CL')}`;

                                                        const hrStatus = ep.roadmapStatus || 'Pendiente';
                                                        const isHrCompleted = hrStatus === 'Aprobado';
                                                        const currentEpApprovalStatus = ep.epApprovalStatus || 'En Espera';

                                                        // Calculate % Dcto HR for this EP
                                                        let epDiscountPercent = 0;
                                                        if (ep.roadmapEvaluations) {
                                                            revisionAreas.forEach(area => {
                                                                const maxDcto = area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10;
                                                                let aplicablesCount = 0;
                                                                let sumaFactores = 0;
                                                                area.parameters.forEach(p => {
                                                                    const evalAns = ep.roadmapEvaluations?.[p.id];
                                                                    const status = typeof evalAns === 'string' ? evalAns : evalAns?.status;
                                                                    const period = typeof evalAns === 'object' ? evalAns?.observedPeriods || 1 : 1;
                                                                    if (status === 'cumple') aplicablesCount++;
                                                                    else if (status === 'no_cumple') {
                                                                        aplicablesCount++;
                                                                        sumaFactores += (period === 3 ? 2.0 : period === 2 ? 1.5 : 1.0);
                                                                    }
                                                                });
                                                                if (aplicablesCount > 0 && sumaFactores > 0) {
                                                                    epDiscountPercent += Math.min((maxDcto * sumaFactores) / aplicablesCount, maxDcto);
                                                                }
                                                            });
                                                        }

                                                        const hrDiscountAmount = Math.round(rawAmount * (epDiscountPercent / 100));
                                                        const calculatedApprovedHrAmount = Math.max(0, rawAmount - hrDiscountAmount);

                                                        const approvedHrAmount = ep.roadmapApprovedAmount !== undefined && ep.roadmapApprovedAmount > 0
                                                            ? ep.roadmapApprovedAmount
                                                            : calculatedApprovedHrAmount;

                                                        // Conditions for action buttons:
                                                        const canSendToApproval = isHrCompleted && (currentEpApprovalStatus === 'En Espera' || currentEpApprovalStatus === 'Rechazado') && !isVoided;
                                                        const sendToApprovalTooltip = (!canSendToApproval && currentEpApprovalStatus !== 'En Espera' && currentEpApprovalStatus !== 'Rechazado')
                                                            ? "Ya enviado a Aprobación"
                                                            : "Completar HR";

                                                        const canEditEP = (currentEpApprovalStatus === 'En Espera' || currentEpApprovalStatus === 'Rechazado') && !isVoided;

                                                        return (
                                                            <tr
                                                                key={ep.id}
                                                                className={`
                                                                    transition-colors
                                                                    ${isVoided ? 'bg-slate-50 dark:bg-slate-900/30 opacity-60' : 'hover:bg-blue-50/50 dark:hover:bg-slate-700/40'}
                                                                `}
                                                            >
                                                                {/* # Estado de Pago */}
                                                                <td className="px-3.5 py-4 font-bold font-mono text-slate-800 dark:text-slate-200 whitespace-nowrap">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={`w-2 h-2 rounded-full ${isVoided ? 'bg-rose-400' : 'bg-blue-600'}`}></span>
                                                                        <span>{epCode}</span>
                                                                    </div>
                                                                </td>

                                                                {/* Fecha EP */}
                                                                <td className="px-3.5 py-4 text-slate-600 dark:text-slate-300 font-mono whitespace-nowrap">
                                                                    {epDate}
                                                                </td>

                                                                {/* Monto Preliminar */}
                                                                <td className="px-3.5 py-4 text-right font-mono font-medium text-slate-800 dark:text-slate-200 whitespace-nowrap">
                                                                    {prelimFormatted}
                                                                </td>

                                                                {/* Hoja de Ruta (Botón Evaluar / Ver HR) */}
                                                                <td className="px-3.5 py-4 text-center whitespace-nowrap">
                                                                    <button
                                                                        onClick={() => handleOpenRoadmapEvaluation(ep)}
                                                                        disabled={isVoided}
                                                                        className={`
                                                                            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs shadow-xs transition-all
                                                                            ${isVoided 
                                                                                ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
                                                                                : currentEpApprovalStatus === 'En Espera' || currentEpApprovalStatus === 'Rechazado'
                                                                                    ? 'bg-blue-600 hover:bg-blue-700 text-white active:scale-95 cursor-pointer'
                                                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 cursor-pointer'}
                                                                        `}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px]">
                                                                            {currentEpApprovalStatus === 'En Espera' || currentEpApprovalStatus === 'Rechazado' ? 'assignment_turned_in' : 'visibility'}
                                                                        </span>
                                                                        {currentEpApprovalStatus === 'En Espera' || currentEpApprovalStatus === 'Rechazado' ? 'Evaluar HR' : 'Ver HR'}
                                                                    </button>
                                                                </td>

                                                                {/* Estado Hoja de Ruta */}
                                                                <td className="px-3.5 py-4 whitespace-nowrap">
                                                                    <div className="flex justify-center">
                                                                        {hrStatus === 'Pendiente' && (
                                                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700/50 flex items-center gap-1.5">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                                                                                Pendiente
                                                                            </span>
                                                                        )}
                                                                        {hrStatus === 'En Proceso' && (
                                                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800 border border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700/50 flex items-center gap-1.5">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                                                En Proceso
                                                                            </span>
                                                                        )}
                                                                        {hrStatus === 'Aprobado' && (
                                                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700/50 flex items-center gap-1.5">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                                                Aprobado
                                                                            </span>
                                                                        )}
                                                                        {hrStatus === 'Rechazado' && (
                                                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-300 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700/50 flex items-center gap-1.5">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                                                                                Rechazada
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* % Dcto */}
                                                                <td className="px-3.5 py-4 text-center font-mono font-bold text-rose-600 dark:text-rose-400 bg-rose-50/30 dark:bg-rose-900/10 whitespace-nowrap">
                                                                    {epDiscountPercent > 0 ? `${epDiscountPercent.toFixed(2)}%` : '0.00%'}
                                                                </td>

                                                                {/* Monto Aprobado HR */}
                                                                <td className="px-3.5 py-4 text-right font-mono font-bold text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                                                                    ${approvedHrAmount.toLocaleString('es-CL')}
                                                                </td>

                                                                {/* Acción (Botón Enviar a Aprobación) */}
                                                                <td className="px-3.5 py-4 text-center whitespace-nowrap">
                                                                    <button
                                                                        onClick={() => handleSendEpToApproval(ep.id)}
                                                                        disabled={!canSendToApproval}
                                                                        title={!canSendToApproval ? sendToApprovalTooltip : "Enviar Estado de Pago a Aprobación"}
                                                                        className={`
                                                                            inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl font-bold text-xs shadow-xs transition-all
                                                                            ${canSendToApproval
                                                                                ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer active:scale-95'
                                                                                : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 cursor-not-allowed opacity-60'}
                                                                        `}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[15px]">send</span>
                                                                        Enviar a Aprobación
                                                                    </button>
                                                                </td>

                                                                {/* Estado EP */}
                                                                <td className="px-3.5 py-4 whitespace-nowrap">
                                                                    <div className="flex justify-center">
                                                                        {(() => {
                                                                            if (isVoided) {
                                                                                return (
                                                                                    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-200 text-slate-600 border border-slate-300 dark:bg-slate-800 dark:text-slate-400">
                                                                                        Anulado
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            if (currentEpApprovalStatus === 'En Espera') {
                                                                                return (
                                                                                    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 flex items-center gap-1.5">
                                                                                        <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span>
                                                                                        En Espera
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            if (currentEpApprovalStatus === 'Pendiente') {
                                                                                return (
                                                                                    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 flex items-center gap-1.5">
                                                                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                                                                                        Pendiente
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            if (currentEpApprovalStatus === 'En proceso') {
                                                                                return (
                                                                                    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800 border border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 flex items-center gap-1.5">
                                                                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                                                        En proceso
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            if (currentEpApprovalStatus === 'Rechazado') {
                                                                                return (
                                                                                    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-300 dark:bg-rose-900/40 dark:text-rose-300 flex items-center gap-1.5">
                                                                                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                                                                                        Rechazado
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            return (
                                                                                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 flex items-center gap-1.5">
                                                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                                                    Aprobado
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                </td>

                                                                {/* Ver EP (Carátula Oficial) */}
                                                                <td className="px-3.5 py-4 text-center whitespace-nowrap">
                                                                    <button
                                                                        onClick={() => {
                                                                            const contract = contracts.find(c => c.id === ep.contractId);
                                                                            setExpandedItemsInView(new Set());
                                                                            setRetentionPercentageInView(contract?.retention !== undefined ? contract.retention : 5);
                                                                            setSelectedEPForView(ep);
                                                                            setEpDetailModalOpen(true);
                                                                        }}
                                                                        disabled={isVoided}
                                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 dark:bg-slate-700/60 hover:bg-blue-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 hover:text-blue-700 dark:hover:text-blue-300 rounded-xl font-bold text-xs border border-slate-200 dark:border-slate-600 transition-colors shadow-xs cursor-pointer"
                                                                        title="Ver Carátula de Estado de Pago"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[15px] text-blue-600 dark:text-blue-400">description</span>
                                                                        Ver EP
                                                                    </button>
                                                                </td>

                                                                {/* Editar EP */}
                                                                <td className="px-3.5 py-4 text-center whitespace-nowrap">
                                                                    <button
                                                                        onClick={() => handleOpenEpForView(ep)}
                                                                        disabled={!canEditEP}
                                                                        title={!canEditEP ? "No se puede editar un EP enviado a aprobación o cerrado" : "Editar Estado de Pago"}
                                                                        className={`
                                                                            inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl font-bold text-xs shadow-xs transition-colors
                                                                            ${canEditEP 
                                                                                ? 'bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700/60 cursor-pointer' 
                                                                                : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 border border-slate-200 dark:border-slate-700 cursor-not-allowed opacity-50'}
                                                                        `}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[15px]">edit</span>
                                                                        Editar
                                                                    </button>
                                                                </td>

                                                                {/* Anular EP */}
                                                                <td className="px-3.5 py-4 text-center whitespace-nowrap">
                                                                    {isVoided ? (
                                                                        <span className="text-[11px] font-bold text-rose-500 italic bg-rose-50 dark:bg-rose-900/20 px-2 py-0.5 rounded-md">
                                                                            Anulado
                                                                        </span>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => handleCancelPaymentStatement(ep.id)}
                                                                            className={`
                                                                                inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl font-bold text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30 border border-rose-200 dark:border-rose-900/40 transition-colors shadow-xs cursor-pointer
                                                                                ${!hasPermission("sc_ep_eliminar", "edit") ? "opacity-50 pointer-events-none" : ""}
                                                                            `}
                                                                            title="Anular Estado de Pago"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[15px]">block</span>
                                                                            Anular
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                            <button
                                onClick={() => setViewEPModalOpen(false)}
                                className="px-5 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 text-slate-700 dark:text-slate-200 font-bold text-xs rounded-xl transition-colors"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
                )
            )}

            {/* --- MODAL OFICIAL: CARATULA ESTADO DE PAGO (MATCHING USER SCREENSHOT) --- */}
            {selectedEPForCaratula && (() => {
                const ep = selectedEPForCaratula;
                const contract = contracts.find(c => c.id === ep.contractId);
                const sub = getSubcontractorById(ep.subcontractorId);

                // EPs no anulados de este contrato
                const contractEPs = paymentStatements.filter(p => p.contractId === ep.contractId && p.status !== 'Anulado');
                const epIndex = contractEPs.findIndex(p => p.id === ep.id);
                const epNumber = (epIndex >= 0 ? epIndex : 0) + 1;
                
                // EPs anteriores a este
                const previousEPs = epIndex > 0 ? contractEPs.slice(0, epIndex) : [];
                const epDate = ep.endDate || ep.startDate || ep.period || new Date().toISOString().split('T')[0];

                return (
                    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-7xl max-h-[96vh] border border-slate-300 dark:border-slate-700 overflow-hidden flex flex-col font-sans">
                            
                            {/* Top Control Bar */}
                            <div className="bg-slate-100 dark:bg-slate-800 px-6 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between shrink-0">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => window.print()}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-md shadow-xs transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">print</span>
                                        Exportar PDF
                                    </button>
                                    <span className="text-xs italic text-slate-500 dark:text-slate-400">
                                        Los ítems desplegados aparecerán así en el PDF.
                                    </span>
                                </div>
                                <button
                                    onClick={() => setSelectedEPForCaratula(null)}
                                    className="text-slate-400 hover:text-slate-700 dark:hover:text-white p-1 rounded-lg transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[24px]">close</span>
                                </button>
                            </div>

                            {/* Printable Carátula Body */}
                            <div className="p-6 md:p-8 overflow-y-auto bg-white dark:bg-slate-900 flex-1 flex flex-col gap-6">
                                
                                {/* Company Header & Badge */}
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
                                    <div className="flex items-center gap-4">
                                        {/* Logo Constructora LYD Block */}
                                        <div className="flex items-center gap-2 shrink-0 select-none">
                                            <div className="text-blue-600 dark:text-blue-500 font-black text-4xl tracking-tighter leading-none font-sans">
                                                LD
                                            </div>
                                            <div className="flex flex-col text-[7.5px] font-black text-blue-600 dark:text-blue-500 uppercase leading-[8.5px] tracking-widest border-l-2 border-blue-600 dark:border-blue-500 pl-1.5 font-sans">
                                                <span>CONS</span>
                                                <span>TRUC</span>
                                                <span>TORA</span>
                                            </div>
                                        </div>
                                        <div>
                                            <h2 className="text-base font-extrabold text-slate-900 dark:text-white tracking-wide uppercase">
                                                CONSTRUCTORA LYD S.A
                                            </h2>
                                            <div className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-tight">
                                                <div><strong className="font-semibold text-slate-700 dark:text-slate-300">RUT:</strong> 96.528.140-1</div>
                                                <div>PALACIO RIESCO 4387, HUECHURABA, REGIÓN METROPOLITANA</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Blue Badge */}
                                    <div className="bg-blue-600 text-white px-6 py-2.5 rounded-md text-xs font-black uppercase tracking-wider shadow-xs text-center shrink-0">
                                        CARATULA ESTADO DE PAGO
                                    </div>
                                </div>

                                {/* Metadata Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8 text-xs py-2">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-900 dark:text-white uppercase">N° ESTADO DE PAGO:</span>
                                        <span className="font-mono text-slate-800 dark:text-slate-200 font-bold">{epNumber}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-900 dark:text-white uppercase">R.U.T.:</span>
                                        <span className="font-mono text-slate-800 dark:text-slate-200 font-semibold">{sub?.rut || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-900 dark:text-white uppercase">RAZÓN SOCIAL:</span>
                                        <span className="text-slate-800 dark:text-slate-200 font-bold uppercase">{sub?.razonSocial || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-900 dark:text-white uppercase">FECHA DE EMISIÓN:</span>
                                        <span className="font-mono text-slate-800 dark:text-slate-200">{epDate}</span>
                                    </div>
                                </div>

                                {/* Detailed Partidas & Units Table */}
                                <div className="overflow-x-auto border border-slate-300 dark:border-slate-700 rounded-md">
                                    <table className="w-full text-left border-collapse text-[11px] font-sans">
                                        <thead>
                                            {/* Row 1 Header */}
                                            <tr className="bg-slate-100 dark:bg-slate-800 border-b border-slate-300 dark:border-slate-700 font-bold text-slate-800 dark:text-slate-200">
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 w-14 text-center" rowSpan={2}>Cod</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-3 py-2 min-w-[200px]" rowSpan={2}>Partidas / Detalle Unidades</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 w-20 text-center" rowSpan={2}>Nivel</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 w-16 text-center" rowSpan={2}>Unidad</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 w-20 text-center" rowSpan={2}>Cant. Contractual</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2.5 py-2 w-24 text-right" rowSpan={2}>Val. Unitario</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2.5 py-2 w-24 text-right" rowSpan={2}>Valor Total</th>
                                                
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2 py-1 text-center bg-slate-200/80 dark:bg-slate-700/80" colSpan={3}>Avance Acumulado</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2 py-1 text-center bg-slate-100 dark:bg-slate-800" colSpan={3}>Avance Anterior</th>
                                                <th className="px-2 py-1 text-center bg-slate-200/80 dark:bg-slate-700/80" colSpan={3}>Avance Actual EP</th>
                                            </tr>
                                            {/* Row 2 Header Sub-Columns */}
                                            <tr className="bg-slate-100 dark:bg-slate-800 border-b border-slate-300 dark:border-slate-700 font-bold text-slate-700 dark:text-slate-300 text-[10px]">
                                                {/* Avance Acumulado */}
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1 text-center w-12 bg-slate-200/80 dark:bg-slate-700/80">Cant.</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1 text-center w-14 bg-slate-200/80 dark:bg-slate-700/80">%</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2 py-1 text-right w-20 bg-slate-200/80 dark:bg-slate-700/80">Total $</th>
                                                {/* Avance Anterior */}
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1 text-center w-12">Cant.</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1 text-center w-14">%</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-2 py-1 text-right w-20">Total $</th>
                                                {/* Avance Actual EP */}
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1 text-center w-12 bg-slate-200/80 dark:bg-slate-700/80">Cant.</th>
                                                <th className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1 text-center w-14 bg-slate-200/80 dark:bg-slate-700/80">%</th>
                                                <th className="px-2 py-1 text-right w-20 bg-slate-200/80 dark:bg-slate-700/80">Total $</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700 font-mono text-[11px]">
                                            {contract && contract.items && contract.items.map((item) => {
                                                const totalUnitsCount = item.details.length || 1;
                                                const unitValueAvg = totalUnitsCount > 0 ? Math.round(item.totalValue / totalUnitsCount) : item.totalValue;
                                                
                                                // Check units paid in previous EPs
                                                const prevPaidUnitIds = new Set<string>();
                                                previousEPs.forEach(pEp => {
                                                    const pItem = pEp.items?.find(i => i.id === item.id);
                                                    pItem?.selectedUnitIds?.forEach(id => prevPaidUnitIds.add(id));
                                                });

                                                // Check units paid in current EP
                                                const currentEpItem = ep.items?.find(i => i.id === item.id);
                                                const currentPaidUnitIds = new Set<string>(currentEpItem?.selectedUnitIds || []);

                                                // Calculate Partida totals
                                                let cantPrev = 0;
                                                let totalPrev = 0;
                                                let cantNow = 0;
                                                let totalNow = 0;

                                                item.details.forEach(d => {
                                                    if (prevPaidUnitIds.has(d.unitId)) {
                                                        cantPrev++;
                                                        totalPrev += d.value;
                                                    }
                                                    if (currentPaidUnitIds.has(d.unitId)) {
                                                        cantNow++;
                                                        totalNow += d.value;
                                                    }
                                                });

                                                const cantAcum = cantPrev + cantNow;
                                                const totalAcum = totalPrev + totalNow;

                                                const percentPrev = totalUnitsCount > 0 ? (cantPrev / totalUnitsCount) * 100 : 0;
                                                const percentNow = totalUnitsCount > 0 ? (cantNow / totalUnitsCount) * 100 : 0;
                                                const percentAcum = totalUnitsCount > 0 ? (cantAcum / totalUnitsCount) * 100 : 0;

                                                const isExpanded = expandedPartidaIds.has(item.id);

                                                return (
                                                    <React.Fragment key={item.id}>
                                                        {/* Parent Row (Partida) */}
                                                        <tr className="bg-slate-50/90 dark:bg-slate-800/70 font-bold text-slate-900 dark:text-white border-b border-slate-300 dark:border-slate-700">
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 text-center font-bold">{resolveItemGanttCode(item.id, item.code)}</td>
                                                            <td 
                                                                onClick={() => togglePartidaExpand(item.id)}
                                                                className="border-r border-slate-300 dark:border-slate-700 px-3 py-2 cursor-pointer font-bold flex items-center gap-1.5 hover:text-blue-600 dark:hover:text-blue-400 select-none"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px] text-slate-500">
                                                                    {isExpanded ? 'expand_less' : 'expand_more'}
                                                                </span>
                                                                <span>{item.description}</span>
                                                            </td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 text-center text-slate-500 font-normal">-</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 text-center font-semibold">Unidad</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 text-center font-bold">{totalUnitsCount}</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-2.5 py-2 text-right">{unitValueAvg.toLocaleString('es-CL')}</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-2.5 py-2 text-right font-bold">{item.totalValue.toLocaleString('es-CL')}</td>
                                                            
                                                            {/* Avance Acumulado */}
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-2 text-center font-bold bg-slate-100/50 dark:bg-slate-800/50">{cantAcum}</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-2 text-center font-bold bg-slate-100/50 dark:bg-slate-800/50">{percentAcum.toFixed(2)}%</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 text-right font-bold bg-slate-100/50 dark:bg-slate-800/50">{totalAcum.toLocaleString('es-CL')}</td>
                                                            
                                                            {/* Avance Anterior */}
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-2 text-center text-slate-600">{cantPrev}</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-2 text-center text-slate-600">{percentPrev.toFixed(2)}%</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-2 text-right text-slate-600">{totalPrev.toLocaleString('es-CL')}</td>
                                                            
                                                            {/* Avance Actual EP */}
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-2 text-center font-bold text-blue-700 dark:text-blue-400 bg-slate-100/50 dark:bg-slate-800/50">{cantNow}</td>
                                                            <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-2 text-center font-bold text-blue-700 dark:text-blue-400 bg-slate-100/50 dark:bg-slate-800/50">{percentNow.toFixed(2)}%</td>
                                                            <td className="px-2 py-2 text-right font-bold text-blue-700 dark:text-blue-400 bg-slate-100/50 dark:bg-slate-800/50">{totalNow.toLocaleString('es-CL')}</td>
                                                        </tr>

                                                        {/* Child Rows (Units) */}
                                                        {isExpanded && item.details.map((detail) => {
                                                            const unitInfo = units.find(u => u.id === detail.unitId);
                                                            const levelName = levels.find(l => l.id === unitInfo?.levelId)?.name || '-';

                                                            const isPaidPrev = prevPaidUnitIds.has(detail.unitId);
                                                            const isPaidNow = currentPaidUnitIds.has(detail.unitId);
                                                            const isPaidAcum = isPaidPrev || isPaidNow;

                                                            return (
                                                                <tr key={detail.unitId} className="hover:bg-slate-50 dark:hover:bg-slate-900/30 text-slate-600 dark:text-slate-400 font-normal">
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-1.5 text-center text-slate-400 font-mono text-[10px]">-</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-3 py-1.5 pl-8 italic font-sans">
                                                                        <span className="text-slate-400 mr-1.5">-</span>
                                                                        {detail.unitName}
                                                                    </td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-1.5 text-center font-sans">{levelName}</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-1.5 text-center font-sans text-slate-400">-</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-1.5 text-center">1</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-2.5 py-1.5 text-right">{detail.value.toLocaleString('es-CL')}</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-2.5 py-1.5 text-right font-medium">{detail.value.toLocaleString('es-CL')}</td>
                                                                    
                                                                    {/* Avance Acumulado (Unit) */}
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1.5 text-center bg-slate-50/50 dark:bg-slate-800/30 font-semibold">{isPaidAcum ? 1 : 0}</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1.5 text-center bg-slate-50/50 dark:bg-slate-800/30">{isPaidAcum ? '100.00%' : '0.00%'}</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-1.5 text-right bg-slate-50/50 dark:bg-slate-800/30 font-medium">{isPaidAcum ? detail.value.toLocaleString('es-CL') : '0'}</td>
                                                                    
                                                                    {/* Avance Anterior (Unit) */}
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1.5 text-center text-slate-400">{isPaidPrev ? 1 : 0}</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1.5 text-center text-slate-400">{isPaidPrev ? '100.00%' : '0.00%'}</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-2 py-1.5 text-right text-slate-400">{isPaidPrev ? detail.value.toLocaleString('es-CL') : '0'}</td>
                                                                    
                                                                    {/* Avance Actual EP (Unit) */}
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1.5 text-center bg-slate-50/50 dark:bg-slate-800/30 font-bold text-blue-600 dark:text-blue-400">{isPaidNow ? 1 : 0}</td>
                                                                    <td className="border-r border-slate-300 dark:border-slate-700 px-1.5 py-1.5 text-center bg-slate-50/50 dark:bg-slate-800/30 font-bold text-blue-600 dark:text-blue-400">{isPaidNow ? '100.00%' : '0.00%'}</td>
                                                                    <td className="px-2 py-1.5 text-right bg-slate-50/50 dark:bg-slate-800/30 font-bold text-blue-600 dark:text-blue-400">{isPaidNow ? detail.value.toLocaleString('es-CL') : '0'}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* RESUMEN DE PAGO Table (Aligned to the Right) */}
                                {(() => {
                                    // Total Avance Actual EP (Monto Bruto de las partidas del presente EP)
                                    let totalEpAmount = 0;
                                    contract?.items?.forEach(item => {
                                        const currentEpItem = ep.items?.find(i => i.id === item.id);
                                        const currentPaidUnitIds = new Set<string>(currentEpItem?.selectedUnitIds || []);
                                        item.details.forEach(d => {
                                            if (currentPaidUnitIds.has(d.unitId)) {
                                                totalEpAmount += d.value;
                                            }
                                        });
                                    });

                                    // Fallback if no unit-level breakdown found
                                    if (totalEpAmount === 0) {
                                        totalEpAmount = ep.preliminaryAmount !== undefined
                                            ? ep.preliminaryAmount
                                            : (parseInt((ep.amount || '').replace(/\D/g, ''), 10) || 0);
                                    }

                                    const retPercent = caratulaRetentionPercent;
                                    const retentionVal = Math.round(totalEpAmount * (retPercent / 100));
                                    const epNeto = totalEpAmount - retentionVal;
                                    const ivaVal = Math.round(epNeto * 0.19);
                                    const valorAPagar = epNeto + ivaVal;

                                    return (
                                        <div className="flex justify-end my-2">
                                            <table className="border-collapse border border-black text-xs w-80 font-sans">
                                                <thead>
                                                    <tr>
                                                        <th colSpan={2} className="border border-black bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-center font-bold text-black dark:text-white uppercase tracking-wide">
                                                            RESUMEN DE PAGO
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-black text-black dark:text-white font-medium text-[11px]">
                                                    <tr>
                                                        <td className="border border-black px-3 py-1.5 font-bold uppercase">ESTADO DE PAGO</td>
                                                        <td className="border border-black px-3 py-1.5 text-right font-mono font-bold">${totalEpAmount.toLocaleString('es-CL')}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border border-black px-3 py-1.5 font-bold uppercase flex items-center justify-between gap-2">
                                                            <span>% RETENCIÓN</span>
                                                            <input 
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                step="0.1"
                                                                value={caratulaRetentionPercent}
                                                                onChange={(e) => setCaratulaRetentionPercent(Number(e.target.value))}
                                                                className="w-12 px-1 py-0.5 text-center border border-black font-bold text-xs bg-white dark:bg-slate-800 text-black dark:text-white rounded-none"
                                                            />
                                                        </td>
                                                        <td className="border border-black px-3 py-1.5 text-right font-mono">{retPercent.toFixed(2)}%</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border border-black px-3 py-1.5 font-bold uppercase text-red-600">RETENCIÓN</td>
                                                        <td className="border border-black px-3 py-1.5 text-right font-mono font-bold text-red-600">-${retentionVal.toLocaleString('es-CL')}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border border-black px-3 py-1.5 font-bold uppercase">ESTADO DE PAGO NETO</td>
                                                        <td className="border border-black px-3 py-1.5 text-right font-mono font-bold">${epNeto.toLocaleString('es-CL')}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border border-black px-3 py-1.5 font-bold uppercase">IVA (19%)</td>
                                                        <td className="border border-black px-3 py-1.5 text-right font-mono">${ivaVal.toLocaleString('es-CL')}</td>
                                                    </tr>
                                                    <tr className="bg-slate-200/80 dark:bg-slate-700/80">
                                                        <td className="border border-black px-3 py-2 font-black uppercase text-xs">VALOR A PAGAR</td>
                                                        <td className="border border-black px-3 py-2 text-right font-mono font-black text-xs">${valorAPagar.toLocaleString('es-CL')}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })()}

                                {/* Bottom Approvals Section */}
                                <div className="pt-4 border-t border-slate-200 dark:border-slate-800 flex flex-col gap-3">
                                    <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[12px] font-black">✓</span>
                                        APROBACIONES
                                    </div>
                                    <div className="p-4 text-center text-xs text-slate-400 italic bg-slate-50 dark:bg-slate-800/40 rounded-md border border-slate-200 dark:border-slate-700">
                                        No se han configurado firmas.
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* --- EVALUACIÓN DE HOJA DE RUTA PARA UN EP SELECCIONADO --- */}
            {selectedEPForRoadmap && (() => {
                const isRoadmapReadOnly = Boolean(selectedEPForRoadmap && (
                    selectedEPForRoadmap.epApprovalStatus === 'Pendiente' ||
                    selectedEPForRoadmap.epApprovalStatus === 'En proceso' ||
                    selectedEPForRoadmap.epApprovalStatus === 'Aprobado'
                ));

                return (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="bg-slate-900 p-5 text-white flex items-center justify-between shadow-md shrink-0 border-b border-slate-800">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-600/30 border border-blue-500/40 flex items-center justify-center shrink-0">
                                    <span className="material-symbols-outlined text-blue-400 text-[24px]">assignment_turned_in</span>
                                </div>
                                <div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <h3 className="text-lg font-bold uppercase tracking-wider text-white">
                                            Evaluación Hoja de Ruta — {selectedEPForRoadmap.period || 'EP'}
                                        </h3>
                                        {isRoadmapReadOnly && (
                                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-900/60 text-blue-300 border border-blue-700/60 flex items-center gap-1.5 shadow-sm">
                                                <span className="material-symbols-outlined text-[16px] text-blue-400">visibility</span>
                                                Modo Visualización (Solo Lectura)
                                            </span>
                                        )}
                                        {/* Estado Hoja de Ruta en la Franja Superior */}
                                        {!isRoadmapReadOnly && computedOverallRoadmapStatus === 'Pendiente' && (
                                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-1.5 shadow-sm">
                                                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                                                Estado: Pendiente
                                            </span>
                                        )}
                                        {!isRoadmapReadOnly && computedOverallRoadmapStatus === 'En Proceso' && (
                                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-500/40 flex items-center gap-1.5 shadow-sm">
                                                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                                Estado: En Proceso
                                            </span>
                                        )}
                                        {!isRoadmapReadOnly && computedOverallRoadmapStatus === 'Aprobado' && (
                                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1.5 shadow-sm">
                                                <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                                                Estado: Aprobado
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {isRoadmapReadOnly ? 'Modo de consulta. El Estado de Pago ya fue enviado a aprobación.' : 'Completa la evaluación por áreas para determinar automáticamente el descuento y el monto liberado del EP.'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    onClick={handlePrintRoadmapOnly}
                                    className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                                    title="Exportar Hoja de Ruta en PDF"
                                >
                                    <span className="material-symbols-outlined text-[16px]">picture_as_pdf</span>
                                    Exportar PDF
                                </button>
                                <button
                                    onClick={() => setSelectedEPForRoadmap(null)}
                                    className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors shrink-0"
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto bg-slate-50 dark:bg-slate-900/50 flex-1 flex flex-col gap-6">
                            {/* Controls / Summary Cards (Cuadrícula 2x2) */}
                            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Fila 1 - Col 1: Monto EP Preliminar */}
                                <div className="bg-slate-50 dark:bg-slate-900/50 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                                        Monto EP Preliminar
                                    </label>
                                    <div className="text-base font-mono font-bold text-slate-800 dark:text-white">
                                        ${globalRoadmapMetrics.preliminaryAmount.toLocaleString('es-CL')}
                                    </div>
                                </div>

                                {/* Fila 1 - Col 2: % Descuentos por Hoja de Ruta */}
                                <div className="bg-amber-50/70 dark:bg-amber-900/20 p-3.5 rounded-xl border border-amber-200/80 dark:border-amber-800/40">
                                    <label className="block text-[11px] font-bold text-amber-800 dark:text-amber-300 uppercase mb-1">
                                        % Descuentos por Hoja de Ruta
                                    </label>
                                    <div className="text-base font-mono font-bold text-amber-900 dark:text-amber-200">
                                        {globalRoadmapMetrics.totalDiscountPercent.toFixed(1).replace('.', ',')}%
                                    </div>
                                </div>

                                {/* Fila 2 - Col 1: Descuentos de Hoja de Ruta */}
                                <div className="bg-rose-50/70 dark:bg-rose-900/20 p-3.5 rounded-xl border border-rose-200/80 dark:border-rose-800/40">
                                    <label className="block text-[11px] font-bold text-rose-800 dark:text-rose-300 uppercase mb-1">
                                        Descuentos de Hoja de Ruta
                                    </label>
                                    <div className="text-base font-mono font-bold text-rose-700 dark:text-rose-300">
                                        -${globalRoadmapMetrics.discountAmount.toLocaleString('es-CL')}
                                    </div>
                                </div>

                                {/* Fila 2 - Col 2: Monto Aprobado por Hoja de Ruta (No editable - Automático) */}
                                <div className="bg-emerald-50 dark:bg-emerald-900/30 p-3.5 rounded-xl border border-emerald-200 dark:border-emerald-700">
                                    <label className="block text-[11px] font-bold text-emerald-800 dark:text-emerald-300 uppercase mb-1">
                                        Monto Aprobado por Hoja de Ruta
                                    </label>
                                    <div className="text-base font-mono font-extrabold text-emerald-700 dark:text-emerald-300">
                                        ${globalRoadmapMetrics.approvedAmount.toLocaleString('es-CL')}
                                    </div>
                                </div>
                            </div>

                            {/* Areas & Parameters checklist */}
                            <div className="flex flex-col gap-4">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                    Checklist de Evaluación por Áreas
                                </h4>

                                {revisionAreas.map((area, aIdx) => {
                                    const isExpanded = expandedAreaIds.has(area.id);
                                    const metrics = getAreaMetrics(area);
                                    const areaApproval = selectedEPForRoadmap.roadmapAreaApprovals?.[area.id];

                                    return (
                                        <div key={area.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
                                            <div
                                                onClick={() => toggleAreaExpand(area.id)}
                                                className="p-4 bg-slate-100 dark:bg-slate-700/50 text-xs border-b border-slate-200 dark:border-slate-700 flex flex-col gap-3 cursor-pointer select-none hover:bg-slate-200/70 dark:hover:bg-slate-700 transition-colors"
                                            >
                                                {/* FILA 1: Nombre del Área (solo) + Controles + Aprobar Área / Sello Aprobación */}
                                                <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                                                    <div className="flex items-center gap-2">
                                                        <span className="material-symbols-outlined text-slate-500 text-[20px] shrink-0">
                                                            {isExpanded ? 'expand_less' : 'expand_more'}
                                                        </span>
                                                        <span className="text-slate-900 dark:text-white font-extrabold text-sm uppercase tracking-wide">
                                                            {aIdx + 1}. {area.title}
                                                        </span>
                                                        <span className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold ml-1">
                                                            ({area.parameters.length} controles)
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center gap-3">
                                                        {areaApproval ? (
                                                            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 rounded-full font-bold text-xs shadow-2xs shrink-0 select-none">
                                                                <span className="material-symbols-outlined text-[16px] text-emerald-600">verified</span>
                                                                <span>Área Aprobada por {areaApproval.approvedBy} ({areaApproval.approvedAt})</span>
                                                            </div>
                                                        ) : !isRoadmapReadOnly ? (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleApproveArea(area.id);
                                                                }}
                                                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs shadow-xs shrink-0 cursor-pointer transition-colors"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                                Aprobar Área
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                {/* FILA 2: Indicadores debajo, ordenados de forma clara */}
                                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                                    {/* Dcto. Máx. */}
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 dark:bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40 rounded-lg font-semibold text-[11px] shadow-2xs">
                                                        <span className="text-[10px] uppercase font-bold text-amber-800 dark:text-amber-300">Dcto. Máx.:</span>
                                                        <span className="font-mono font-extrabold text-xs bg-amber-500 text-white px-2 py-0.5 rounded-md">
                                                            {metrics.maxDiscount}%
                                                        </span>
                                                    </div>

                                                    {/* Observados */}
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-200/80 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-[11px] font-medium border border-slate-300 dark:border-slate-700">
                                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Observados:</span>
                                                        <span className={`font-mono font-extrabold ${metrics.observados > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-800 dark:text-slate-200'}`}>
                                                            {metrics.observados}
                                                        </span>
                                                    </div>

                                                    {/* Aplicables */}
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-200/80 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-[11px] font-medium border border-slate-300 dark:border-slate-700">
                                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Aplicables:</span>
                                                        <span className="font-mono font-extrabold text-slate-800 dark:text-slate-200">
                                                            {metrics.aplicables}
                                                        </span>
                                                    </div>

                                                    {/* Suma Factores */}
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-200/80 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-[11px] font-medium border border-slate-300 dark:border-slate-700">
                                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase">Suma Factores:</span>
                                                        <span className="font-mono font-extrabold text-amber-700 dark:text-amber-400">
                                                            {metrics.sumaFactores.toFixed(1).replace('.', ',')}
                                                        </span>
                                                    </div>

                                                    {/* Dcto. Área */}
                                                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border ${metrics.descuentoAreaPercent > 0 ? 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700' : 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700'}`}>
                                                        <span className="text-[10px] uppercase font-bold">Dcto. Área:</span>
                                                        <span className="font-mono font-extrabold text-xs">
                                                            {metrics.descuentoAreaPercent.toFixed(1).replace('.', ',')}%
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="overflow-x-auto border-t border-slate-100 dark:border-slate-800 animate-in fade-in duration-150">
                                                    <table className="w-full text-left border-collapse text-xs">
                                                        <thead>
                                                            <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase">
                                                                <th className="px-3.5 py-2.5">Parámetro de Revisión</th>
                                                                <th className="px-3.5 py-2.5 text-center w-40">Estado</th>
                                                                <th className="px-3.5 py-2.5 text-center w-44">Periodos Observados</th>
                                                                <th className="px-3.5 py-2.5 text-center w-28">Factor</th>
                                                                <th className="px-3.5 py-2.5 text-center w-36">Acción / Comentarios</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                            {area.parameters.map((param) => {
                                                                const evalData = roadmapEvalAnswers[param.id] || {};
                                                                const status = evalData.status || '';
                                                                const isNoCumple = status === 'no_cumple';
                                                                const observedPeriods = evalData.observedPeriods || 1;
                                                                const factor = isNoCumple
                                                                    ? (observedPeriods === 2 ? 1.5 : observedPeriods === 3 ? 2.0 : 1.0)
                                                                    : undefined;

                                                                return (
                                                                    <tr key={param.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-900/40 transition-colors">
                                                                        {/* Parámetro */}
                                                                        <td className="px-3.5 py-3 font-medium text-slate-700 dark:text-slate-200">
                                                                            <div>{param.name}</div>
                                                                            {evalData.comment && (
                                                                                <div className="mt-1 text-[11px] italic text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-900/60 p-1.5 rounded border-l-2 border-blue-500 flex items-start gap-1">
                                                                                    <span className="material-symbols-outlined text-[14px] text-blue-500 shrink-0 mt-0.5">comment</span>
                                                                                    <span>{evalData.comment}</span>
                                                                                </div>
                                                                            )}
                                                                        </td>

                                                                        {/* Estado Dropdown */}
                                                                        <td className="px-3.5 py-3 text-center">
                                                                            <select
                                                                                value={status}
                                                                                disabled={isRoadmapReadOnly}
                                                                                onChange={(e) => handleParamStatusChange(param.id, e.target.value as any)}
                                                                                className={`
                                                                                    w-full px-2.5 py-1.5 rounded-lg font-bold text-xs border transition-colors focus:ring-2 focus:ring-blue-500
                                                                                    ${isRoadmapReadOnly ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}
                                                                                    ${status === 'cumple'
                                                                                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-300 dark:border-emerald-700'
                                                                                        : status === 'no_cumple'
                                                                                            ? 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/50 dark:text-rose-300 dark:border-rose-700'
                                                                                            : status === 'na'
                                                                                                ? 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                                                                                                : 'bg-white text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'}
                                                                                `}
                                                                            >
                                                                                <option value="">-- Seleccionar --</option>
                                                                                <option value="cumple" className="bg-emerald-100 text-emerald-800">Cumple</option>
                                                                                <option value="no_cumple" className="bg-rose-100 text-rose-800">No Cumple</option>
                                                                                <option value="na" className="bg-slate-100 text-slate-700">No Aplica</option>
                                                                            </select>
                                                                        </td>

                                                                        {/* Periodos Observados Dropdown */}
                                                                        <td className="px-3.5 py-3 text-center">
                                                                            <select
                                                                                value={isNoCumple ? observedPeriods : 1}
                                                                                disabled={isRoadmapReadOnly || !isNoCumple}
                                                                                onChange={(e) => handleParamPeriodsChange(param.id, Number(e.target.value) as 1 | 2 | 3)}
                                                                                className={`
                                                                                    w-full px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors
                                                                                    ${isNoCumple && !isRoadmapReadOnly
                                                                                        ? 'bg-white text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-white cursor-pointer focus:ring-2 focus:ring-blue-500'
                                                                                        : 'bg-slate-100 text-slate-400 border-slate-200 dark:bg-slate-900/50 dark:text-slate-600 dark:border-slate-800 cursor-not-allowed opacity-60'}
                                                                                `}
                                                                            >
                                                                                {isNoCumple ? (
                                                                                    <>
                                                                                        <option value={1}>1</option>
                                                                                        <option value={2}>2</option>
                                                                                        <option value={3}>3</option>
                                                                                    </>
                                                                                ) : (
                                                                                    <option value={1}>-</option>
                                                                                )}
                                                                            </select>
                                                                        </td>

                                                                        {/* Factor Display */}
                                                                        <td className="px-3.5 py-3 text-center">
                                                                            <div className={`
                                                                                px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold border text-center
                                                                                ${isNoCumple
                                                                                    ? 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700'
                                                                                    : 'bg-slate-100 text-slate-400 border-slate-200 dark:bg-slate-900/50 dark:text-slate-600 dark:border-slate-800 opacity-60'}
                                                                            `}>
                                                                                {isNoCumple && factor !== undefined ? factor.toFixed(1).replace('.', ',') : '-'}
                                                                            </div>
                                                                        </td>

                                                                        {/* Action / Comment button */}
                                                                        <td className="px-3.5 py-3 text-center">
                                                                            {isNoCumple ? (
                                                                                <button
                                                                                    disabled={isRoadmapReadOnly}
                                                                                    onClick={() => setCommentModalTarget({ paramId: param.id, paramName: param.name, comment: evalData.comment || '' })}
                                                                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                                                                                        evalData.comment
                                                                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
                                                                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                                                                                    }`}
                                                                                    title="Agregar o editar observación del parámetro"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px]">
                                                                                        {evalData.comment ? 'chat' : 'chat_bubble_outline'}
                                                                                    </span>
                                                                                    <span>{evalData.comment ? 'Obs.' : 'Comentar'}</span>
                                                                                </button>
                                                                            ) : (
                                                                                <span className="text-slate-300 dark:text-slate-700 text-xs">-</span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
                            {isRoadmapReadOnly ? (
                                <button
                                    onClick={() => setSelectedEPForRoadmap(null)}
                                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                                >
                                    Cerrar Visualizador
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setSelectedEPForRoadmap(null)}
                                        className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 text-slate-700 dark:text-slate-200 font-bold text-xs rounded-xl cursor-pointer"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleSaveRoadmapEvaluation}
                                        className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">save</span>
                                        Guardar Evaluación
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* --- GENERATE EP WORKFLOW MODALS --- */}

            {/* 1. Generate EP Main Modal (Summary) */}
            {generateEpOpen && (
                isMobile ? (
                    <MobileGenerateEPModal
                        open={generateEpOpen}
                        onClose={() => setGenerateEpOpen(false)}
                        currentDraftEp={currentDraftEp}
                        subcontractor={getSubcontractorById(currentDraftEp.subcontractorId || '')}
                        epNumber={paymentStatements.filter(p => p.contractId === currentDraftEp.contractId).length + 1}
                        onOpenItemDetail={handleOpenEpDetail}
                        onSaveDraft={handleSaveDraftEp}
                        onAuthorize={handleAuthorizeClick}
                        canAuthorize={hasPermission("sc_ep_autorizar", "edit")}
                    />
                ) : (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-none shadow-2xl w-full max-w-6xl border border-slate-300 dark:border-slate-700 flex flex-col h-[90vh] overflow-hidden">

                            {/* Blue Header */}
                            <div className="bg-blue-600 px-6 py-4">
                                <h3 className="text-lg font-bold text-white uppercase tracking-wider">ESTADO DE PAGO</h3>
                            </div>

                            {/* Info Section */}
                            <div className="p-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                                <div className="flex flex-col gap-1 mb-4">
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Estado de Pago #{paymentStatements.filter(p => p.contractId === currentDraftEp.contractId).length + 1}</h4>
                                    <div className="text-sm text-slate-700 dark:text-slate-300 flex flex-col gap-0.5">
                                        <span><span className="font-semibold">R.U.T:</span> {getSubcontractorById(currentDraftEp.subcontractorId || '')?.rut}</span>
                                        <span><span className="font-semibold uppercase">RAZÓN SOCIAL:</span> {getSubcontractorById(currentDraftEp.subcontractorId || '')?.razonSocial}</span>
                                    </div>
                                </div>

                                {/* NOTE: Add Items button removed because items come strictly from the contract now */}
                            </div>

                            {/* Table */}
                            <div className="flex-1 overflow-auto p-6">
                                <table className="w-full text-left border-collapse border border-black">
                                    <thead>
                                        <tr>
                                            <th className="border border-black px-2 py-1 text-sm font-normal text-black w-24">Codigo</th>
                                            <th className="border border-black px-2 py-1 text-sm font-normal text-black">Partidas</th>
                                            <th className="border border-black px-2 py-1 text-sm font-normal text-black w-48 text-right">Valor Acumulado</th>
                                            <th className="border border-black px-2 py-1 text-sm font-normal text-black w-48 text-right">Monto a Pagar Acumulado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentDraftEp.items?.map(item => (
                                            <tr key={item.id} onClick={() => handleOpenEpDetail(item)} className="cursor-pointer hover:bg-blue-50">
                                                <td className="border border-black px-2 py-1 text-sm">{resolveItemGanttCode(item.id, item.code)}</td>
                                                <td className="border border-black px-2 py-1 text-sm">{item.description}</td>
                                                <td className="border border-black px-2 py-1 text-sm text-right">{item.totalValue.toLocaleString()}</td>
                                                <td className="border border-black px-2 py-1 text-sm text-right font-bold">{item.paidAmount.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                        {(!currentDraftEp.items || currentDraftEp.items.length === 0) && (
                                            <tr><td colSpan={4} className="border border-black p-4 text-center text-slate-500 italic">No hay partidas añadidas.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer Buttons */}
                            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 bg-white dark:bg-slate-900">
                                <button onClick={() => setGenerateEpOpen(false)} className="px-6 py-2 bg-white border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 rounded">Cancelar</button>
                                <button onClick={handleSaveDraftEp} className="px-6 py-2 bg-blue-600 text-white font-bold hover:bg-blue-700 rounded shadow-sm">Guardar</button>
                                <button onClick={handleAuthorizeClick} className={`px-6 py-2 bg-green-600 text-white font-bold hover:bg-green-700 rounded shadow-sm ${!hasPermission("sc_ep_autorizar", "edit") ? "opacity-50 pointer-events-none" : ""}`}>Autorizar</button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {/* 2. Detail EP Modal */}
            {detailEpOpen && activeDraftTask && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-none shadow-2xl w-full max-w-6xl border border-slate-300 dark:border-slate-700 flex flex-col max-h-[90vh] overflow-hidden">

                        {/* Blue Header */}
                        <div className="bg-blue-600 px-6 py-4">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">DETALLE ESTADO DE PAGO</h3>
                        </div>

                        {/* Detail Table */}
                        <div className="flex-1 overflow-auto p-6">
                            <table className="w-full text-left border-collapse border border-black">
                                <thead>
                                    <tr>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black w-24">Codigo</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black">Partidas</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black">Nivel</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black">Unidad Fisica</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black text-right w-32">Valor</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black text-center w-40">Protocolo Aprobado</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black text-right w-32">Monto a Pagar</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black w-10 text-center">Sel.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const item = currentDraftEp.items?.find(i => i.id === activeDraftTask.id);

                                        return item?.details.map(detail => {
                                            const info = getUnitPendingBalanceAndStatus(
                                                currentDraftEp.contractId || '',
                                                activeDraftTask.id,
                                                detail,
                                                currentDraftEp.id
                                            );

                                            const isSelected = tempSelectedDetailUnits.includes(detail.unitId);

                                            return (
                                                <tr key={detail.unitId} className={`hover:bg-slate-50 ${info.status === 'FULLY_PAID' || info.status === 'IN_PROCESS' ? 'opacity-70 bg-slate-50' : ''}`}>
                                                    <td className="border border-black px-2 py-1 text-sm font-mono">{activeDraftTask.code}</td>
                                                    <td className="border border-black px-2 py-1 text-sm font-bold">{activeDraftTask.name}</td>
                                                    <td className="border border-black px-2 py-1 text-sm">{levels.find(l => l.id === units.find(u => u.id === detail.unitId)?.levelId)?.name || "-"}</td>
                                                    <td className="border border-black px-2 py-1 text-sm font-medium">{detail.unitName}</td>
                                                    <td className="border border-black px-2 py-1 text-sm text-right font-mono">${(detail.value || 0).toLocaleString()}</td>
                                                    <td className="border border-black px-2 py-1 text-sm text-center font-bold">
                                                        <div className="flex items-center justify-center gap-1">
                                                            {info.status === 'IN_PROCESS' ? (
                                                                <span className="text-blue-800 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 font-extrabold px-2 py-0.5 rounded border border-blue-300 dark:border-blue-700 text-xs flex items-center justify-center gap-1 shadow-2xs">
                                                                    <span className="material-symbols-outlined text-[14px] text-blue-600">hourglass_top</span>
                                                                    En Proceso
                                                                </span>
                                                            ) : info.status === 'FULLY_PAID' ? (
                                                                <span className="text-slate-700 bg-slate-200 dark:bg-slate-800 dark:text-slate-300 font-bold px-2 py-0.5 rounded text-xs">
                                                                    Pagado
                                                                </span>
                                                            ) : info.status === 'PENDING_BALANCE' ? (
                                                                <span className="text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 font-extrabold px-2 py-0.5 rounded border border-amber-300 dark:border-amber-700 text-xs flex items-center justify-center gap-1 shadow-2xs">
                                                                    <span className="material-symbols-outlined text-[14px] text-amber-600">history_edu</span>
                                                                    Saldo Pendiente
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <span className={info.isPayable ? 'text-green-600 font-bold' : 'text-red-500 bg-red-50 font-bold px-1 rounded'}>{info.label}</span>
                                                                    {!info.isPayable && (
                                                                        <span className="material-symbols-outlined text-[16px] text-red-500" title="Protocolo Pendiente de Aprobador de Pago">warning</span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-sm text-right font-mono font-bold">
                                                        {(info.status === 'FULLY_PAID' || info.status === 'IN_PROCESS' || info.status === 'NOT_APPROVED')
                                                            ? '-'
                                                            : (info.pendingBalance > 0 ? `$${info.pendingBalance.toLocaleString()}` : '-')}
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={(info.status === 'FULLY_PAID' || info.status === 'IN_PROCESS') ? true : isSelected}
                                                            disabled={info.status === 'FULLY_PAID' || info.status === 'IN_PROCESS' || !info.isPayable || info.pendingBalance <= 0}
                                                            onChange={() => toggleDetailUnitSelection(detail.unitId, info.pendingBalance, info.status === 'PENDING_BALANCE')}
                                                            className="rounded border-slate-300 text-primary focus:ring-primary disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        });
                                    })()}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 bg-white dark:bg-slate-900">
                            <button onClick={() => setDetailEpOpen(false)} className="px-6 py-2 bg-white border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 rounded">Cancelar</button>
                            <button onClick={handleSaveDetailEp} className="px-6 py-2 bg-blue-600 text-white font-bold hover:bg-blue-700 rounded shadow-sm">Guardar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. Signature Modal */}
            {signatureModalOpen && (
                <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[500px] border border-slate-300 p-6">
                        <h3 className="text-lg font-bold mb-4 text-center">Autorizar Estado de Pago</h3>
                        <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 h-40 bg-slate-50 dark:bg-slate-800 rounded flex items-center justify-center cursor-crosshair relative group">
                            <span className="text-slate-400 group-hover:hidden select-none">Dibuje su firma aquí</span>
                            <div className="hidden group-hover:block absolute text-4xl text-blue-600 font-handwriting select-none" style={{ fontFamily: 'cursive' }}>Firma Autorizada</div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button onClick={() => setSignatureModalOpen(false)} className="flex-1 py-2 border border-slate-300 rounded text-slate-600 hover:bg-slate-50">Cancelar</button>
                            <button onClick={handleConfirmSignature} className="flex-1 py-2 bg-green-600 text-white rounded font-bold hover:bg-green-700">Confirmar Firma</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 1. Modal: Crear/Editar Master Subcontrato (DB) */}
            {createMasterSubModalOpen && (
                isMobile ? (
                    <MobileCreateSubcontractorModal
                        open={createMasterSubModalOpen}
                        onClose={() => { setCreateMasterSubModalOpen(false); setEditingSubcontractorId(null); }}
                        editingSubcontractorId={editingSubcontractorId}
                        newSubcontractor={newSubcontractor}
                        setNewSubcontractor={setNewSubcontractor}
                        subcontractTypes={subcontractTypes}
                        globalSubcontractors={globalSubcontractors}
                        subcontractors={subcontractors}
                        onSave={handleSaveMasterSubcontractor}
                    />
                ) : (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]">
                            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">{editingSubcontractorId ? 'Editar Empresa Contratista' : 'Crear Nueva Empresa Contratista'}</h3>
                                <button onClick={() => { setCreateMasterSubModalOpen(false); setEditingSubcontractorId(null); }} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                        <div className="p-6 overflow-y-auto">
                            {(() => {
                                const cleanCurrentRut = cleanRut(newSubcontractor.rut);
                                const isRutEntered = cleanCurrentRut.length >= 2;
                                const isRutFound = !editingSubcontractorId && isRutEntered && (
                                    (globalSubcontractors || []).some(s => cleanRut(s.rut) === cleanCurrentRut) ||
                                    subcontractors.some(s => cleanRut(s.rut) === cleanCurrentRut)
                                );

                                const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                    const formatted = formatRut(e.target.value);
                                    const cleaned = cleanRut(formatted);

                                    if (!editingSubcontractorId && cleaned.length >= 8) {
                                        const match = (globalSubcontractors || []).find(s => cleanRut(s.rut) === cleaned) ||
                                                      subcontractors.find(s => cleanRut(s.rut) === cleaned);
                                        if (match) {
                                            setNewSubcontractor({
                                                rut: formatted,
                                                razonSocial: match.razonSocial,
                                                giro: match.giro || '',
                                                representante: match.representante || '',
                                                telefono: match.telefono || '',
                                                correo: match.correo || '',
                                                subcontractTypeId: match.subcontractTypeId || '',
                                                fono: match.fono || '',
                                                ciudad: match.ciudad || '',
                                                comuna: match.comuna || '',
                                                status: match.status || 'Activo',
                                                linkedUserIds: match.linkedUserIds || []
                                            });
                                            return;
                                        }
                                    }

                                    setNewSubcontractor(prev => ({ ...prev, rut: formatted }));
                                };

                                return (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="col-span-1 md:col-span-2">
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2 flex items-center justify-between">
                                                    <span>R.U.T <span className="text-rose-500">*</span></span>
                                                    {!isRutEntered && (
                                                        <span className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold lowercase">
                                                            (Ingrese el RUT para habilitar los demás campos)
                                                        </span>
                                                    )}
                                                    {isRutFound && (
                                                        <span className="text-[11px] bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 rounded font-bold">
                                                            ✓ Empresa encontrada en el Maestro (Datos autocompletados)
                                                        </span>
                                                    )}
                                                </label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Ej: 76.543.210-K" 
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm font-bold font-mono outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white" 
                                                    value={newSubcontractor.rut} 
                                                    onChange={handleRutChange} 
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Razón Social <span className="text-rose-500">*</span></label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Ej: Instalaciones SpA" 
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                                                    value={newSubcontractor.razonSocial} 
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, razonSocial: e.target.value })} 
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Giro</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Ej: Obras civiles" 
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                                                    value={newSubcontractor.giro} 
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, giro: e.target.value })} 
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Tipo de Subcontrato</label>
                                                <select
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={newSubcontractor.subcontractTypeId}
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, subcontractTypeId: e.target.value })}
                                                >
                                                    <option value="">Seleccionar Tipo...</option>
                                                    {subcontractTypes.map(t => (
                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Representante</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Nombre del representante" 
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                                                    value={newSubcontractor.representante} 
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, representante: e.target.value })} 
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Teléfono</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="+56 9 1234 5678" 
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                                                    value={newSubcontractor.telefono} 
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, telefono: e.target.value })} 
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Fono</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="+56 2 2345 6789" 
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                                                    value={newSubcontractor.fono} 
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, fono: e.target.value })} 
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Correo</label>
                                                <input 
                                                    type="email" 
                                                    placeholder="contacto@empresa.cl" 
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                                                    value={newSubcontractor.correo} 
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, correo: e.target.value })} 
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Ciudad (Región)</label>
                                                <select
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={newSubcontractor.ciudad}
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, ciudad: e.target.value, comuna: '' })}
                                                >
                                                    <option value="">Seleccionar Región...</option>
                                                    {chileData.map(d => (
                                                        <option key={d.region} value={d.region}>{d.region}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Comuna</label>
                                                <select
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={newSubcontractor.comuna}
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, comuna: e.target.value })}
                                                    disabled={!isRutEntered || !newSubcontractor.ciudad}
                                                >
                                                    <option value="">Seleccionar Comuna...</option>
                                                    {chileData.find(d => d.region === newSubcontractor.ciudad)?.comunas.map(c => (
                                                        <option key={c} value={c}>{c}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Estado</label>
                                                <select
                                                    disabled={!isRutEntered}
                                                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={newSubcontractor.status || 'Activo'}
                                                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, status: e.target.value })}
                                                >
                                                    <option value="Activo">Activo</option>
                                                    <option value="Inactivo">Inactivo</option>
                                                </select>
                                            </div>

                                            <div className="col-span-1 md:col-span-2 mt-2">
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Usuarios Vinculados</label>
                                                <div className={`border border-slate-300 dark:border-slate-600 rounded-lg p-2 max-h-40 overflow-y-auto bg-white dark:bg-slate-800 ${!isRutEntered ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    {globalUsers.map(user => {
                                                        const isChecked = (newSubcontractor.linkedUserIds || []).includes(user.id);
                                                        return (
                                                            <label key={user.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer rounded">
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={isChecked}
                                                                    disabled={!isRutEntered}
                                                                    onChange={(e) => {
                                                                        const currentIds = newSubcontractor.linkedUserIds || [];
                                                                        if (e.target.checked) {
                                                                            setNewSubcontractor({ ...newSubcontractor, linkedUserIds: [...currentIds, user.id] });
                                                                        } else {
                                                                            setNewSubcontractor({ ...newSubcontractor, linkedUserIds: currentIds.filter(id => id !== user.id) });
                                                                        }
                                                                    }}
                                                                    className="rounded border-slate-300 text-primary"
                                                                />
                                                                <span className="text-sm text-slate-700 dark:text-slate-300">{user.name} ({user.email})</span>
                                                            </label>
                                                        )
                                                    })}
                                                    {globalUsers.length === 0 && <span className="text-xs text-slate-500">No hay usuarios disponibles</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                        <div className="p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 rounded-b-xl">
                            <button onClick={() => { setCreateMasterSubModalOpen(false); setEditingSubcontractorId(null); }} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg font-medium text-sm">Cancelar</button>
                            <button 
                                onClick={handleSaveMasterSubcontractor} 
                                disabled={cleanRut(newSubcontractor.rut).length < 2}
                                className="px-6 py-2 bg-primary text-white hover:bg-blue-600 rounded-lg font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingSubcontractorId ? 'Guardar Cambios' : 'Guardar Empresa Contratista'}
                            </button>
                        </div>
                    </div>
                </div>
                )
            )}

            {/* 2. Modal: Lista Master Subcontratos */}
            {listMasterSubModalOpen && (
                isMobile ? (
                    <MobileContractorListSheet
                        open={listMasterSubModalOpen}
                        onClose={() => setListMasterSubModalOpen(false)}
                        subcontractors={subcontractors}
                        onEditSubcontractor={handleEditMasterSubcontractor}
                    />
                ) : (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">Lista de Empresas Contratistas</h3>
                                <button onClick={() => setListMasterSubModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            <div className="flex-1 overflow-auto p-0">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 shadow-sm">
                                        <tr>
                                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">R.U.T</th>
                                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Razón Social</th>
                                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Estado</th>
                                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700 bg-white dark:bg-slate-900">
                                        {subcontractors.map((sub) => (
                                            <tr key={sub.id}>
                                                <td className="px-6 py-4 text-sm font-mono">{sub.rut}</td>
                                                <td className="px-6 py-4 text-sm font-bold">{sub.razonSocial}</td>
                                                <td className="px-6 py-4 text-sm">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                        (sub.status || 'Activo') === 'Activo' 
                                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                                                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                    }`}>
                                                        {sub.status || 'Activo'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-right">
                                                    <button
                                                        onClick={() => handleEditMasterSubcontractor(sub)}
                                                        className="inline-flex items-center gap-1 text-primary hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-bold"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">edit</span>
                                                        Editar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 rounded-b-xl flex justify-end">
                                <button onClick={() => setListMasterSubModalOpen(false)} className="px-6 py-2 bg-white border rounded-lg text-sm font-medium">Cerrar</button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {/* 2.5 Modal: Configuración de Aprobaciones */}
            {approvalConfigModalOpen && (
                isMobile ? (
                    <MobileApprovalConfigModal
                        open={approvalConfigModalOpen}
                        onClose={() => setApprovalConfigModalOpen(false)}
                        approvalFlows={approvalFlows}
                        setApprovalFlows={setApprovalFlows}
                        sortedResponsibles={sortedResponsibles}
                    />
                ) : (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh] overflow-hidden">
                            {/* Header */}
                            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary text-[28px]">fact_check</span>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-800 dark:text-white">Configuración de Aprobaciones</h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Define los flujos de aprobación y asigna los revisores responsables.</p>
                                    </div>
                                </div>
                                <button onClick={() => setApprovalConfigModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 dark:bg-slate-900/20">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    
                                    {/* Card 1: Aprobación Subcontratos */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col">
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="material-symbols-outlined text-blue-500 text-[24px]">handshake</span>
                                            <h4 className="text-base font-bold text-slate-800 dark:text-white">Aprobación Subcontratos</h4>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                                            Flujo de firmas requeridas para activar un contrato de subcontratación.
                                        </p>

                                        <div className="flex-1 flex flex-col gap-3">
                                            <button
                                                onClick={() => {
                                                    setApprovalFlows(prev => ({
                                                        ...prev,
                                                        subcontracts: [...prev.subcontracts, { reviewerId: '' }]
                                                    }));
                                                }}
                                                className="w-full py-2 border-2 border-dashed border-primary/30 text-primary font-bold text-xs rounded-lg hover:bg-primary/5 transition-colors flex items-center justify-center gap-1 bg-slate-50/50 dark:bg-slate-800/50"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                                Añadir Revisor
                                            </button>

                                            <div className="space-y-3 max-h-[250px] overflow-y-auto pr-1">
                                                {approvalFlows.subcontracts.map((step, idx) => (
                                                    <div key={idx} className="flex flex-col gap-2 bg-slate-50 dark:bg-slate-800/60 p-2 rounded-lg border border-slate-100 dark:border-slate-700/50 animate-in fade-in slide-in-from-top-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-bold text-slate-400 w-6 text-center">{idx + 1}°</span>
                                                            <select
                                                                className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary dark:text-white"
                                                                value={step.reviewerId}
                                                                onChange={(e) => {
                                                                    const updated = [...approvalFlows.subcontracts];
                                                                    updated[idx] = { ...updated[idx], reviewerId: e.target.value };
                                                                    setApprovalFlows(prev => ({ ...prev, subcontracts: updated }));
                                                                }}
                                                            >
                                                                <option value="">Seleccionar Revisor...</option>
                                                                {sortedResponsibles.map(r => (
                                                                    <option key={r.id} value={r.id}>{r.name} ({r.role})</option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                onClick={() => {
                                                                    const updated = approvalFlows.subcontracts.filter((_, i) => i !== idx);
                                                                    setApprovalFlows(prev => ({ ...prev, subcontracts: updated }));
                                                                }}
                                                                className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                                            >
                                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                                {approvalFlows.subcontracts.length === 0 && (
                                                    <p className="text-xs text-slate-400 italic text-center py-6 bg-slate-50 dark:bg-slate-800/30 rounded-lg border border-dashed border-slate-200 dark:border-slate-700">
                                                        No se han configurado revisores.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card 2: Aprobación de Estados de Pago */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col">
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="material-symbols-outlined text-emerald-500 text-[24px]">payments</span>
                                            <h4 className="text-base font-bold text-slate-800 dark:text-white">Aprobación de Estados de Pago</h4>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                                            Flujo secuencial de aprobaciones necesario para liberar y visar un estado de pago.
                                        </p>

                                        <div className="flex-1 flex flex-col gap-3">
                                            <button
                                                onClick={() => {
                                                    setApprovalFlows(prev => ({
                                                        ...prev,
                                                        paymentStatements: [...prev.paymentStatements, { reviewerId: '' }]
                                                    }));
                                                }}
                                                className="w-full py-2 border-2 border-dashed border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-bold text-xs rounded-lg hover:bg-emerald-500/5 transition-colors flex items-center justify-center gap-1 bg-slate-50/50 dark:bg-slate-800/50"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                                Añadir Revisor
                                            </button>

                                            <div className="space-y-3 max-h-[250px] overflow-y-auto pr-1">
                                                {approvalFlows.paymentStatements.map((step, idx) => (
                                                    <div key={idx} className="flex flex-col gap-2 bg-slate-50 dark:bg-slate-800/60 p-3 rounded-lg border border-slate-100 dark:border-slate-700/50 animate-in fade-in slide-in-from-top-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-bold text-slate-400 w-6 text-center">{idx + 1}°</span>
                                                            <select
                                                                className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-emerald-500 dark:text-white"
                                                                value={step.reviewerId}
                                                                onChange={(e) => {
                                                                    const updated = [...approvalFlows.paymentStatements];
                                                                    updated[idx] = { ...updated[idx], reviewerId: e.target.value };
                                                                    setApprovalFlows(prev => ({ ...prev, paymentStatements: updated }));
                                                                }}
                                                            >
                                                                <option value="">Seleccionar Revisor...</option>
                                                                {sortedResponsibles.map(r => (
                                                                    <option key={r.id} value={r.id}>{r.name} ({r.role})</option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                onClick={() => {
                                                                    const updated = approvalFlows.paymentStatements.filter((_, i) => i !== idx);
                                                                    setApprovalFlows(prev => ({ ...prev, paymentStatements: updated }));
                                                                }}
                                                                className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                                            >
                                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                                            </button>
                                                        </div>
                                                        <div className="flex items-center gap-2 pl-8">
                                                            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Aplica si monto supera:</span>
                                                            <div className="relative flex-1 max-w-[160px]">
                                                                <span className="absolute left-2 top-1 text-[10px] text-slate-400">$</span>
                                                                <input
                                                                    type="number"
                                                                    placeholder="Siempre aplica"
                                                                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded pl-5 pr-1 py-0.5 text-[10px] outline-none focus:ring-1 focus:ring-emerald-500 dark:text-white"
                                                                    value={step.minAmount || ''}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value ? parseFloat(e.target.value) : undefined;
                                                                        const updated = [...approvalFlows.paymentStatements];
                                                                        updated[idx] = { ...updated[idx], minAmount: val };
                                                                        setApprovalFlows(prev => ({ ...prev, paymentStatements: updated }));
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {approvalFlows.paymentStatements.length === 0 && (
                                                    <p className="text-xs text-slate-400 italic text-center py-6 bg-slate-50 dark:bg-slate-800/30 rounded-lg border border-dashed border-slate-200 dark:border-slate-700">
                                                        No se han configurado revisores.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 bg-white dark:bg-slate-900 rounded-b-xl">
                                <button onClick={() => setApprovalConfigModalOpen(false)} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded-lg shadow-sm">Listo</button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {/* 3. NEW Modal: AÑADIR SUBCONTRATOS (Create Payment Statement) */}
            {addStatementModalOpen && (
                isMobile ? (
                    <MobileAddContractModal
                        open={addStatementModalOpen}
                        onClose={() => setAddStatementModalOpen(false)}
                        currentForm={currentForm}
                        setCurrentForm={setCurrentForm}
                        isEditingAuthorized={isEditingAuthorized}
                        subcontractors={subcontractors}
                        responsibles={responsibles}
                        globalUsers={globalUsers}
                        tasks={tasks}
                        onOpenTaskSelector={() => handleOpenTaskSelector(false)}
                        onOpenSubcontractorPicker={() => setSubcontractorPickerOpen(true)}
                        onOpenDocuments={() => setDocumentsModalOpen(true)}
                        onSave={handleSaveContract}
                        approvalFlows={approvalFlows}
                    />
                ) : (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-none md:rounded-lg shadow-2xl w-full max-w-7xl border-0 md:border border-slate-300 dark:border-slate-700 flex flex-col h-full md:h-[85vh] overflow-hidden">

                        {/* Header Blue */}
                        <div className="bg-blue-600 px-6 py-3 flex justify-between items-center shrink-0">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                                {currentForm.id ? 'EDITAR SUBCONTRATO' : 'AÑADIR SUBCONTRATO'}
                                {isEditingAuthorized && <span className="ml-3 text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium">Solo Lectura</span>}
                            </h3>
                            <button onClick={() => setAddStatementModalOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto flex flex-col">
                        {/* Form Area */}
                        <div className="p-3 md:p-6 grid grid-cols-12 gap-3 md:gap-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0">
                            {/* RUT */}
                            <div className="col-span-5 md:col-span-2">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium">R.U.T</label>
                                <input
                                    type="text"
                                    readOnly
                                    disabled={isEditingAuthorized}
                                    className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm bg-slate-50 dark:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed"
                                    value={currentForm.rut}
                                />
                            </div>

                            {/* Razon Social with Picker */}
                            <div className="col-span-7 md:col-span-4">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium uppercase">RAZÓN SOCIAL</label>
                                <div className="flex gap-1">
                                    <input
                                        type="text"
                                        readOnly
                                        disabled={isEditingAuthorized}
                                        className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm bg-slate-50 dark:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed"
                                        value={currentForm.razonSocial}
                                    />
                                    <button
                                        onClick={() => !isEditingAuthorized && setSubcontractorPickerOpen(true)}
                                        disabled={isEditingAuthorized}
                                        className="bg-slate-300 hover:bg-slate-400 text-slate-700 px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        ...
                                    </button>
                                </div>
                            </div>

                            {/* Usuarios Vinculados */}
                            <div className="col-span-12 md:col-span-6">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium uppercase">Usuarios Vinculados</label>
                                <div className="relative">
                                    <div className="border border-slate-400 dark:border-slate-600 p-1 md:p-1.5 text-xs md:text-sm bg-slate-50 dark:bg-slate-800 flex flex-wrap gap-1 min-h-[34px] md:min-h-[38px] items-center overflow-x-auto max-w-full relative group">
                                        {currentForm.linkedUserIds.length === 0 && (
                                            <span className="text-slate-400 italic px-1 absolute pointer-events-none whitespace-nowrap text-[10px] md:text-xs">Ninguno seleccionado</span>
                                        )}
                                        {currentForm.linkedUserIds.map(uid => {
                                            const u = (responsibles || []).find(gu => gu.id === uid) || (globalUsers || []).find(gu => gu.id === uid);
                                            return (
                                                <span key={uid} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded flex items-center gap-1 text-[10px] md:text-xs whitespace-nowrap">
                                                    {u?.name || u?.email || uid}
                                                    {!isEditingAuthorized && (
                                                        <span 
                                                            className="material-symbols-outlined text-[14px] cursor-pointer hover:text-red-500"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setCurrentForm({...currentForm, linkedUserIds: currentForm.linkedUserIds.filter(id => id !== uid)});
                                                            }}
                                                        >close</span>
                                                    )}
                                                </span>
                                            );
                                        })}
                                        {!isEditingAuthorized && (
                                            <select
                                                className="opacity-0 absolute inset-0 w-full h-full cursor-pointer z-10"
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val && !currentForm.linkedUserIds.includes(val)) {
                                                        setCurrentForm({...currentForm, linkedUserIds: [...currentForm.linkedUserIds, val]});
                                                    }
                                                    e.target.value = '';
                                                }}
                                            >
                                                <option value="">+ Añadir Usuario</option>
                                                {(responsibles || []).map(u => (
                                                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="text-[9px] text-slate-500 mt-0.5">Seleccione usuarios que participan en la obra para vincular al contratista.</div>
                                </div>
                            </div>

                            {/* Fechas */}
                            <div className="col-span-4 md:col-span-2">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium truncate">F. Inicio</label>
                                <div className="relative">
                                    <input
                                        type="date"
                                        disabled={isEditingAuthorized}
                                        className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm text-slate-600 disabled:opacity-70 disabled:cursor-not-allowed"
                                        value={currentForm.startDate}
                                        onChange={e => setCurrentForm({ ...currentForm, startDate: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium truncate">F. Termino</label>
                                <div className="relative">
                                    <input
                                        type="date"
                                        disabled={isEditingAuthorized}
                                        className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm text-slate-600 disabled:opacity-70 disabled:cursor-not-allowed"
                                        value={currentForm.endDate}
                                        onChange={e => setCurrentForm({ ...currentForm, endDate: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* Retencion */}
                            <div className="col-span-4 md:col-span-2 text-center">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium">%Retención</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        disabled={isEditingAuthorized}
                                        className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm text-center text-slate-600 focus:ring-2 focus:ring-primary outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                                        value={currentForm.retention}
                                        onChange={e => setCurrentForm({ ...currentForm, retention: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Toolbar Area */}
                        <div className="px-3 md:px-6 py-3 md:py-4 flex flex-col md:flex-row items-stretch md:items-center justify-between bg-white dark:bg-slate-900 gap-3 md:gap-4">
                            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-4 w-full">
                                <button
                                    onClick={() => handleOpenTaskSelector(false)}
                                    disabled={isEditingAuthorized}
                                    className="bg-[#00b050] hover:bg-[#009040] text-white px-4 py-2 md:py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                    +Añadir Partidas
                                </button>

                                <div className="relative flex-1 w-full md:max-w-lg">
                                    <input
                                        type="text"
                                        className="w-full border border-slate-400 p-1.5 pl-2 text-sm"
                                    />
                                    <span className="material-symbols-outlined absolute right-2 top-1.5 text-slate-400 text-[18px]">search</span>
                                </div>
                            </div>

                            <div className="relative w-full md:w-auto md:ml-4 shrink-0 flex items-center">
                                <button 
                                    type="button"
                                    onClick={() => setDocumentsModalOpen(true)}
                                    className={`bg-[#ffc000] hover:bg-[#e0a800] text-black px-4 py-2 md:py-1.5 text-sm font-medium transition-colors w-full h-full whitespace-nowrap rounded flex items-center justify-center gap-1 ${(currentForm.attachedDocuments?.length || 0) > 0 ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                                >
                                    <span className="material-symbols-outlined text-[18px]">attach_file</span>
                                    Documentos del Contrato ({(currentForm.attachedDocuments || []).length})
                                </button>
                            </div>
                        </div>

                        {/* Table Area */}
                        <div className="flex-none overflow-x-auto px-2 md:px-6 pb-6">
                            <div className="min-w-max">
                                <table className="w-full border border-black text-left border-collapse">
                                <thead>
                                    <tr className="bg-white">
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-8 md:w-10 text-center"></th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-16 md:w-24">Codigo</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black">Partidas</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-12 md:w-20 text-center">Unidad</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-16 md:w-24 text-center">Total Cant.</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-24 md:w-40 text-right">Valor Acumulado</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-24 md:w-40 text-right">Monto Pagado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentForm.items.length === 0 ? (
                                        <tr><td colSpan={5} className="border border-black px-2 py-8 text-center text-xs text-slate-500">No hay partidas añadidas.</td></tr>
                                    ) : (
                                        currentForm.items.map((item) => (
                                            <React.Fragment key={item.id}>
                                                <tr className="bg-white hover:bg-slate-50">
                                                    <td className="border border-black px-2 py-1 text-center">
                                                        <button
                                                            onClick={() => toggleItemExpansion(item.id)}
                                                            className="text-slate-500 hover:text-black flex items-center justify-center"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">
                                                                {item.expanded ? 'expand_less' : 'expand_more'}
                                                            </span>
                                                        </button>
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-xs">{resolveItemGanttCode(item.id, item.code)}</td>
                                                    <td className="border border-black px-2 py-1 text-xs font-bold">{item.description}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-center">{findTaskById(item.id, tasks)?.unit || '-'}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-center font-bold">
                                                        {item.details.reduce((sum, d) => sum + (parseFloat(d.quantity || '0') || 0), 0).toLocaleString()}
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-xs text-right font-medium">{item.totalValue.toLocaleString()}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-right">{item.paidAmount || ''}</td>
                                                </tr>
                                                {item.expanded && (
                                                    <tr>
                                                        <td colSpan={7} className="border border-black p-0">
                                                            <div className="bg-slate-50 p-2">
                                                                <table className="w-full text-left bg-white border border-slate-200">
                                                                    <thead className="bg-slate-100 text-xs text-slate-500">
                                                                        <tr>
                                                                            <th className="p-2 border-b">Nivel</th>
                                                                            <th className="p-2 border-b">Unidad Física</th>
                                                                            <th className="p-2 border-b text-center">Cantidad</th>
                                                                            <th className="p-2 border-b text-right">Valor Unitario</th>
                                                                            <th className="p-2 border-b text-right">Valor</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {item.details.map((detail, dIdx) => (
                                                                            <tr key={dIdx} className="border-b border-slate-100 last:border-0 text-xs text-slate-600">
                                                                                <td className="p-2">{levels.find(l => l.id === units.find(u => u.id === detail.unitId)?.levelId)?.name || "-"}</td>
                                                                                <td className="p-2">{detail.unitName}</td>
                                                                                <td className="p-2 text-center">{detail.quantity || '0'}</td>
                                                                                <td className="p-2 text-right font-mono">{detail.unitPrice ? `$${parseInt(detail.unitPrice).toLocaleString()}` : '-'}</td>
                                                                                <td className="p-2 text-right font-mono">${detail.value.toLocaleString()}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    )}
                                </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Flujo de Firmas (Iconstruye-style) */}
                    <div className="mx-6 my-2 p-2 bg-slate-50 dark:bg-slate-800/40 rounded-lg border border-slate-205 dark:border-slate-700">
                        <h4 className="text-[11px] font-black text-slate-850 dark:text-slate-200 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <span className="material-symbols-outlined text-[16px] text-blue-600">verified</span>
                            Aprobaciones
                        </h4>
                        <div className="flex flex-wrap md:flex-nowrap items-center justify-start md:justify-center gap-2 md:gap-4 overflow-x-auto py-1">
                            {approvalFlows.subcontracts.map((step, idx) => {
                                const reviewer = responsibles.find(r => r.id === step.reviewerId);
                                if (!reviewer) return null;
                                
                                const signature = currentForm.signatures?.[reviewer.id];
                                
                                // It is their turn if all previous reviewers in the flow have signed
                                const isTheirTurn = approvalFlows.subcontracts.slice(0, idx).every(prevStep => 
                                    currentForm.signatures?.[prevStep.reviewerId]
                                );
                                
                                const statusText = signature ? "Aprobó" : "Aun no aprueba";
                                const statusColorClass = signature ? "text-green-650 dark:text-green-400" : "text-orange-500";
                                
                                return (
                                    <React.Fragment key={idx}>
                                        {idx > 0 && (
                                            <div className="flex items-center justify-center shrink-0 text-slate-400 dark:text-slate-600">
                                                <span className="material-symbols-outlined text-[18px]">arrow_circle_right</span>
                                            </div>
                                        )}
                                        <div className="flex flex-col items-center text-center p-1 min-w-[120px] relative">
                                            <div className={`text-[9px] font-black uppercase tracking-wide ${statusColorClass}`}>
                                                {idx === 0 ? 'Solicitante' : 'Aprobador'}
                                            </div>
                                            <div className="text-xs font-extrabold text-slate-900 dark:text-slate-100">
                                                {reviewer.name}
                                            </div>
                                            <div className="text-[9px] text-slate-400 dark:text-slate-500 leading-none">
                                                {reviewer.role}
                                            </div>
                                            
                                            <div className="mt-1.5 min-h-[32px] flex flex-col items-center justify-center">
                                                {signature ? (
                                                    <div className="flex flex-col items-center">
                                                        <img src={signature} alt="Firma" className="max-h-[24px] object-contain mix-blend-multiply dark:brightness-90" />
                                                        <span className="text-[8px] text-green-655 dark:text-green-400 font-black">Aprobó</span>
                                                    </div>
                                                ) : isTheirTurn ? (
                                                    <button
                                                        onClick={() => handleOpenSignatureModalForContract(reviewer.id)}
                                                        className="px-3 py-0.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full text-[10px] font-extrabold shadow-sm transition-all"
                                                    >
                                                        Aprobar
                                                    </button>
                                                ) : (
                                                    <span className="text-[10px] text-orange-500 font-bold italic">Aun no aprueba</span>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                            {approvalFlows.subcontracts.length === 0 && (
                                <div className="col-span-full py-1 text-center text-xs text-slate-400 italic">
                                    No se han configurado firmas.
                                </div>
                            )}
                        </div>
                    </div>

                        {/* Footer Actions for the Modal */}
                        <div className="p-4 bg-slate-100 dark:bg-slate-800 flex justify-end gap-3 border-t border-slate-300">
                            {/* Autorizar button removed based on user request */}
                            <button onClick={() => setAddStatementModalOpen(false)} className="px-4 py-2 bg-white border border-slate-400 text-sm font-medium hover:bg-slate-50">Cancelar</button>
                            {!isEditingAuthorized && (() => {
                                const isContractValid = Boolean(currentForm.subcontractorId && currentForm.items.length > 0 && currentForm.rut && currentForm.razonSocial && currentForm.startDate && currentForm.endDate && currentForm.retention !== undefined && ((currentForm as any).signedContractUrl || (currentForm.attachedDocuments && currentForm.attachedDocuments.length > 0)));
                                const isDraft = currentForm.subcontractorId || currentForm.items.length > 0;
                                
                                if (isContractValid) {
                                    return (
                                        <button onClick={handleSaveContract} className="px-6 py-2 bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">{currentForm.id ? 'Guardar Cambios' : 'Habilitar Contratista'}</button>
                                    );
                                } else if (isDraft) {
                                    return (
                                        <button onClick={handleSaveContract} className="px-6 py-2 bg-amber-500 text-white text-sm font-bold hover:bg-amber-600">Guardado Campos Ingresados</button>
                                    );
                                }
                                return (
                                    <button disabled className="px-6 py-2 bg-slate-300 text-white text-sm font-bold cursor-not-allowed">{currentForm.id ? 'Guardar Cambios' : 'Habilitar Contratista'}</button>
                                );
                            })()}
                        </div>
                    </div>
                </div>
                )
            )}

            {/* Picker: Select Subcontractor for the Form */}
            {subcontractorPickerOpen && (
                isMobile ? (
                    <MobileSubcontractorPickerSheet
                        open={subcontractorPickerOpen}
                        onClose={() => setSubcontractorPickerOpen(false)}
                        subcontractors={subcontractors}
                        onPickSubcontractor={handlePickSubcontractor}
                    />
                ) : (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded shadow-xl w-[500px] border border-slate-300 p-4">
                        <h3 className="font-bold mb-4 text-sm uppercase">Seleccionar Subcontratista</h3>
                        <div className="max-h-[300px] overflow-y-auto border border-slate-200 mb-4">
                            {subcontractors.map(sub => (
                                <div
                                    key={sub.id}
                                    onClick={() => handlePickSubcontractor(sub)}
                                    className="p-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 text-sm"
                                >
                                    <div className="font-bold">{sub.razonSocial}</div>
                                    <div className="text-xs text-slate-500">{sub.rut}</div>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setSubcontractorPickerOpen(false)} className="w-full border py-1 text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
                    </div>
                </div>
                )
            )}

            {/* STEP 1: Task Selector (List of Budget Items) */}
            {taskSelectorOpen && (
                isMobile ? (
                    <MobileTaskSelectorSheet
                        open={taskSelectorOpen}
                        onClose={() => setTaskSelectorOpen(false)}
                        tasks={tasks}
                        onSelectTask={(task, code) => handleTaskSelect(task, code)}
                        selectedTaskId={selectedTaskForAdd?.id}
                        currentFormItemIds={(currentForm.items || []).map(i => i.id)}
                    />
                ) : (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded shadow-xl w-full max-w-2xl border border-slate-300 p-4 max-h-[85vh] flex flex-col">
                        <h3 className="font-bold mb-4 text-sm uppercase flex items-center gap-2 shrink-0">
                            <span className="material-symbols-outlined text-[18px]">list_alt</span>
                            Seleccionar Partida
                        </h3>
                        <div className="flex-1 overflow-auto border border-slate-200 mb-4 rounded bg-white min-h-[300px]">
                            <div className="min-w-max">
                                {renderTaskSelectorItems(tasks)}
                            </div>
                        </div>
                        <button onClick={() => setTaskSelectorOpen(false)} className="w-full border py-2 text-sm font-bold bg-slate-100 hover:bg-slate-200 rounded shrink-0 transition-colors">Cancelar</button>
                    </div>
                </div>
                )
            )}

            {/* STEP 2: Unit Selector (The Requested Image) */}
            {unitSelectorOpen && selectedTaskForAdd && (() => {
                const validUnits = getValidUnitsForTask(selectedTaskForAdd.id);
                const totalSelected = tempSelectedUnits.reduce((acc, uid) => {
                    const u = validUnits.find(v => v.id === uid);
                    return acc + (u?.value || 0);
                }, 0);
                const allSelected = validUnits.length > 0 && validUnits.every(u => tempSelectedUnits.includes(u.id));
                const someSelected = validUnits.some(u => tempSelectedUnits.includes(u.id)) && !allSelected;

                const handleToggleAll = () => {
                    if (allSelected) {
                        setTempSelectedUnits([]);
                    } else {
                        setTempSelectedUnits(validUnits.map(u => u.id));
                    }
                };

                if (isMobile) {
                    return (
                        <MobileUnitSelectorSheet
                            open={unitSelectorOpen}
                            onClose={() => setUnitSelectorOpen(false)}
                            task={selectedTaskForAdd}
                            validUnits={validUnits}
                            levels={levels}
                            tempSelectedUnits={tempSelectedUnits}
                            onToggleUnit={toggleUnitSelection}
                            onToggleAll={handleToggleAll}
                            onConfirm={handleConfirmUnits}
                            isUnitAlreadyContracted={(unitId, taskId) => isUnitAlreadyContracted(unitId, taskId, currentForm.id)}
                        />
                    );
                }

                return (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-0 md:p-4">
                        <div className="bg-white dark:bg-slate-900 rounded-none md:rounded-lg shadow-xl w-full max-w-3xl border-0 md:border border-slate-300 flex flex-col h-full md:h-auto md:max-h-[80vh]">
                            <div className="p-3 md:p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
                                <div>
                                    <h3 className="font-bold text-xs md:text-sm uppercase text-slate-700">Partida: {selectedTaskForAdd.name}</h3>
                                    <p className="text-[10px] md:text-xs text-slate-500">Código: {selectedTaskForAdd.code}</p>
                                </div>
                                <button onClick={() => setUnitSelectorOpen(false)} className="text-slate-400 hover:text-black"><span className="material-symbols-outlined">close</span></button>
                            </div>

                            {/* Select All Bar */}
                            {validUnits.length > 0 && (
                                <div className="px-3 md:px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2 md:gap-3 shrink-0">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={el => { if (el) el.indeterminate = someSelected; }}
                                        onChange={handleToggleAll}
                                        className="rounded border-slate-300 text-primary focus:ring-primary size-4 cursor-pointer"
                                        id="select-all-units"
                                    />
                                    <label htmlFor="select-all-units" className="text-[10px] md:text-xs font-semibold text-blue-700 cursor-pointer select-none">
                                        {allSelected ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
                                        <span className="ml-1 md:ml-2 text-blue-400 font-normal">({tempSelectedUnits.length}/{validUnits.length} seleccionadas)</span>
                                    </label>
                                </div>
                            )}

                            <div className="flex-1 overflow-x-auto overflow-y-auto p-0">
                                <div className="min-w-max">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-white sticky top-0 shadow-sm z-10">
                                        <tr>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold w-8 md:w-12 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    onChange={handleToggleAll}
                                                    className="rounded border-slate-300 text-primary focus:ring-primary size-3 md:size-4 cursor-pointer"
                                                />
                                            </th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-slate-500 uppercase">Nivel</th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-slate-500 uppercase">Unidad Física</th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-center text-slate-500 uppercase">Cantidad</th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-right text-slate-500 uppercase">Valor Unit.</th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-right text-slate-500 uppercase">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {validUnits.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="p-8 text-center">
                                                    <div className="flex flex-col items-center gap-2 text-slate-500">
                                                        <span className="material-symbols-outlined text-4xl text-slate-300">money_off</span>
                                                        <span className="text-sm font-medium">No se han asociado presupuestos</span>
                                                        <p className="text-xs max-w-xs">Debe ingresar valores en la columna "Estados de Pago" del módulo EDT para esta partida.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            validUnits.map(unit => {
                                                const levelName = levels.find(l => l.id === unit.levelId)?.name || '-';
                                                const alreadyInAnotherContract = isUnitAlreadyContracted(unit.id, selectedTaskForAdd.id, currentForm.id);
                                                return (
                                                    <tr key={unit.id} className={`hover:bg-blue-50 transition-colors ${tempSelectedUnits.includes(unit.id) ? 'bg-blue-50/50' : ''} ${alreadyInAnotherContract ? 'opacity-50 bg-slate-100' : ''}`}>
                                                        <td className="p-1.5 md:p-3 border-b text-center relative">
                                                            <input
                                                                type="checkbox"
                                                                checked={alreadyInAnotherContract || tempSelectedUnits.includes(unit.id)}
                                                                onChange={() => toggleUnitSelection(unit.id)}
                                                                disabled={alreadyInAnotherContract}
                                                                className="rounded border-slate-300 text-primary focus:ring-primary size-3 md:size-4 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                            />
                                                            {alreadyInAnotherContract && (
                                                                <span className="absolute left-1/2 -translate-x-1/2 bottom-0.5 md:-bottom-2 text-[6px] md:text-[8px] font-bold text-red-500 uppercase tracking-tighter whitespace-nowrap pointer-events-none">Asignado</span>
                                                            )}
                                                        </td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm text-slate-500">{levelName}</td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm font-medium">
                                                            {unit.name}
                                                        </td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm text-center">{unit.quantity}</td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm text-right font-mono">{unit.unitPrice ? `$${parseInt(unit.unitPrice).toLocaleString()}` : '-'}</td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm text-right font-mono">${unit.value.toLocaleString()}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                                </div>
                            </div>

                            <div className="p-3 md:p-4 bg-slate-50 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-2 md:gap-0 shrink-0">
                                <div className="text-[10px] md:text-sm w-full md:w-auto text-center md:text-left">
                                    <span className="text-slate-500 mr-2">Total Seleccionado:</span>
                                    <span className="font-bold font-mono text-base md:text-lg">${totalSelected.toLocaleString()}</span>
                                </div>
                                <div className="flex gap-2 w-full md:w-auto">
                                    <button onClick={() => setUnitSelectorOpen(false)} className="flex-1 md:flex-none px-3 py-1.5 text-[10px] md:text-sm bg-white border border-slate-300 rounded hover:bg-slate-100">Cancelar</button>
                                    <button
                                        onClick={handleConfirmUnits}
                                        disabled={validUnits.length === 0}
                                        className={`flex-1 md:flex-none px-4 py-1.5 text-[10px] md:text-sm rounded font-bold shadow-sm ${validUnits.length === 0 ? 'bg-slate-300 text-white cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}



            {/* --- ANEXOS MODALS --- */}
            
            {/* Anexos List Modal */}
            {anexosListModalOpen && selectedContract && (
                isMobile ? (
                    <MobileContractAnnexesSheet
                        open={anexosListModalOpen}
                        onClose={() => setAnexosListModalOpen(false)}
                        contract={selectedContract}
                        subcontractor={getSubcontractorById(selectedContract.subcontractorId)}
                        onCreateAnexo={handleCreateAnexo}
                        onEditAnexo={handleEditAnexo}
                        canCreateAnexo={hasPermission('sc_anexo', 'edit')}
                    />
                ) : (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-5xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[80vh]">
                            <div className="bg-blue-600 p-4 flex justify-between items-center">
                                <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                                    ANEXOS DE CONTRATO - {getSubcontractorById(selectedContract.subcontractorId)?.razonSocial}
                                </h3>
                            <button onClick={() => setAnexosListModalOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 flex justify-between items-center">
                            <div>
                                <span className="font-bold text-slate-700">Contrato Base:</span> <span className="font-mono text-slate-600">${selectedContract.totalAmount.toLocaleString()}</span>
                            </div>
                            {hasPermission('sc_anexo', 'edit') && (
                                <button
                                    onClick={handleCreateAnexo}
                                    className="bg-primary text-white px-4 py-2 rounded font-bold shadow-sm hover:bg-blue-600 flex items-center gap-2 text-sm"
                                >
                                    <span className="material-symbols-outlined text-[18px]">add</span>
                                    Nuevo Anexo
                                </button>
                            )}
                        </div>

                        <div className="flex-1 overflow-auto p-6 bg-white dark:bg-slate-900">
                            {!selectedContract.anexos || selectedContract.anexos.length === 0 ? (
                                <div className="text-center py-12 text-slate-500">
                                    <span className="material-symbols-outlined text-5xl opacity-30 mb-2">post_add</span>
                                    <p className="text-lg">No hay anexos registrados para este contrato.</p>
                                </div>
                            ) : (
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Número</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Fecha</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Descripción</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-right">Monto Adicional</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-center">Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {selectedContract.anexos.map(anexo => (
                                            <tr key={anexo.id} onClick={() => handleEditAnexo(anexo)} className="cursor-pointer hover:bg-slate-50 transition-colors">
                                                <td className="px-4 py-3 font-bold text-slate-800 text-sm">{anexo.numero}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{anexo.fecha}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{anexo.description || '-'}</td>
                                                <td className="px-4 py-3 font-mono font-bold text-right text-slate-800 text-sm">${anexo.totalAmount.toLocaleString()}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide
                                                        ${anexo.status === 'Borrador' ? 'bg-blue-100 text-blue-700' :
                                                            anexo.status === 'Anulado' ? 'bg-red-100 text-red-700' :
                                                                'bg-green-100 text-green-700'}
                                                    `}>{anexo.status}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
                )
            )}

            {/* Anexo Form Modal (Create/Edit) */}
            {anexoFormModalOpen && selectedContract && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-none shadow-2xl w-full max-w-7xl border border-slate-300 dark:border-slate-700 flex flex-col h-[80vh] overflow-hidden">
                        <div className="bg-blue-600 px-6 py-3 flex justify-between items-center shrink-0">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                                {currentAnexoForm.id ? 'EDITAR ANEXO' : 'AÑADIR ANEXO'}
                                {currentAnexoForm.status === 'Activo' && <span className="ml-3 text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium">Solo Lectura</span>}
                            </h3>
                            <button onClick={() => setAnexoFormModalOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-6 grid grid-cols-12 gap-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0">
                            <div className="col-span-12 md:col-span-3">
                                <label className="block text-xs text-slate-800 mb-1 font-medium">Número de Anexo</label>
                                <input
                                    type="text"
                                    disabled={currentAnexoForm.status === 'Activo'}
                                    className="w-full border border-slate-400 p-1.5 text-sm bg-white disabled:bg-slate-100"
                                    value={currentAnexoForm.numero}
                                    onChange={e => setCurrentAnexoForm({ ...currentAnexoForm, numero: e.target.value })}
                                />
                            </div>
                            <div className="col-span-12 md:col-span-3">
                                <label className="block text-xs text-slate-800 mb-1 font-medium">Fecha Aprobación</label>
                                <input
                                    type="date"
                                    disabled={currentAnexoForm.status === 'Activo'}
                                    className="w-full border border-slate-400 p-1.5 text-sm bg-white disabled:bg-slate-100"
                                    value={currentAnexoForm.fecha}
                                    onChange={e => setCurrentAnexoForm({ ...currentAnexoForm, fecha: e.target.value })}
                                />
                            </div>
                            <div className="col-span-12 md:col-span-6">
                                <label className="block text-xs text-slate-800 mb-1 font-medium">Descripción breve</label>
                                <input
                                    type="text"
                                    disabled={currentAnexoForm.status === 'Activo'}
                                    className="w-full border border-slate-400 p-1.5 text-sm bg-white disabled:bg-slate-100"
                                    placeholder="Ej: Obras extraordinarias por cambio de trazado"
                                    value={currentAnexoForm.description}
                                    onChange={e => setCurrentAnexoForm({ ...currentAnexoForm, description: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="px-6 py-4 flex items-center justify-between bg-white dark:bg-slate-900">
                            <button
                                onClick={() => handleOpenTaskSelector(true)}
                                disabled={currentAnexoForm.status === 'Activo'}
                                className="bg-[#00b050] hover:bg-[#009040] text-white px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                +Añadir Partidas al Anexo
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto px-6 pb-6">
                            <table className="w-full border border-black text-left border-collapse">
                                <thead>
                                    <tr className="bg-white">
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-10 text-center"></th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-24">Codigo</th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black">Partidas</th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-20 text-center">Unidad</th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-24 text-center">Cant. Ajuste</th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-40 text-right">Valor Ajuste</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* --- ORIGINAL / BASE CONTRACT ITEMS (FOR ELIMINATION & GRANULAR UNIT CONTROL) --- */}
                                    {selectedContract.items.map(baseItem => {
                                        const adjustmentItem = currentAnexoForm.items?.find(i => i.id === baseItem.id);
                                        const eliminatedUnitIds: string[] = (adjustmentItem as any)?.eliminatedUnitIds || [];
                                        const isWholePartidaEliminated = adjustmentItem?.totalValue === -baseItem.totalValue || (eliminatedUnitIds.length === baseItem.details.length && baseItem.details.length > 0);
                                        
                                        const activeUnits = baseItem.details.filter(d => !eliminatedUnitIds.includes(d.unitId));
                                        const eliminatedUnits = baseItem.details.filter(d => eliminatedUnitIds.includes(d.unitId));
                                        const isExpanded = expandedAnexoItemIds.has(baseItem.id);

                                        return (
                                            <React.Fragment key={`base-${baseItem.id}`}>
                                                <tr className={`bg-slate-50 dark:bg-slate-800/60 ${isWholePartidaEliminated ? 'opacity-60' : ''}`}>
                                                    <td className="border border-black dark:border-slate-700 px-2 py-1 text-center">
                                                        <button
                                                            onClick={() => toggleAnexoItemExpansion(baseItem.id)}
                                                            className="text-slate-600 dark:text-slate-300 hover:text-black dark:hover:text-white flex items-center justify-center mx-auto cursor-pointer"
                                                            title="Desplegar unidades físicas"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">
                                                                {isExpanded ? 'expand_less' : 'expand_more'}
                                                            </span>
                                                        </button>
                                                    </td>
                                                    <td className="border border-black dark:border-slate-700 px-2 py-1 text-xs text-slate-500 font-mono">{resolveItemGanttCode(baseItem.id, baseItem.code)}</td>
                                                    <td className="border border-black dark:border-slate-700 px-2 py-1 text-xs">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-slate-700 dark:text-slate-200">{baseItem.description}</span>
                                                            {eliminatedUnitIds.length > 0 && !isWholePartidaEliminated && (
                                                                <span className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded font-bold">
                                                                    {eliminatedUnits.length} {eliminatedUnits.length === 1 ? 'Unidad Eliminada' : 'Unidades Eliminadas'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="border border-black dark:border-slate-700 px-2 py-1 text-xs text-center text-slate-500">{findTaskById(baseItem.id, tasks)?.unit || '-'}</td>
                                                    <td className="border border-black dark:border-slate-700 px-2 py-1 text-xs text-center font-bold">
                                                        {isWholePartidaEliminated ? (
                                                            <span className="text-rose-600 font-extrabold">ELIMINADA</span>
                                                        ) : eliminatedUnits.length > 0 ? (
                                                            <span className="text-rose-600">-{eliminatedUnits.length} U.F.</span>
                                                        ) : (
                                                            <span className="text-slate-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className={`border border-black dark:border-slate-700 px-2 py-1 text-xs text-right font-mono font-bold ${adjustmentItem && adjustmentItem.totalValue < 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                                                        {adjustmentItem && adjustmentItem.totalValue < 0 ? `-${Math.abs(adjustmentItem.totalValue).toLocaleString()}` : '$0'}
                                                    </td>
                                                </tr>

                                                {/* Sub-tabla de desgloses de Unidades Físicas (Trazabilidad Activas vs Eliminadas) */}
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan={6} className="border border-black dark:border-slate-700 p-3 bg-slate-100/80 dark:bg-slate-900/80">
                                                            <div className="flex flex-col gap-3">

                                                                {/* Sección 1: Unidades Físicas Activas */}
                                                                <div className="bg-emerald-50/60 dark:bg-emerald-900/20 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800/40">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <span className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 uppercase flex items-center gap-1">
                                                                            <span className="material-symbols-outlined text-[16px] text-emerald-600">check_circle</span>
                                                                            Unidades Físicas Activas en Contrato ({activeUnits.length})
                                                                        </span>
                                                                    </div>
                                                                    {activeUnits.length === 0 ? (
                                                                        <p className="text-[11px] text-slate-400 italic">No hay unidades activas (todas han sido eliminadas por este anexo).</p>
                                                                    ) : (
                                                                        <table className="w-full text-left bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-800 text-xs rounded-lg overflow-hidden">
                                                                            <thead className="bg-emerald-100/60 dark:bg-emerald-950/40 text-[10px] text-emerald-900 dark:text-emerald-200 font-bold uppercase">
                                                                                <tr>
                                                                                    <th className="p-2 border-b border-emerald-200 dark:border-emerald-800">Nivel</th>
                                                                                    <th className="p-2 border-b border-emerald-200 dark:border-emerald-800">Unidad Física</th>
                                                                                    <th className="p-2 border-b border-emerald-200 dark:border-emerald-800 text-center">Cantidad</th>
                                                                                    <th className="p-2 border-b border-emerald-200 dark:border-emerald-800 text-right">Val. Unitario</th>
                                                                                    <th className="p-2 border-b border-emerald-200 dark:border-emerald-800 text-right">Valor Total</th>
                                                                                    <th className="p-2 border-b border-emerald-200 dark:border-emerald-800 text-center">Acción</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {activeUnits.map(d => (
                                                                                    <tr key={d.unitId} className="border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">
                                                                                        <td className="p-2">{levels.find(l => l.id === units.find(u => u.id === d.unitId)?.levelId)?.name || "-"}</td>
                                                                                        <td className="p-2 font-medium">{d.unitName}</td>
                                                                                        <td className="p-2 text-center font-bold text-emerald-600">{d.quantity}</td>
                                                                                        <td className="p-2 text-right font-mono">${parseInt(d.unitPrice || '0').toLocaleString()}</td>
                                                                                        <td className="p-2 text-right font-mono font-bold text-emerald-700 dark:text-emerald-400">${d.value.toLocaleString()}</td>
                                                                                        <td className="p-2 text-center">
                                                                                            <button
                                                                                                onClick={() => handleToggleEliminateUnitInAnexo(baseItem, d.unitId)}
                                                                                                className="px-2 py-0.5 bg-rose-600 hover:bg-rose-700 text-white rounded text-[10px] font-bold uppercase cursor-pointer"
                                                                                                title="Eliminar esta unidad del contrato mediante el anexo"
                                                                                            >
                                                                                                Eliminar Unidad
                                                                                            </button>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </div>

                                                                {/* Sección 2: Unidades Físicas Eliminadas por este Anexo */}
                                                                {eliminatedUnits.length > 0 && (
                                                                    <div className="bg-rose-50/60 dark:bg-rose-900/20 p-3 rounded-xl border border-rose-200 dark:border-rose-800/40">
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <span className="text-[11px] font-bold text-rose-800 dark:text-rose-300 uppercase flex items-center gap-1">
                                                                                <span className="material-symbols-outlined text-[16px] text-rose-600">cancel</span>
                                                                                Unidades Físicas Eliminadas por este Anexo ({eliminatedUnits.length})
                                                                            </span>
                                                                        </div>
                                                                        <table className="w-full text-left bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-800 text-xs rounded-lg overflow-hidden">
                                                                            <thead className="bg-rose-100/60 dark:bg-rose-950/40 text-[10px] text-rose-900 dark:text-rose-200 font-bold uppercase">
                                                                                <tr>
                                                                                    <th className="p-2 border-b border-rose-200 dark:border-rose-800">Nivel</th>
                                                                                    <th className="p-2 border-b border-rose-200 dark:border-rose-800">Unidad Física</th>
                                                                                    <th className="p-2 border-b border-rose-200 dark:border-rose-800 text-center">Cant. Eliminada</th>
                                                                                    <th className="p-2 border-b border-rose-200 dark:border-rose-800 text-right">Val. Unitario</th>
                                                                                    <th className="p-2 border-b border-rose-200 dark:border-rose-800 text-right">Monto Descuento</th>
                                                                                    <th className="p-2 border-b border-rose-200 dark:border-rose-800 text-center">Acción</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {eliminatedUnits.map(d => (
                                                                                    <tr key={d.unitId} className="border-b border-slate-100 dark:border-slate-800 text-rose-800 dark:text-rose-300 bg-rose-50/30 dark:bg-rose-900/10">
                                                                                        <td className="p-2 line-through opacity-70">{levels.find(l => l.id === units.find(u => u.id === d.unitId)?.levelId)?.name || "-"}</td>
                                                                                        <td className="p-2 font-medium line-through">{d.unitName}</td>
                                                                                        <td className="p-2 text-center font-bold text-rose-600">-{d.quantity}</td>
                                                                                        <td className="p-2 text-right font-mono">${parseInt(d.unitPrice || '0').toLocaleString()}</td>
                                                                                        <td className="p-2 text-right font-mono font-bold text-rose-600">-${d.value.toLocaleString()}</td>
                                                                                        <td className="p-2 text-center">
                                                                                            <button
                                                                                                onClick={() => handleToggleEliminateUnitInAnexo(baseItem, d.unitId)}
                                                                                                className="px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-bold uppercase cursor-pointer"
                                                                                                title="Restaurar esta unidad al contrato"
                                                                                            >
                                                                                                Restaurar Unidad
                                                                                            </button>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                )}

                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* --- NEW/ADDED ITEMS IN THIS ANEXO --- */}
                                    {currentAnexoForm.items?.filter(i => !selectedContract.items.some(bi => bi.id === i.id)).length === 0 && selectedContract.items.length === 0 && (
                                        <tr><td colSpan={6} className="border border-black px-2 py-8 text-center text-xs text-slate-500">No hay partidas en este anexo.</td></tr>
                                    )}
                                    {currentAnexoForm.items?.filter(i => !selectedContract.items.some(bi => bi.id === i.id)).map((item) => (
                                        <React.Fragment key={`added-${item.id}`}>
                                            <tr className="bg-white hover:bg-slate-50">
                                                    <td className="border border-black px-2 py-1 text-center">
                                                        <button
                                                            onClick={() => toggleItemExpansion(item.id, true)}
                                                            className="text-slate-500 hover:text-black flex items-center justify-center"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">
                                                                {item.expanded ? 'expand_less' : 'expand_more'}
                                                            </span>
                                                        </button>
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-xs">{resolveItemGanttCode(item.id, item.code)}</td>
                                                    <td className="border border-black px-2 py-1 text-xs font-bold">{item.description}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-center">{findTaskById(item.id, tasks)?.unit || '-'}</td>
                                                    <td className={`border border-black px-2 py-1 text-xs text-center font-bold ${item.totalValue < 0 ? 'text-red-700' : 'text-green-700'}`}>
                                                        {item.totalValue < 0 ? '-' : '+'}
                                                        {Math.abs(item.details.reduce((sum, d) => sum + (parseFloat(d.quantity || '0') || 0), 0)).toLocaleString()}
                                                    </td>
                                                    <td className={`border border-black px-2 py-1 text-xs text-right font-medium ${item.totalValue < 0 ? 'text-red-700' : 'text-green-700'}`}>
                                                        {item.totalValue < 0 ? '-' : '+'}
                                                        {Math.abs(item.totalValue).toLocaleString()}
                                                    </td>
                                                </tr>
                                                {item.expanded && (
                                                    <tr>
                                                        <td colSpan={6} className="border border-black p-0">
                                                            <div className="bg-slate-50 p-2">
                                                                <table className="w-full text-left bg-white border border-slate-200">
                                                                    <thead className="bg-slate-100 text-xs text-slate-500">
                                                                        <tr>
                                                                            <th className="p-2 border-b">Nivel</th>
                                                                            <th className="p-2 border-b">Unidad Física</th>
                                                                            <th className="p-2 border-b text-center">Cantidad a Agregar</th>
                                                                            <th className="p-2 border-b text-right">Valor Unitario</th>
                                                                            <th className="p-2 border-b text-right">Valor Aumento</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {item.details.map((detail, dIdx) => (
                                                                            <tr key={dIdx} className="border-b border-slate-100 last:border-0 text-xs text-slate-600">
                                                                                <td className="p-2">{levels.find(l => l.id === units.find(u => u.id === detail.unitId)?.levelId)?.name || "-"}</td>
                                                                                <td className="p-2">{detail.unitName}</td>
                                                                                <td className="p-2 text-center text-green-600 font-bold">+{detail.quantity || '0'}</td>
                                                                                <td className="p-2 text-right font-mono">{detail.unitPrice ? `$${parseInt(detail.unitPrice).toLocaleString()}` : '-'}</td>
                                                                                <td className="p-2 text-right font-mono font-bold text-green-600">+${detail.value.toLocaleString()}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                            </table>
                        </div>

                        <div className="p-4 bg-slate-100 flex justify-end gap-3 border-t border-slate-300">
                            {currentAnexoForm.status !== 'Activo' && currentAnexoForm.id && (
                                <button onClick={handleAuthorizeAnexo} className="px-6 py-2 bg-green-600 text-white text-sm font-bold hover:bg-green-700 mr-auto shadow-sm">Autorizar Anexo</button>
                            )}
                            <button onClick={() => setAnexoFormModalOpen(false)} className="px-4 py-2 bg-white border border-slate-400 text-sm font-medium hover:bg-slate-50">Cancelar</button>
                            {currentAnexoForm.status !== 'Activo' && (
                                <button onClick={handleSaveAnexo} className="px-6 py-2 bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">Guardar Anexo</button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {subcontractTypeModalOpen && renderSubcontractTypeModal()}
            {epDetailModalOpen && renderEPDetailModal()}
            {/* Documents Modal */}
            {documentsModalOpen && (
                isMobile ? (
                    <MobileContractDocumentsSheet
                        open={documentsModalOpen}
                        onClose={() => setDocumentsModalOpen(false)}
                        attachedDocuments={currentForm.attachedDocuments || []}
                        onAddDocument={(docName) => {
                            setCurrentForm({
                                ...currentForm,
                                attachedDocuments: [...(currentForm.attachedDocuments || []), docName]
                            });
                        }}
                        onRemoveDocument={(idx) => {
                            const newDocs = [...(currentForm.attachedDocuments || [])];
                            newDocs.splice(idx, 1);
                            setCurrentForm({ ...currentForm, attachedDocuments: newDocs });
                        }}
                        isEditingAuthorized={isEditingAuthorized}
                    />
                ) : (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-lg border border-slate-300 dark:border-slate-700 overflow-hidden flex flex-col">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                                <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-blue-500">description</span>
                                    Documentos del Contrato
                                </h3>
                                <button onClick={() => setDocumentsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-4 overflow-y-auto max-h-[60vh]">
                                {(!currentForm.attachedDocuments || currentForm.attachedDocuments.length === 0) ? (
                                    <div className="text-center py-8 text-slate-400 italic">No hay documentos adjuntos.</div>
                                ) : (
                                    <ul className="space-y-2">
                                        {currentForm.attachedDocuments.map((doc, idx) => (
                                            <li key={idx} className="flex justify-between items-center bg-slate-50 dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700">
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    <span className="material-symbols-outlined text-slate-400">insert_drive_file</span>
                                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{doc}</span>
                                                </div>
                                                {!isEditingAuthorized && (
                                                    <button 
                                                        title="Eliminar documento"
                                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 p-1.5 rounded transition-colors"
                                                        onClick={() => {
                                                            const newDocs = [...currentForm.attachedDocuments];
                                                            newDocs.splice(idx, 1);
                                                            setCurrentForm({ ...currentForm, attachedDocuments: newDocs });
                                                        }}
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">delete</span>
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                
                                {!isEditingAuthorized && (
                                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                        <div className="relative overflow-hidden rounded bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-600 border-dashed w-full">
                                            <input 
                                                type="file" 
                                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10" 
                                                title="Agregar más archivos..." 
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files.length > 0) {
                                                        setCurrentForm({ 
                                                            ...currentForm, 
                                                            attachedDocuments: [...(currentForm.attachedDocuments || []), e.target.files[0].name] 
                                                        });
                                                    }
                                                }}
                                            />
                                            <div className="flex items-center justify-center gap-2 py-3 text-sm font-medium text-slate-600 dark:text-slate-400 cursor-pointer">
                                                <span className="material-symbols-outlined text-[20px]">add_circle</span>
                                                Adjuntar archivo
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                                <button onClick={() => setDocumentsModalOpen(false)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded shadow-sm transition-colors">
                                    Guardar y Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {signatureTarget && (
                <SignatureModal
                    isOpen={Boolean(signatureTarget)}
                    onClose={() => setSignatureTarget(null)}
                    onSave={(signatureData) => handleSaveSignature(signatureData)}
                />
            )}

            {confirmApproveTarget && (
                isMobile ? (
                    <KlDialog
                        open={!!confirmApproveTarget}
                        onClose={() => setConfirmApproveTarget(null)}
                        title="¿Aprobar Estado de Pago?"
                        message={`Se registrará la aprobación de ${confirmApproveTarget.reviewerName} y avanzará al siguiente aprobador en el flujo.`}
                        confirmText="Aprobar"
                        cancelText="Cancelar"
                        onConfirm={handleConfirmApproval}
                    />
                ) : (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col p-6 text-center">
                            <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="material-symbols-outlined text-3xl">help_outline</span>
                            </div>
                            <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-2">
                                ¿Seguro que desea aprobar este Estado de Pago?
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 mb-6">
                                Se registrará la aprobación de <strong className="text-slate-800 dark:text-slate-200">{confirmApproveTarget.reviewerName}</strong> y avanzará al siguiente aprobador en el flujo.
                            </p>
                            <div className="flex items-center justify-center gap-3">
                                <button
                                    onClick={() => setConfirmApproveTarget(null)}
                                    className="px-5 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-bold text-xs transition-colors cursor-pointer"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleConfirmApproval}
                                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                    Aprobar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {confirmRejectTarget && (
                isMobile ? (
                    <KlDialog
                        open={!!confirmRejectTarget}
                        onClose={() => setConfirmRejectTarget(null)}
                        title="¿Rechazar Estado de Pago?"
                        message={confirmRejectTarget.idx === 0 
                            ? "El Estado de Pago y la Hoja de Ruta cambiarán a estado Rechazado y volverán a estar en modo Edición para sus correcciones."
                            : "La aprobación del paso anterior será revocada y el flujo de firmas retrocederá al aprobador anterior."}
                        danger={true}
                        confirmText="Rechazar"
                        cancelText="Cancelar"
                        onConfirm={handleConfirmRejection}
                    />
                ) : (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col p-6 text-center">
                            <div className="w-14 h-14 bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="material-symbols-outlined text-3xl">cancel</span>
                            </div>
                            <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-2">
                                ¿Seguro que desea rechazar este Estado de Pago?
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 mb-6">
                                {confirmRejectTarget.idx === 0 
                                    ? "El Estado de Pago y la Hoja de Ruta cambiarán a estado Rechazado y volverán a estar en modo Edición para sus correcciones."
                                    : "La aprobación del paso anterior será revocada y el flujo de firmas retrocederá al aprobador anterior."}
                            </p>
                            <div className="flex items-center justify-center gap-3">
                                <button
                                    onClick={() => setConfirmRejectTarget(null)}
                                    className="px-5 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-bold text-xs transition-colors cursor-pointer"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleConfirmRejection}
                                    className="px-5 py-2 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-[16px]">cancel</span>
                                    Rechazar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {confirmReopenTarget && (
                isMobile ? (
                    <KlDialog
                        open={!!confirmReopenTarget}
                        onClose={() => setConfirmReopenTarget(null)}
                        title="¿Re-abrir Estado de Pago?"
                        message="El Estado de Pago volverá al estado En proceso y se revocará la aprobación del último revisor para que pueda ser evaluado nuevamente."
                        confirmText="Re-abrir EP"
                        cancelText="Cancelar"
                        onConfirm={handleConfirmReopen}
                    />
                ) : (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col p-6 text-center">
                            <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="material-symbols-outlined text-3xl">lock_open</span>
                            </div>
                            <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-2">
                                ¿Seguro que desea re-abrir este Estado de Pago?
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 mb-6">
                                El Estado de Pago volverá al estado <strong className="text-amber-700 dark:text-amber-300 font-bold">En proceso</strong> y se revocará la aprobación del último revisor para que pueda ser evaluado nuevamente.
                            </p>
                            <div className="flex items-center justify-center gap-3">
                                <button
                                    onClick={() => setConfirmReopenTarget(null)}
                                    className="px-5 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-bold text-xs transition-colors cursor-pointer"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleConfirmReopen}
                                    className="px-5 py-2 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-[16px]">lock_open</span>
                                    Re-abrir EP
                                </button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {/* Dialog de confirmación / prompt para acciones de contratos en móvil */}
            {mobileContractDialog && (
                <KlDialog
                    open={mobileContractDialog.open}
                    onClose={() => setMobileContractDialog(null)}
                    title={mobileContractDialog.title}
                    message={mobileContractDialog.message}
                    danger={mobileContractDialog.danger}
                    confirmText={mobileContractDialog.confirmText || 'Aceptar'}
                    cancelText={mobileContractDialog.cancelText}
                    onConfirm={handleConfirmContractDialog}
                >
                    {mobileContractDialog.type === 'anular' && (
                        <div className="mt-3">
                            <input
                                type="text"
                                autoFocus
                                value={voidReasonText}
                                onChange={(e) => setVoidReasonText(e.target.value)}
                                placeholder="Escribe el motivo..."
                                className="w-full h-10 px-3 border border-[#CBD5E1] dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-[8px] text-xs outline-none focus:ring-2 focus:ring-red-500"
                            />
                        </div>
                    )}
                </KlDialog>
            )}

            {/* Comment Modal for Parameter Non-Compliance */}
            {commentModalTarget && (
                <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 p-6 flex flex-col gap-4">
                        <div className="flex justify-between items-center border-b border-slate-200 dark:border-slate-700 pb-3">
                            <div className="flex items-center gap-2 text-slate-800 dark:text-white font-bold text-sm uppercase">
                                <span className="material-symbols-outlined text-blue-600">comment</span>
                                Comentario de Parámetro
                            </div>
                            <button onClick={() => setCommentModalTarget(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700">
                            Parámetro: <span className="text-slate-900 dark:text-white font-bold">{commentModalTarget.paramName}</span>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase mb-1">
                                Observación / Motivo de Incumplimiento:
                            </label>
                            <textarea
                                rows={4}
                                value={commentModalTarget.comment}
                                onChange={(e) => setCommentModalTarget({ ...commentModalTarget, comment: e.target.value })}
                                placeholder="Escriba aquí las observaciones o justificación del parámetro no cumplido..."
                                className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-xs focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 dark:text-white outline-none"
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                onClick={() => setCommentModalTarget(null)}
                                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-bold text-xs hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleSaveParamComment(commentModalTarget.comment)}
                                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs shadow-md transition-all cursor-pointer"
                            >
                                Guardar Comentario
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {epDetailModalOpen && renderEPDetailModal()}

            {/* Subcontractor Evaluation Configuration Modal */}
            {evaluationConfigModalOpen && (
                isMobile ? (
                    <MobileEvaluationConfigModal
                        open={evaluationConfigModalOpen}
                        onClose={() => setEvaluationConfigModalOpen(false)}
                        config={evaluationConfig}
                        onSaveConfig={handleSaveEvaluationConfig}
                        globalProfiles={globalProfiles}
                        hasEditPermission={hasPermission('sc_config', 'edit')}
                    />
                ) : (
                    <EvaluationConfigModal
                        isOpen={evaluationConfigModalOpen}
                        onClose={() => setEvaluationConfigModalOpen(false)}
                        config={evaluationConfig}
                        onSaveConfig={handleSaveEvaluationConfig}
                        globalProfiles={globalProfiles}
                        hasEditPermission={hasPermission('sc_config', 'edit')}
                    />
                )
            )}

            {/* Subcontractor Weekly Evaluation Modal */}
            {evaluationModalOpen && (
                isMobile ? (
                    <MobileSubcontractorEvaluationModal
                        open={evaluationModalOpen}
                        onClose={() => setEvaluationModalOpen(false)}
                        weeklyPlans={weeklyPlans}
                        subcontractors={subcontractors}
                        contracts={contracts}
                        tasks={tasks}
                        savedProtocols={savedProtocols}
                        lookaheadPlans={lookaheadPlans}
                        evaluationConfig={evaluationConfig}
                        onOpenConfigModal={() => {
                            setEvaluationModalOpen(false);
                            setEvaluationConfigModalOpen(true);
                        }}
                        hasEditPermission={hasPermission('sc_eval', 'edit') || hasPermission('mod_subcontratos', 'edit')}
                        projectId={projectInfo?.name || 'default'}
                        globalUsers={globalUsers}
                        responsibles={responsibles}
                    />
                ) : (
                    <SubcontractorEvaluationModal
                        isOpen={evaluationModalOpen}
                        onClose={() => setEvaluationModalOpen(false)}
                        weeklyPlans={weeklyPlans}
                        subcontractors={subcontractors}
                        contracts={contracts}
                        tasks={tasks}
                        savedProtocols={savedProtocols}
                        lookaheadPlans={lookaheadPlans}
                        evaluationConfig={evaluationConfig}
                        onOpenConfigModal={() => {
                            setEvaluationModalOpen(false);
                            setEvaluationConfigModalOpen(true);
                        }}
                        hasEditPermission={hasPermission('sc_eval', 'edit') || hasPermission('mod_subcontratos', 'edit')}
                        projectId={projectInfo?.name || 'default'}
                        globalUsers={globalUsers}
                        responsibles={responsibles}
                    />
                )
            )}
        </div>
    );
};

export default PaymentsScreen;
